import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import { toast } from 'sonner';
import { TCBadge, Callout, EmptyState, PaginationFooter } from './trust-center/primitives';
import { SearchOverlay } from './search/SearchOverlay';
import {
  ShieldCheck, Globe, Copy, ExternalLink, Loader2, Check, AlertTriangle,
  Inbox, Eye, EyeOff, Plus, Trash2, GripVertical,
  Pencil, Sparkles, Clock, X, ChevronUp, ChevronDown, Link2, LayoutGrid,
  Monitor, Tablet, Smartphone, CheckCircle2, XCircle, Mail, Building2, MessageSquare, History, ChevronRight,
  ChevronLeft, Layers, Search, RotateCcw, Archive, BarChart3, Download, Activity, Info as InfoIcon,
  ArrowUpFromLine, FileText, ShieldAlert, BadgeCheck,
} from 'lucide-react';
import Header from './Header';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { formatDate } from '../utils/dateFormatter';
import { TrustCenterView, buildPublicData } from './PublicTrustCenter';

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : `id-${Math.random().toString(36).slice(2)}`);

const TABS = [
  { k: 'basics', label: 'Basics' },
  { k: 'overview', label: 'Overview' },
  { k: 'security', label: 'Security' },
  { k: 'documents', label: 'Resources' },
  { k: 'policies', label: 'Policies' },
  { k: 'controls', label: 'Controls' },
  { k: 'subprocessors', label: 'Subprocessors' },
  { k: 'faq', label: 'FAQ' },
  { k: 'contact', label: 'Contact' },
  { k: 'updates', label: 'Updates' },
  { k: 'requests', label: 'Requests' },
  { k: 'analytics', label: 'Analytics' },
];

// Public nav pages (visibility toggles) — Policies live inside Resources so
// they are NOT a public nav section, and Compliance/Security/Privacy/AI
// Governance are Overview sub-sections, not standalone pages.
const SECTION_DEFS = [
  { key: 'overview', label: 'Overview' },
  { key: 'security', label: 'Security' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'resources', label: 'Resources' },
  { key: 'controls', label: 'Controls' },
  { key: 'subprocessors', label: 'Subprocessors' },
  { key: 'faq', label: 'FAQ' },
  { key: 'contact', label: 'Contact' },
];
const RESOURCE_CATEGORIES = ['Compliance', 'Security', 'Privacy', 'AI Governance', 'Reports', 'Questionnaires', 'Legal', 'Certificates', 'Documentation', 'Other'];
const DOC_TYPES = ['SOC 2 Report', 'ISO 27001 Certificate', 'Security Overview', 'DPA', 'Privacy Policy', 'Terms of Service', 'Incident Response Policy', 'Access Control Policy', 'Security Questionnaire', 'Other'];

const VIS_BADGE = {
  public: { label: 'Public', cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' },
  gated: { label: 'On Request', cls: 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]' },
  restricted: { label: 'On Request', cls: 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]' },
  nda: { label: 'Under NDA', cls: 'bg-[#F8FAFC] text-[#475569] border-[#CBD5E1]' },
  hidden: { label: 'Internal Only', cls: 'bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]' },
};

const STATUS_BADGE = {
  draft: { label: 'Draft', cls: 'bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]' },
  published: { label: 'Published', cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' },
  archived: { label: 'Archived', cls: 'bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]' },
};

// Private by Default — a small pill that shows Public/Private visibility and
// toggles the per-entity `published` boolean. `published` is undefined/true →
// Public; explicit false → Private. Labels are Public/Private (never
// "Published") per product decision; the stored field remains a boolean.
const PublishPill = ({ published, onToggle, testid, label }) => {
  const isPublic = published !== false;
  return (
    <span className="inline-flex items-center gap-1">
      {label && <span className="text-[10px] text-[#94A3B8]">{label}</span>}
      <button
        type="button"
        onClick={() => onToggle(!isPublic)}
        data-testid={testid}
        data-published={isPublic ? 'true' : 'false'}
        aria-label={isPublic ? 'Visibility: Public. Click to make Private.' : 'Visibility: Private. Click to make Public.'}
        title={isPublic ? 'Public — visible on your Trust Center. Click to make Private.' : 'Private — hidden from your Trust Center. Click to make Public.'}
        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition-colors ${isPublic
          ? 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0] hover:bg-[#D1FAE5]'
          : 'bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0] hover:bg-[#E2E8F0]'}`}
      >
        {isPublic ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
        {isPublic ? 'Public' : 'Private'}
      </button>
    </span>
  );
};


const VERSION_ACTION_LABEL = {
  created: 'Created', edited: 'Edited', restored: 'Restored', published: 'Published', archived: 'Archived',
};

const TrustCenterAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [tab, setTab] = useState('basics');
  const [cfg, setCfg] = useState(null);
  // Phase C — enterprise pagination state per admin tab (URL + localStorage
  // preserved via effect below). Client-side over already-loaded config
  // because the mutation model (single-config PUT + per-resource PATCH)
  // remains authoritative; UX matches server-side pagination exactly.
  const [docsPage, setDocsPage] = useState(1);
  const [docsPageSize, setDocsPageSize] = useState(() => {
    try { return parseInt(localStorage.getItem('tc.docs.pageSize') || '25', 10) || 25; } catch (_) { return 25; }
  });
  useEffect(() => { try { localStorage.setItem('tc.docs.pageSize', String(docsPageSize)); } catch (_) { /* */ } }, [docsPageSize]);
  const [posture, setPosture] = useState(null);
  const [availableDocs, setAvailableDocs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [controls, setControls] = useState([]);
  const [slugStatus, setSlugStatus] = useState(null);
  const slugTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, docsRes] = await Promise.all([
        axios.get('/api/trust-center'),
        axios.get('/api/trust-center/available-documents'),
      ]);
      setCfg(data.config);
      setPosture(data.posture);
      setControls(data.controls || []);
      setAvailableDocs(docsRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Trust Center');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    try { const { data } = await axios.get('/api/trust-center/access-requests'); setRequests(data || []); } catch (e) { /* */ }
  }, []);

  const refreshPreviewControls = useCallback(async () => {
    try { const { data } = await axios.get('/api/trust-center'); setControls(data.controls || []); } catch (e) { /* */ }
  }, []);

  const [regenOpen, setRegenOpen] = useState(false);
  // Preview modal — replaces the old side-by-side Live Preview. Editor
  // now uses the full width; admins open Preview from the top-right button.
  // Closing the modal preserves the editor's exact scroll position and
  // active tab because nothing about the editor is unmounted or reset.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchPreviewOpen, setSearchPreviewOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const doRegenerate = async (overwrite) => {
    setRegenerating(true);
    try {
      const { data } = await axios.post('/api/trust-center/generate-draft', { overwrite });
      setCfg(data.config); setPosture(data.posture); setControls(data.controls || []);
      toast.success(overwrite ? 'Draft regenerated (your edits were refreshed)' : 'Draft refreshed — your edits were preserved');
      setRegenOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to regenerate draft');
    } finally { setRegenerating(false); }
  };

  useEffect(() => { load(); loadRequests(); }, [load, loadRequests]);

  // ----- updaters -----
  const setField = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const setNested = (parent, k, v) => setCfg((p) => ({ ...p, [parent]: { ...(p[parent] || {}), [k]: v } }));
  const setSection = (k, v) => setCfg((p) => ({ ...p, sections: { ...(p.sections || {}), [k]: v } }));
  const updateItem = (key, idx, patch) => setCfg((p) => ({ ...p, [key]: p[key].map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const removeItem = (key, idx) => setCfg((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));
  const addItem = (key, item) => setCfg((p) => ({ ...p, [key]: [...(p[key] || []), item] }));
  const reorder = (key, next) => setCfg((p) => ({ ...p, [key]: next }));
  const updateAiItem = (idx, patch) => setCfg((p) => ({ ...p, ai_governance: { ...p.ai_governance, items: p.ai_governance.items.map((it, i) => i === idx ? { ...it, ...patch } : it) } }));
  const removeAiItem = (idx) => setCfg((p) => ({ ...p, ai_governance: { ...p.ai_governance, items: p.ai_governance.items.filter((_, i) => i !== idx) } }));
  const addAiItem = () => setCfg((p) => ({ ...p, ai_governance: { ...p.ai_governance, items: [...(p.ai_governance.items || []), { id: uid(), title: '', description: '', enabled: true }] } }));
  const reorderAi = (next) => setCfg((p) => ({ ...p, ai_governance: { ...p.ai_governance, items: next } }));

  const onSlugChange = (raw) => {
    setField('slug', raw);
    setSlugStatus(null);
    if (slugTimer.current) clearTimeout(slugTimer.current);
    if (!raw) return;
    slugTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/trust-center/slug-check', { params: { slug: raw } });
        setCfg((p) => ({ ...p, slug: data.slug }));
        setSlugStatus({ valid: data.valid, available: data.available });
      } catch (e) { /* */ }
    }, 450);
  };

  const buildPayload = () => {
    const summaries = {};
    (cfg.documents || []).forEach((d) => {
      if (d._change_summary && d._change_summary.trim()) summaries[d.id] = d._change_summary.trim();
    });
    return {
      slug: cfg.slug, company_name: cfg.company_name, security_contact_email: cfg.security_contact_email,
      trust_statement: cfg.trust_statement, hero: cfg.hero, theme: cfg.theme, overview_text: cfg.overview_text,
      show_control_metrics: cfg.show_control_metrics, sections: cfg.sections,
      allow_search_indexing: cfg.allow_search_indexing,
      compliance_frameworks: cfg.compliance_frameworks, security_program: cfg.security_program,
      privacy_handling: cfg.privacy_handling, ai_governance: cfg.ai_governance, subprocessors: cfg.subprocessors,
      faqs: cfg.faqs, documents: cfg.documents, disclosure: cfg.disclosure, footer: cfg.footer,
      controls_settings: cfg.controls_settings,
      trust_indicators: cfg.trust_indicators,
      security_profile: cfg.security_profile,
      document_change_summaries: summaries,
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put('/api/trust-center', buildPayload());
      setCfg(data.config); setPosture(data.posture); setControls(data.controls || []); setSlugStatus(null);
      toast.success('Saved');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save'); return false;
    } finally { setSaving(false); }
  };

  const [versionDlg, setVersionDlg] = useState({ open: false, resourceId: null, title: '', loading: false, data: null });

  const openVersions = async (resourceId, title) => {
    setVersionDlg({ open: true, resourceId, title, loading: true, data: null });
    try {
      const { data } = await axios.get(`/api/trust-center/resources/${resourceId}/versions`);
      setVersionDlg((p) => ({ ...p, loading: false, data }));
    } catch (e) {
      setVersionDlg((p) => ({ ...p, loading: false, data: { versions: [] } }));
      toast.error(e.response?.data?.detail || 'No version history yet — save this resource first.');
    }
  };

  const doRestore = async (resourceId, version) => {
    try {
      const { data } = await axios.post(`/api/trust-center/resources/${resourceId}/restore`, { version });
      setCfg(data.config); setPosture(data.posture); setControls(data.controls || []);
      toast.success(`Restored from version ${version}`);
      await openVersions(resourceId, versionDlg.title);
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
  };

  const doResourceAction = async (resourceId, action) => {
    const ok = await save();  // persist any pending edits so the endpoint sees current state
    if (!ok) return;
    try {
      const { data } = await axios.post(`/api/trust-center/resources/${resourceId}/${action}`, {});
      setCfg(data.config); setPosture(data.posture); setControls(data.controls || []);
      toast.success(action === 'publish' ? 'Resource published' : 'Resource archived');
    } catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); }
  };

  // Nova-hosted file upload/remove. Save first so the resource exists
  // server-side, then hit the dedicated file endpoint (mirrors doResourceAction).
  const uploadResourceFile = async (resourceId, file) => {
    const ok = await save();
    if (!ok) return;
    const fd = new FormData();
    fd.append('file', file);
    setSaving(true);
    try {
      const { data } = await axios.post(`/api/trust-center/resources/${resourceId}/file`, fd);
      setCfg(data.config); setPosture(data.posture); setControls(data.controls || []);
      toast.success('File uploaded to Nova');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setSaving(false); }
  };

  const removeResourceFile = async (resourceId) => {
    try {
      const { data } = await axios.delete(`/api/trust-center/resources/${resourceId}/file`);
      setCfg(data.config); setPosture(data.posture); setControls(data.controls || []);
      toast.success('File removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
  };

  const doPublish = async () => {
    setPublishing(true);
    try {
      const ok = await save();
      if (!ok) return;
      const { data } = await axios.post('/api/trust-center/publish');
      setCfg(data.config); toast.success('Trust Center published');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.errors) toast.error(detail.errors.join(' '));
      else toast.error(detail || 'Failed to publish');
    } finally { setPublishing(false); }
  };

  const doUnpublish = async () => {
    setPublishing(true);
    try { const { data } = await axios.post('/api/trust-center/unpublish'); setCfg(data.config); toast.success('Trust Center unpublished'); }
    catch (e) { toast.error('Failed to unpublish'); } finally { setPublishing(false); }
  };

  // ----- recent updates (changelog) -----
  const addUpdate = async (payload) => {
    try {
      const { data } = await axios.post('/api/trust-center/updates', payload);
      setCfg((p) => ({ ...p, recent_updates: data.recent_updates }));
      toast.success('Update added');
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add update'); return false; }
  };
  const editUpdate = async (id, payload) => {
    try {
      const { data } = await axios.put(`/api/trust-center/updates/${id}`, payload);
      setCfg((p) => ({ ...p, recent_updates: data.recent_updates }));
      toast.success('Update saved');
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save update'); return false; }
  };
  const toggleUpdateHidden = async (id, hidden) => {
    try {
      const { data } = await axios.put(`/api/trust-center/updates/${id}`, { hidden });
      setCfg((p) => ({ ...p, recent_updates: data.recent_updates }));
    } catch (e) { toast.error('Failed to update visibility'); }
  };
  const deleteUpdate = async (id) => {
    try {
      const { data } = await axios.delete(`/api/trust-center/updates/${id}`);
      setCfg((p) => ({ ...p, recent_updates: data.recent_updates }));
      toast.success('Update deleted');
    } catch (e) { toast.error('Failed to delete update'); }
  };

  const requestAction = async (id, body) => {
    try {
      const { data } = await axios.patch(`/api/trust-center/access-requests/${id}`, body);
      setRequests((p) => p.map((r) => (r.id === id ? data : r)));
      const verb = { review: 'moved to Under Review', reopen: 'reopened (Under Review)', approve: 'approved',
        reject: 'rejected', complete: 'marked Completed', note: 'note saved' }[body.action] || 'updated';
      if ((body.action === 'approve' || body.action === 'reject') && data.email_sent === false) {
        toast.warning(`Request ${verb}. Email was NOT sent — configure email delivery to notify the requester.`);
      } else {
        toast.success(`Request ${verb}`);
      }
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update request'); return false; }
  };

  const publicPath = cfg?.slug ? `/t/${cfg.slug}` : null;
  const publicUrl = publicPath ? `${window.location.origin}${publicPath}` : null;

  const copyLink = async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); toast.success('Public link copied'); }
    catch (e) {
      try {
        const ta = document.createElement('textarea'); ta.value = publicUrl; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
        toast.success('Public link copied');
      } catch (_) { toast.error('Copy failed — select the URL and press Ctrl+C'); }
    }
  };

  const frameworkNames = (cfg?.compliance_frameworks || []).map((f) => (f.name || '').trim()).filter(Boolean);

  if (loading) {
    return (<div className="min-h-screen bg-appbg"><Header /><div className="flex items-center justify-center py-40"><Loader2 className="w-6 h-6 animate-spin text-[#0F62FE]" /></div></div>);
  }

  // Load failed or no config for this context (e.g. super-admin / missing workspace):
  // render a safe empty state instead of crashing on cfg.* access. The API error was
  // already surfaced via toast in load(); this does not hide it.
  if (!cfg) {
    return (
      <div className="min-h-screen bg-appbg" data-testid="trust-center-admin-empty">
        <Header />
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 py-24 flex flex-col items-center text-center">
          <ShieldCheck className="w-10 h-10 text-[#94A3B8] mb-4" strokeWidth={1.5} />
          <h1 className="text-2xl font-semibold text-[#0A0A0A] mb-2">Trust Center unavailable</h1>
          <p className="text-[#475569] text-sm max-w-md mb-6">We couldn't load a Trust Center for this workspace. Select a workspace with a Trust Center, or try again.</p>
          <Button onClick={load} data-testid="tc-admin-retry" className="rounded-[10px] bg-[#0F62FE] hover:bg-[#0A4FCC] text-white gap-1.5"><Loader2 className="w-4 h-4" /> Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg" data-testid="trust-center-admin">
      <Header />
      {/* Shared container: every Trust Center Admin surface (header,
          publish bar, readiness panel, tabs, cards, tables, forms,
          analytics, empty states) inherits the same 1240px max width
          and responsive gutters — removing the side-by-side preview
          otherwise let content stretch to the full viewport. */}
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-[#0A0A0A] flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-[#0F62FE]" strokeWidth={1.75} /> Trust Center
            </h1>
            <p className="text-[#475569] text-sm mt-1">Build and publish a professional, customer-facing security &amp; compliance portal.</p>
          </div>
          <div className="flex items-center gap-3">
            {cfg.last_updated_at && <span className="text-xs text-[#64748B] hidden sm:inline">Updated {formatDate(cfg.last_updated_at)}</span>}
            <Button
              onClick={() => setSearchPreviewOpen(true)}
              data-testid="tc-search-preview-btn"
              variant="outline"
              className="border-[#D7E0F2] rounded-[10px] gap-1.5"
              title="Preview search exactly as a buyer sees it — same service, same privacy boundaries"
            >
              <Search className="w-4 h-4 text-[#0F62FE]" /> Preview search
            </Button>
            <Button
              onClick={() => setPreviewOpen(true)}
              data-testid="tc-preview-btn"
              className="bg-[#0F62FE] hover:bg-[#0A4FCC] rounded-[10px] gap-1.5 text-white"
              title="Open a full-screen preview of your public Trust Center"
            >
              <Eye className="w-4 h-4" /> Preview
            </Button>
            <Button variant="outline" onClick={() => setRegenOpen(true)} data-testid="tc-regenerate-btn" className="border-[#D7E0F2] rounded-[10px] gap-1.5"><Sparkles className="w-4 h-4 text-[#0F62FE]" /> Regenerate Draft</Button>
            {cfg.is_published ? (
              <Badge className="bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0] gap-1.5" data-testid="tc-status-published"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Published</Badge>
            ) : (
              <Badge className="bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0] gap-1.5" data-testid="tc-status-draft"><span className="w-1.5 h-1.5 rounded-full bg-[#94A3B8]" /> Draft</Badge>
            )}
          </div>
        </div>

        {/* Publish bar */}
        <Card className="p-4 mb-6" data-testid="tc-publish-card">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1">
              <div className="font-semibold text-[15px] text-[#0A0A0A]">{cfg.is_published ? 'Your Trust Center is live' : 'Not published yet'}</div>
              <div className="text-xs text-[#64748B] mt-0.5">{cfg.is_published ? 'Visible to anyone with the link.' : 'Fill in company name, slug & security email, then publish.'}</div>
            </div>
            {publicPath && (
              <div className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2 md:w-[420px]">
                <Globe className="w-4 h-4 text-[#64748B] shrink-0" />
                <span className="text-xs text-[#334155] truncate flex-1" data-testid="tc-public-url">{publicUrl}</span>
                <button onClick={copyLink} data-testid="tc-copy-link" className="text-[#0F62FE] hover:text-[#0A4FCC] p-1" title="Copy link"><Copy className="w-4 h-4" /></button>
                {cfg.is_published && <a href={publicPath} target="_blank" rel="noreferrer" data-testid="tc-open-public" className="text-[#0F62FE] hover:text-[#0A4FCC] p-1" title="Open public page"><ExternalLink className="w-4 h-4" /></a>}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button onClick={save} disabled={saving} variant="outline" data-testid="tc-save-btn" className="border-[#D7E0F2] rounded-[10px]">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
              {cfg.is_published ? (
                <Button variant="outline" onClick={doUnpublish} disabled={publishing} data-testid="tc-unpublish-btn" className="border-[#D7E0F2] rounded-[10px]">{publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unpublish'}</Button>
              ) : (
                <Button onClick={doPublish} disabled={publishing} data-testid="tc-publish-btn" className="bg-[#0F62FE] hover:bg-[#0A4FCC] rounded-[10px]">{publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publish'}</Button>
              )}
            </div>
          </div>
        </Card>

        <HealthPanel cfg={cfg} controlsCount={controls.length} activePolicies={availableDocs.length} onJump={setTab} />

        {/* Editor — full-width now that Live Preview lives in a full-screen
            modal opened via the top-right Preview button. All previous editor
            functionality is unchanged; only the surrounding grid is dropped
            so admins have the full page width for editing. */}
        <div>
          <div>
            {/* Tabs — polished (Phase A): tighter padding, subtle active-tab
                pill, hover row, sticky-friendly overflow scroll with fade edge. */}
            <div className="relative mb-5" data-testid="tc-tabs">
              <div className="flex items-center gap-0.5 border-b border-[var(--tc-line)] overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
                {TABS.map((t) => {
                  const active = tab === t.k;
                  const showCount = t.k === 'requests' && requests.length;
                  return (
                    <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tc-tab-${t.k}`}
                      role="tab" aria-selected={active} data-state={active ? 'active' : 'inactive'}
                      className={[
                        'group inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors tc-focus',
                        active
                          ? 'text-[var(--tc-blue)] border-[var(--tc-blue)]'
                          : 'text-[var(--tc-mute)] border-transparent hover:text-[var(--tc-ink)] hover:border-[var(--tc-line)]',
                      ].join(' ')}>
                      {t.label}
                      {showCount ? (
                        <span data-testid={`tc-tab-${t.k}-count`} className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full ${active ? 'bg-[var(--tc-blue)] text-white' : 'bg-[var(--tc-surface-3)] text-[var(--tc-mute)]'}`}>{requests.length}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
              {/* Right-edge fade indicates horizontal overflow on narrow viewports */}
              <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent" aria-hidden="true" />
            </div>

            <div className="space-y-5">
              {tab === 'basics' && (
                <div className="tc-surface p-5 space-y-5" data-testid="tc-basics-card">
                  <div>
                    <div className="tc-h2">Identity &amp; branding</div>
                    <div className="tc-muted mt-0.5">Company name, public slug, logo and accent color.</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Company Name" required><Input value={cfg.company_name || ''} onChange={(e) => setField('company_name', e.target.value)} data-testid="tc-company-name" className="rounded-[var(--tc-radius)] h-9" placeholder="Acme Corp" /></Field>
                    <Field label="Public URL Slug" required>
                      <div className="flex items-center gap-2"><span className="text-sm text-[var(--tc-mute-soft)]">/t/</span><Input value={cfg.slug || ''} onChange={(e) => onSlugChange(e.target.value)} data-testid="tc-slug" className="rounded-[var(--tc-radius)] h-9" placeholder="acme-corp" /></div>
                      {slugStatus && cfg.slug && (
                        <div className={`text-xs mt-1.5 flex items-center gap-1 ${slugStatus.valid && slugStatus.available ? 'text-[var(--tc-success)]' : 'text-[var(--tc-danger)]'}`} data-testid="tc-slug-status">
                          {slugStatus.valid && slugStatus.available ? <><Check className="w-3.5 h-3.5" /> Available</> : <><AlertTriangle className="w-3.5 h-3.5" /> {slugStatus.valid ? 'Already taken' : 'Use lowercase letters, numbers, hyphens'}</>}
                        </div>
                      )}
                    </Field>
                    <Field label="Logo URL (optional)"><Input value={cfg.theme?.logo_url || ''} onChange={(e) => setNested('theme', 'logo_url', e.target.value)} data-testid="tc-logo-url" className="rounded-[var(--tc-radius)] h-9" placeholder="https://…/logo.png" /></Field>
                    <Field label="Accent Color">
                      <div className="flex items-center gap-2"><input type="color" value={cfg.theme?.accent_color || '#0F62FE'} onChange={(e) => setNested('theme', 'accent_color', e.target.value)} data-testid="tc-accent-color" className="w-9 h-9 rounded-[var(--tc-radius)] border border-[var(--tc-line)] cursor-pointer" /><Input value={cfg.theme?.accent_color || ''} onChange={(e) => setNested('theme', 'accent_color', e.target.value)} className="rounded-[var(--tc-radius)] h-9" /></div>
                    </Field>
                  </div>

                  <div className="border-t border-[var(--tc-line-soft)] pt-4">
                    <div className="tc-h3 mb-2">Footer links</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Privacy Policy URL"><Input value={cfg.footer?.privacy_url || ''} onChange={(e) => setNested('footer', 'privacy_url', e.target.value)} data-testid="tc-footer-privacy" className="rounded-[var(--tc-radius)] h-9" placeholder="https://…" /></Field>
                      <Field label="Terms URL"><Input value={cfg.footer?.terms_url || ''} onChange={(e) => setNested('footer', 'terms_url', e.target.value)} data-testid="tc-footer-terms" className="rounded-[var(--tc-radius)] h-9" placeholder="https://…" /></Field>
                    </div>
                  </div>

                  <div className="border-t border-[var(--tc-line-soft)] pt-4" data-testid="tc-basics-publishing-visibility">
                    <div className="tc-h3 mb-1">Publishing &amp; Visibility</div>
                    <div className="tc-muted mb-3">Site-wide publishing controls for this Trust Center.</div>
                    <div className="flex items-start justify-between gap-4 rounded-[var(--tc-radius)] border border-[var(--tc-line-soft)] p-4">
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-[#0A0A0A]">Search Engine Visibility</div>
                        <div className="text-[13px] text-[#475569] mt-0.5">Allow search engines to index this Trust Center</div>
                        <p className="text-[12.5px] text-[#64748B] mt-2 leading-relaxed max-w-[62ch]">
                          When enabled, this published Trust Center may appear in search results such as
                          Google and Bing. Disabling this only prevents search engine indexing — it does
                          not make the Trust Center private.
                        </p>
                      </div>
                      <Switch
                        checked={cfg.allow_search_indexing !== false}
                        onCheckedChange={(v) => setField('allow_search_indexing', v)}
                        data-testid="tc-allow-search-indexing"
                      />
                    </div>
                  </div>

                  <Callout tone="info" data-testid="tc-basics-info">
                    Trust statement, hero content, section visibility and featured resources live in the <button type="button" onClick={() => setTab('overview')} className="font-semibold underline underline-offset-2" data-testid="tc-basics-goto-overview">Overview</button> tab. Security contact and responsible disclosure live in the <button type="button" onClick={() => setTab('contact')} className="font-semibold underline underline-offset-2" data-testid="tc-basics-goto-contact">Contact</button> tab.
                  </Callout>
                </div>
              )}

              {tab === 'overview' && (
                <div className="space-y-4">
                  <div className="tc-surface p-5 space-y-4" data-testid="tc-overview-card">
                    <div>
                      <div className="tc-h2">Trust statement &amp; Hero</div>
                      <div className="tc-muted mt-0.5">The headline, subtext and narrative that anchor the public Overview page.</div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Hero Headline"><Input value={cfg.hero?.headline || ''} onChange={(e) => setNested('hero', 'headline', e.target.value)} data-testid="tc-hero-headline" className="rounded-[var(--tc-radius)] h-9" /></Field>
                      <Field label="Hero Subtext"><Input value={cfg.hero?.subtext || ''} onChange={(e) => setNested('hero', 'subtext', e.target.value)} data-testid="tc-hero-subtext" className="rounded-[var(--tc-radius)] h-9" /></Field>
                    </div>
                    <Field label="Trust Statement"><Textarea value={cfg.trust_statement || ''} onChange={(e) => setField('trust_statement', e.target.value)} data-testid="tc-trust-statement" className="rounded-[var(--tc-radius)] min-h-[64px]" rows={2} /></Field>
                    <Field label="Overview Text"><Textarea value={cfg.overview_text || ''} onChange={(e) => setField('overview_text', e.target.value)} data-testid="tc-overview-text" className="rounded-[var(--tc-radius)] min-h-[100px]" rows={4} placeholder="Describe your security & compliance approach…" /></Field>
                    <Field label="AI Governance Statement"><Textarea value={cfg.ai_governance?.statement || ''} onChange={(e) => setNested('ai_governance', 'statement', e.target.value)} data-testid="tc-ai-statement" rows={3} className="rounded-[var(--tc-radius)] min-h-[80px]" placeholder="Optional intro shown above the AI Governance featured resources on the Overview." /></Field>
                  </div>

                  <TrustIndicatorsManager cfg={cfg} setCfg={setCfg} />

                  <div className="tc-surface p-5" data-testid="tc-sections-card">
                    <div className="mb-3">
                      <div className="tc-h2">Section visibility</div>
                      <div className="tc-muted mt-0.5">Toggle which public sections appear. Policies live inside Resources under the Policies filter — not as a separate page. Empty sections auto-hide.</div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1">
                      {SECTION_DEFS.map((s) => (
                        <div key={s.key} className="flex items-center justify-between gap-4 py-1">
                          <div className="text-[13px] font-medium text-[var(--tc-ink)]">{s.label}</div>
                          <Switch checked={!!cfg.sections?.[s.key]} onCheckedChange={(v) => setSection(s.key, v)} data-testid={`tc-section-${s.key}`} />
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-[var(--tc-line-soft)] mt-3 pt-3 flex items-center justify-between gap-4">
                      <div><div className="text-[13px] font-medium text-[var(--tc-ink)]">Show control coverage numbers</div><div className="tc-muted mt-0.5">OFF by default. When OFF, no counts or percentages are shown publicly.</div></div>
                      <Switch checked={!!cfg.show_control_metrics} onCheckedChange={(v) => setField('show_control_metrics', v)} data-testid="tc-show-metrics" />
                    </div>
                  </div>

                  <OverviewFeaturedManager
                    cfg={cfg}
                    updateItem={updateItem}
                    setTab={setTab}
                    onServerRefresh={load}
                  />

                  <OverviewFrameworksManager cfg={cfg} setCfg={setCfg} />
                </div>
              )}

              {tab === 'security' && (
                <SecurityProfileManager cfg={cfg} setCfg={setCfg} />
              )}

              {tab === 'contact' && (
                <div className="tc-surface p-5 space-y-5" data-testid="tc-contact-card">
                  <div>
                    <div className="tc-h2">Security contact</div>
                    <div className="tc-muted mt-0.5">This email receives documentation-request notifications and is shown on the public Contact page.</div>
                  </div>
                  <Field label="Security Contact Email" required><Input type="email" value={cfg.security_contact_email || ''} onChange={(e) => setField('security_contact_email', e.target.value)} data-testid="tc-security-email" className="rounded-[var(--tc-radius)] h-9" placeholder="security@acme.com" /></Field>

                  <div className="border-t border-[var(--tc-line-soft)] pt-5" data-testid="tc-disclosure-card">
                    <div className="mb-3">
                      <div className="tc-h3">Responsible disclosure</div>
                      <div className="tc-muted mt-0.5">Guidance shown to security researchers who want to report vulnerabilities.</div>
                    </div>
                    <Field label="Statement"><Textarea value={cfg.disclosure?.statement || ''} onChange={(e) => setNested('disclosure', 'statement', e.target.value)} data-testid="tc-disclosure-statement" rows={3} className="rounded-[var(--tc-radius)] min-h-[80px]" placeholder="How can security researchers report a vulnerability?" /></Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                      <Field label="Response SLA (optional)"><Input value={cfg.disclosure?.response_sla || ''} onChange={(e) => setNested('disclosure', 'response_sla', e.target.value)} data-testid="tc-disclosure-sla" className="rounded-[var(--tc-radius)] h-9" placeholder="e.g. Initial response within 48 hours" /></Field>
                      <Field label="Reporting instructions (optional)"><Input value={cfg.disclosure?.reporting_instructions || ''} onChange={(e) => setNested('disclosure', 'reporting_instructions', e.target.value)} data-testid="tc-disclosure-instructions" className="rounded-[var(--tc-radius)] h-9" placeholder="How to submit a report (PGP key, form URL, etc.)" /></Field>
                    </div>
                  </div>
                </div>
              )}

              {tab === 'documents' && (() => {
                const allDocs = cfg.documents || [];
                const entries = allDocs.map((d, idx) => ({ d, idx })).filter(({ d }) => !d.policy_id);
                const policyDocs = allDocs.filter((d) => d.policy_id);
                const items = entries.map((e) => e.d);
                const realIdx = (vi) => entries[vi].idx;
                // Server-side-style pagination (client-side over the loaded
                // config for performance parity — mutation model unchanged).
                const total = items.length;
                const totalPages = Math.max(1, Math.ceil(total / docsPageSize));
                const p = Math.min(Math.max(1, docsPage), totalPages);
                const start = (p - 1) * docsPageSize;
                const end = start + docsPageSize;
                const pagedItems = items.slice(start, end);
                const reorderResources = (nextPaged) => {
                  // Splice the reordered slice back into the full documents
                  // list at the current page offset so DnD works within a page.
                  const nextFull = [...items];
                  nextFull.splice(start, pagedItems.length, ...nextPaged);
                  reorder('documents', [...nextFull, ...policyDocs]);
                };
                return (
                <EditorCard title="Resources" count={items.length}
                  onAdd={() => addItem('documents', { id: uid(), title: '', doc_type: 'Other', category: 'Other', description: '', visibility: 'gated', status: 'draft', source: 'request_only', version: 1, owner: '', effective_date: '', next_review_date: '', show_next_review: false, policy_id: null, related_framework: '', related_frameworks: [], related_section: '', tags: [], external_link: '' })}
                  addTestid="tc-add-document" testid="tc-documents-card"
                  header={<>
                    <p className="tc-muted"><b>Upload a file to Nova</b> (recommended) so the document is hosted and delivered securely. <b>Public</b> resources open &amp; download instantly. <b>Request Access</b> &amp; <b>Available under NDA</b> require a request. <b>Internal Only</b> never appears publicly. An external link is an advanced alternative to uploading.</p>
                    {items.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap" data-testid="tc-resources-bulk-publish-row">
                        <span className="text-xs text-[#64748B]">Bulk visibility:</span>
                        <button type="button" onClick={() => setCfg((prev) => ({ ...prev, documents: (prev.documents || []).map((x) => (x.policy_id ? x : { ...x, published: true })) }))} className="text-xs font-semibold px-2 py-1 rounded-md border border-[#A7F3D0] text-[#047857] hover:bg-[#ECFDF5]" data-testid="tc-resources-bulk-publish">Make all Public</button>
                        <button type="button" onClick={() => setCfg((prev) => ({ ...prev, documents: (prev.documents || []).map((x) => (x.policy_id ? x : { ...x, published: false })) }))} className="text-xs font-semibold px-2 py-1 rounded-md border border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9]" data-testid="tc-resources-bulk-private">Make all Private</button>
                      </div>
                    )}
                    {policyDocs.length > 0 && (
                      <Callout tone="info" data-testid="tc-resources-policies-note">
                        {policyDocs.length} workspace {policyDocs.length === 1 ? 'policy is' : 'policies are'} managed separately in the{' '}
                        <button type="button" onClick={() => setTab('policies')} className="font-semibold underline underline-offset-2" data-testid="tc-resources-goto-policies">Policies tab</button>.
                        {' '}They appear publicly inside Resources under the Policies filter.
                      </Callout>
                    )}
                  </>}>
                  {items.length === 0 && <Empty>No resources yet. Add items like a SOC 2 Report, Security Overview, DPA, or Questionnaire.</Empty>}
                  {items.length > 0 && (
                    <>
                      <SortableList items={pagedItems} onReorder={reorderResources} prefix="tc-document">
                        {(d, vi, dnd) => { const i = realIdx(start + vi); return (
                      <SortableRow dnd={dnd} testid={`tc-document-${i}`}>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Input value={d.title} onChange={(e) => updateItem('documents', i, { title: e.target.value })} placeholder="Resource name" className="rounded-[10px]" data-testid={`tc-document-title-${i}`} />
                            <Badge className={`border text-[10px] px-1.5 py-0 ${STATUS_BADGE[d.status || 'published']?.cls || STATUS_BADGE.published.cls}`} data-testid={`tc-document-status-badge-${i}`}>{STATUS_BADGE[d.status || 'published']?.label || 'Published'}</Badge>
                            <Badge className={`border text-[10px] px-1.5 py-0 ${VIS_BADGE[d.visibility]?.cls || VIS_BADGE.gated.cls}`}>{VIS_BADGE[d.visibility]?.label || 'On Request'}</Badge>
                            <Badge className="border text-[10px] px-1.5 py-0 bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]" data-testid={`tc-document-version-${i}`}>v{d.version || 1}</Badge>
                            {d.generated && <Badge className="border text-[10px] px-1.5 py-0 bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE] gap-1" data-testid={`tc-document-generated-${i}`}><Sparkles className="w-2.5 h-2.5" /> Auto</Badge>}
                            <PublishPill published={d.published} onToggle={(v) => updateItem('documents', i, { published: v })} testid={`tc-document-visibility-${i}`} label="Visibility" />
                            {(() => {
                              const cat = (d.category || '').trim();
                              if (!OVERVIEW_SUGGESTION_CATEGORIES.includes(cat)) return null;
                              if (d.featured_on_overview) {
                                return (
                                  <button type="button" onClick={() => updateItem('documents', i, { featured_on_overview: false })} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0 rounded-full bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0] hover:bg-[#D1FAE5]" data-testid={`tc-document-featured-${i}`} title="Featured on Overview — click to unfeature">★ Featured</button>
                                );
                              }
                              return (
                                <button type="button" onClick={() => updateItem('documents', i, { featured_on_overview: true })} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0 rounded-full bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0] hover:bg-[#EEF2FF] hover:text-[#4338CA]" data-testid={`tc-document-notfeatured-${i}`} title="Not featured — click to feature">☆ Feature</button>
                              );
                            })()}
                            <button onClick={() => removeItem('documents', i)} className="text-[#94A3B8] hover:text-[#DC2626] p-1 ml-auto" data-testid={`tc-document-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            <select value={d.status || 'published'} onChange={(e) => updateItem('documents', i, { status: e.target.value })} className="rounded-[10px] border border-[#CBD5E1] text-sm px-3 py-2 bg-white" data-testid={`tc-document-statussel-${i}`}>
                              <option value="draft">Draft</option>
                              <option value="published">Published</option>
                              <option value="archived">Archived</option>
                            </select>
                            <select value={d.category || 'Other'} onChange={(e) => updateItem('documents', i, { category: e.target.value })} className="rounded-[10px] border border-[#CBD5E1] text-sm px-3 py-2 bg-white" data-testid={`tc-document-category-${i}`}>
                              {RESOURCE_CATEGORIES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <select value={d.doc_type} onChange={(e) => updateItem('documents', i, { doc_type: e.target.value })} className="rounded-[10px] border border-[#CBD5E1] text-sm px-3 py-2 bg-white" data-testid={`tc-document-type-${i}`}>
                              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <select value={d.visibility} onChange={(e) => { const v = e.target.value; const map = { public: 'public', gated: 'request', nda: 'nda', hidden: 'hidden' }; updateItem('documents', i, { visibility: v, access: map[v] || 'request' }); }} className="rounded-[10px] border border-[#CBD5E1] text-sm px-3 py-2 bg-white" data-testid={`tc-document-access-${i}`}>
                              <option value="public">Public</option>
                              <option value="gated">Request Access</option>
                              <option value="nda">Available under NDA</option>
                              <option value="hidden">Internal Only</option>
                            </select>
                            {/* Phase 1 audit-requirements: artefact_type is
                                a SEPARATE concept from visibility. A Diagram
                                or Screenshot may be Public, Request, NDA,
                                or Internal — the two dropdowns are wired
                                independently and never infer each other. */}
                            <select value={d.artefact_type || ''} onChange={(e) => updateItem('documents', i, { artefact_type: e.target.value })} className="rounded-[10px] border border-[#CBD5E1] text-sm px-3 py-2 bg-white" data-testid={`tc-document-artefact-${i}`} title="Artefact type — what kind of evidence is this?">
                              <option value="">Artefact: (none)</option>
                              <option value="report">Report</option>
                              <option value="certificate">Certificate</option>
                              <option value="policy">Policy</option>
                              <option value="procedure">Procedure</option>
                              <option value="diagram">Diagram</option>
                              <option value="register">Register</option>
                              <option value="screenshot">Screenshot</option>
                              <option value="configuration">Configuration</option>
                              <option value="log_sample">Log Sample</option>
                            </select>
                          </div>
                          <Textarea value={d.description} onChange={(e) => updateItem('documents', i, { description: e.target.value })} placeholder="Short description" rows={2} className="rounded-[10px] text-sm" data-testid={`tc-document-description-${i}`} />
                          <div className="grid grid-cols-2 gap-2">
                            <Input value={(d.tags || []).join(', ')} onChange={(e) => updateItem('documents', i, { tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })} placeholder="Tags (comma separated)" className="rounded-[10px] text-sm" data-testid={`tc-document-tags-${i}`} />
                            <Input value={d.external_link || ''} onChange={(e) => updateItem('documents', i, { external_link: e.target.value })} placeholder="Advanced: external link (https://…)" className="rounded-[10px] text-sm" data-testid={`tc-document-link-${i}`} />
                          </div>
                          {!d.policy_id && (
                            <div className="rounded-[10px] border border-[#D7E0F2] bg-[#F8FAFC] px-3 py-2.5" data-testid={`tc-document-file-block-${i}`}>
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8] flex items-center gap-1"><FileText className="w-3 h-3" /> Nova-hosted file (recommended)</Label>
                                {d.file && d.file.id ? (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge className="border text-[11px] px-2 py-0 bg-[#ECFDF5] text-[#047857] border-[#A7F3D0] gap-1" data-testid={`tc-document-file-name-${i}`}><CheckCircle2 className="w-3 h-3" /> {d.file.name || 'Uploaded file'}</Badge>
                                    <label className="cursor-pointer text-xs font-semibold text-[#0F62FE] hover:underline" data-testid={`tc-document-file-replace-${i}`}>
                                      Replace
                                      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.svg,.docx,.xlsx,.pptx,.csv,.txt" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadResourceFile(d.id, f); e.target.value = ''; }} />
                                    </label>
                                    <button type="button" onClick={() => removeResourceFile(d.id)} className="text-xs font-semibold text-[#DC2626] hover:underline" data-testid={`tc-document-file-remove-${i}`}>Remove</button>
                                  </div>
                                ) : (
                                  <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-[#0F62FE] text-[#0F62FE] hover:bg-[#EFF6FF]" data-testid={`tc-document-file-upload-${i}`}>
                                    <ArrowUpFromLine className="w-3.5 h-3.5" /> Upload file
                                    <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.svg,.docx,.xlsx,.pptx,.csv,.txt" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadResourceFile(d.id, f); e.target.value = ''; }} />
                                  </label>
                                )}
                              </div>
                              {d.visibility === 'public' && !(d.file && d.file.id) && !/^https?:\/\//i.test(d.external_link || '') && (
                                <p className="mt-2 text-[11px] text-[#B45309] flex items-center gap-1.5" data-testid={`tc-document-public-warning-${i}`}>
                                  <ShieldAlert className="w-3.5 h-3.5 shrink-0" /> Public resources need an uploaded file or a valid external link. Without one, this will be saved as Request Access.
                                </p>
                              )}
                            </div>
                          )}
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Owner</Label>
                              <Input value={d.owner || ''} onChange={(e) => updateItem('documents', i, { owner: e.target.value })} placeholder="e.g. security@company.com" className="mt-1 rounded-[10px] text-sm" data-testid={`tc-document-owner-${i}`} />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Effective date</Label>
                              <Input type="date" value={d.effective_date || ''} onChange={(e) => updateItem('documents', i, { effective_date: e.target.value })} className="mt-1 rounded-[10px] text-sm" data-testid={`tc-document-effective-${i}`} />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Next review date</Label>
                              <Input type="date" value={d.next_review_date || ''} onChange={(e) => updateItem('documents', i, { next_review_date: e.target.value })} className="mt-1 rounded-[10px] text-sm" data-testid={`tc-document-review-${i}`} />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 items-end">
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8] flex items-center gap-1"><Link2 className="w-3 h-3" /> Related frameworks (comma separated)</Label>
                              <Input value={(d.related_frameworks || []).join(', ')} onChange={(e) => updateItem('documents', i, { related_frameworks: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })} placeholder="e.g. SOC 2, ISO 27001" className="mt-1 rounded-[10px] text-sm" data-testid={`tc-document-frameworks-${i}`} />
                            </div>
                            <label className="flex items-center gap-2 text-xs text-[#475569] pb-2 cursor-pointer" data-testid={`tc-document-showreview-${i}`}>
                              <input type="checkbox" checked={!!d.show_next_review} onChange={(e) => updateItem('documents', i, { show_next_review: e.target.checked })} className="rounded" />
                              Show next review date publicly
                            </label>
                          </div>
                          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-[#F1F5F9]">
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8] flex items-center gap-1"><Link2 className="w-3 h-3" /> Primary framework</Label>
                              <select value={d.related_framework || ''} onChange={(e) => updateItem('documents', i, { related_framework: e.target.value })} className="mt-1 w-full rounded-[10px] border border-[#CBD5E1] text-sm px-3 py-2 bg-white" data-testid={`tc-document-framework-${i}`}>
                                <option value="">— None —</option>
                                {frameworkNames.map((n) => <option key={n} value={n}>{n}</option>)}
                              </select>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8] flex items-center gap-1"><LayoutGrid className="w-3 h-3" /> Related page</Label>
                              <select value={d.related_section || ''} onChange={(e) => updateItem('documents', i, { related_section: e.target.value })} className="mt-1 w-full rounded-[10px] border border-[#CBD5E1] text-sm px-3 py-2 bg-white" data-testid={`tc-document-section-${i}`}>
                                <option value="">— None —</option>
                                {SECTION_DEFS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                              </select>
                            </div>
                          </div>
                          <Input value={d._change_summary || ''} onChange={(e) => updateItem('documents', i, { _change_summary: e.target.value })} placeholder="Change summary (optional — recorded with the next version on save)" className="rounded-[10px] text-xs" data-testid={`tc-document-summary-${i}`} />
                          <div className="flex items-center gap-2 flex-wrap pt-1">
                            <Button size="sm" variant="outline" onClick={() => openVersions(d.id, d.title)} className="rounded-[10px] h-8 text-xs gap-1.5 border-[#D7E0F2]" data-testid={`tc-document-history-${i}`}><History className="w-3.5 h-3.5" /> Version history</Button>
                            {(d.status || 'published') !== 'published' && <Button size="sm" variant="outline" onClick={() => doResourceAction(d.id, 'publish')} className="rounded-[10px] h-8 text-xs gap-1.5 border-[#A7F3D0] text-[#047857]" data-testid={`tc-document-publish-${i}`}><CheckCircle2 className="w-3.5 h-3.5" /> Publish</Button>}
                            {(d.status || 'published') !== 'archived' && <Button size="sm" variant="outline" onClick={() => doResourceAction(d.id, 'archive')} className="rounded-[10px] h-8 text-xs gap-1.5 border-[#FED7AA] text-[#C2410C]" data-testid={`tc-document-archive-${i}`}><Archive className="w-3.5 h-3.5" /> Archive</Button>}
                          </div>
                        </div>
                      </SortableRow>
                    ); }}
                  </SortableList>
                      {total > docsPageSize && (
                        <PaginationFooter
                          page={p}
                          pageSize={docsPageSize}
                          total={total}
                          onPageChange={(n) => setDocsPage(n)}
                          onPageSizeChange={(s) => { setDocsPageSize(s); setDocsPage(1); }}
                          testidPrefix="tc-documents-paging"
                          className="mt-2 -mx-4 -mb-4 rounded-b-[var(--tc-radius-lg)]"
                        />
                      )}
                    </>
                  )}
                </EditorCard>
                );
              })()}

              {tab === 'policies' && (
                <PoliciesTab setCfg={setCfg} publicPath={publicPath} tcSlug={cfg.slug} />
              )}

              {tab === 'controls' && (() => {
                // Business-language settings redesign (Feb 2026). We only
                // surface toggles that the backend actually persists via
                // services/trust_center.py::normalize_controls_settings so
                // the "Public Preview" summary is truthful. Backend
                // precedence (Manual > Bulk > Global default) is unchanged.
                const cs = cfg.controls_settings || {};
                // Public Controls default OFF — undefined counts as OFF so
                // brand-new tenants never expose controls by accident.
                const controlsOn = cs.show_controls === true;
                // Boolean helper with an explicit default.
                const b = (k, def) => (cs[k] === undefined ? def : !!cs[k]);
                // Radio helper — either/or choices are clearer as radios.
                const Radio = ({ name, value, current, onChange, label, hint, testid, recommended }) => (
                  <label className={`flex items-start gap-3 rounded-[10px] border px-3 py-2.5 cursor-pointer transition-colors ${current === value ? 'border-[#0F62FE] bg-[#EFF6FF]' : 'border-[#E2E8F0] hover:bg-[#F8FAFC]'}`}>
                    <input type="radio" name={name} checked={current === value} onChange={() => onChange(value)} className="mt-1 accent-[#0F62FE]" data-testid={testid} />
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#0A0A0A]">{label}</span>
                        {recommended && <span className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">Recommended</span>}
                      </span>
                      {hint && <span className="block text-xs text-[#64748B] mt-0.5">{hint}</span>}
                    </span>
                  </label>
                );
                const Check = ({ k, def, label, hint, locked, testid }) => (
                  <label className="flex items-start gap-3 py-2">
                    <input
                      type="checkbox"
                      checked={locked ? false : b(k, def)}
                      disabled={locked}
                      onChange={(e) => setNested('controls_settings', k, e.target.checked)}
                      className="mt-0.5 accent-[#0F62FE]"
                      data-testid={testid}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm text-[#0A0A0A]">{label}{locked && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#64748B]">Always private</span>}</span>
                      {hint && <span className="block text-xs text-[#64748B] mt-0.5">{hint}</span>}
                    </span>
                  </label>
                );
                // Live counts for the preview summary — recomputed from
                // the *real* backend-supported keys so what we show here
                // matches what visitors will actually experience.
                const totalCtrls = (controls || []).length;
                const publishedOnly = b('published_only', true);
                const hideFailed = b('hide_failed', true);
                const hideNa = b('hide_na', true);
                const hideArchived = b('hide_archived', true);
                const includeInTc = b('include_in_tc', true);
                const showDescriptions = b('show_descriptions', true);
                const showStatus = b('show_status', false);
                const visibleCtrls = (controls || []).filter((c) => {
                  if (!includeInTc) return false;
                  const s = String(c.status || '').toLowerCase();
                  if (publishedOnly && c.audit_published === false) return false;
                  if (hideFailed && (s === 'failed' || s === 'invalid_status')) return false;
                  if (hideNa && (s === 'not_applicable' || s === 'na' || s === 'n/a')) return false;
                  if (hideArchived && (c.is_archived === true || c.archived === true)) return false;
                  return true;
                }).length;
                const hiddenCtrls = Math.max(0, totalCtrls - visibleCtrls);
                const frameworksInPreview = Array.from(new Set((controls || []).flatMap((c) => Array.isArray(c.frameworks) ? c.frameworks : [c.framework]).filter(Boolean))).slice(0, 8);
                const visibility = controlsOn ? 'public' : 'private';

                return (
                  <div className="space-y-4">
                    {/* Master switch card — always visible */}
                    <Card className="p-5" data-testid="tc-controls-master-card">
                      <div className="flex items-start justify-between gap-6">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-[15px] text-[#0A0A0A]">Public Controls page</h3>
                          <p className="text-xs text-[#64748B] mt-1 max-w-2xl">
                            Choose whether visitors to your Trust Center see a list of your controls. Evidence, reviewer notes, remediation and owners are <span className="font-medium text-[#0A0A0A]">never</span> made public — no matter which option you pick. Most companies keep this off and share control detail privately via NDA-gated document requests.
                          </p>
                          <div className="mt-3 grid sm:grid-cols-2 gap-2 max-w-2xl">
                            <Radio
                              name="tc-controls-visibility"
                              value="private"
                              current={visibility}
                              recommended
                              onChange={() => setNested('controls_settings', 'show_controls', false)}
                              label="Keep controls private"
                              hint="Hide the public Controls page entirely. Compliance badges, resources, FAQ and subprocessors continue working normally."
                              testid="tc-controls-vis-nobody"
                            />
                            <Radio
                              name="tc-controls-visibility"
                              value="public"
                              current={visibility}
                              onChange={() => setNested('controls_settings', 'show_controls', true)}
                              label="Publish selected controls"
                              hint="Show a curated, read-only list of controls to every visitor. Use the settings below to decide exactly what appears."
                              testid="tc-controls-vis-anyone"
                            />
                          </div>
                        </div>
                        {/* Compact status pill */}
                        <div className={`shrink-0 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full border ${controlsOn ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`} data-testid="tc-controls-status-pill">
                          {controlsOn ? 'Public' : 'Private'}
                        </div>
                      </div>
                    </Card>

                    {/* Progressive disclosure — everything else only when ON */}
                    {controlsOn && (
                      <>
                        {/* Live preview summary — updates as they toggle */}
                        <Card className="p-5" data-testid="tc-controls-preview-card">
                          <div className="flex items-center gap-2 mb-3">
                            <Eye className="w-4 h-4 text-[#0F62FE]" />
                            <h3 className="font-semibold text-[14px] text-[#0A0A0A]">What visitors will see</h3>
                            <span className="text-[10px] uppercase tracking-wide text-white bg-emerald-600 px-1.5 py-0.5 rounded">Live</span>
                            <span className="text-xs text-[#64748B] ml-auto">Updates as you change settings</span>
                          </div>
                          <div className="grid sm:grid-cols-3 gap-4">
                            <div className="rounded-[10px] border border-emerald-200 bg-emerald-50 p-3">
                              <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Visible publicly</div>
                              <div className="text-3xl font-bold text-emerald-800 mt-0.5" data-testid="tc-preview-visible">{visibleCtrls.toLocaleString()}</div>
                              <div className="text-[11px] text-emerald-700/80 mt-0.5">of {totalCtrls.toLocaleString()} total controls</div>
                            </div>
                            <div className="rounded-[10px] border border-slate-200 bg-slate-50 p-3">
                              <div className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold">Hidden publicly</div>
                              <div className="text-3xl font-bold text-slate-700 mt-0.5" data-testid="tc-preview-hidden">{hiddenCtrls.toLocaleString()}</div>
                              <div className="text-[11px] text-slate-500 mt-0.5">Excluded by filters or overrides</div>
                            </div>
                            <div className="rounded-[10px] border border-[#E2E8F0] bg-white p-3">
                              <div className="text-[11px] uppercase tracking-wide text-[#64748B] font-semibold">Frameworks in view</div>
                              <div className="flex flex-wrap gap-1 mt-1.5" data-testid="tc-preview-frameworks">
                                {frameworksInPreview.length === 0 && <span className="text-xs text-[#94A3B8]">—</span>}
                                {frameworksInPreview.map((f) => (
                                  <span key={f} className="text-[10px] font-medium bg-[#F1F5F9] text-[#334155] px-1.5 py-0.5 rounded">{f}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 grid sm:grid-cols-2 gap-4 pt-4 border-t border-[#F1F5F9]">
                            <div>
                              <div className="text-[11px] uppercase tracking-wide text-emerald-700 mb-1.5 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Visitors WILL see</div>
                              <ul className="space-y-1 text-xs text-[#334155]">
                                <li>• Control title</li>
                                <li>• Framework mapping (SOC 2, ISO 27001, etc.)</li>
                                <li>• Category / domain</li>
                                {showDescriptions && <li>• Description</li>}
                                {showStatus && <li>• Implementation status</li>}
                              </ul>
                            </div>
                            <div>
                              <div className="text-[11px] uppercase tracking-wide text-rose-700 mb-1.5 font-semibold flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Visitors will NEVER see</div>
                              <ul className="space-y-1 text-xs text-[#334155]">
                                <li>• Evidence files and attachments</li>
                                <li>• Reviewer notes and internal comments</li>
                                <li>• Remediation notes and gap language</li>
                                <li>• Control owners and assignees</li>
                                <li>• Audit progress, scores, internal metadata</li>
                                {!showDescriptions && <li>• Descriptions (currently hidden)</li>}
                                {!showStatus && <li>• Implementation status (currently hidden)</li>}
                              </ul>
                            </div>
                          </div>
                        </Card>

                        {/* Section: Which controls appear */}
                        <Card className="p-5" data-testid="tc-section-scope">
                          <h3 className="font-semibold text-[14px] text-[#0A0A0A]">Which controls appear</h3>
                          <p className="text-xs text-[#64748B] mt-1 max-w-2xl">Decide the source of controls that qualify for the public page. Individual overrides on the table below take precedence over everything here.</p>
                          <div className="mt-3 grid sm:grid-cols-2 gap-2">
                            <Radio
                              name="tc-controls-scope"
                              value="published"
                              current={publishedOnly ? 'published' : 'all'}
                              recommended
                              onChange={() => setNested('controls_settings', 'published_only', true)}
                              label="Only published audits"
                              hint="Draft audits stay private until you mark them as published. Recommended for production Trust Centers."
                              testid="tc-scope-published"
                            />
                            <Radio
                              name="tc-controls-scope"
                              value="all"
                              current={publishedOnly ? 'published' : 'all'}
                              onChange={() => setNested('controls_settings', 'published_only', false)}
                              label="Published + Draft audits"
                              hint="Include work-in-progress. Useful for beta or preview Trust Centers — usually not for production."
                              testid="tc-scope-all"
                            />
                          </div>
                          <div className="mt-4 pt-4 border-t border-[#F1F5F9] grid sm:grid-cols-2 gap-x-6">
                            <Check k="hide_failed" def={true} label="Automatically hide failed controls" hint="Keeps in-flight remediation out of the public eye. Recommended." testid="tc-excl-failed" />
                            <Check k="hide_na" def={true} label='Automatically hide "Not Applicable" controls' hint="Controls marked N/A add noise for visitors. Recommended to hide." testid="tc-excl-na" />
                            <Check k="hide_archived" def={true} label="Automatically hide archived / retired controls" hint="Retired or superseded controls stay out of the public list. Recommended." testid="tc-excl-archived" />
                            <Check k="include_in_tc" def={true} label="Include eligible controls by default" hint="Turn this off to start with everything hidden and cherry-pick what to publish via the table below." testid="tc-include-in-tc" />
                          </div>
                        </Card>

                        {/* Section: Which fields are visible */}
                        <Card className="p-5" data-testid="tc-section-fields">
                          <h3 className="font-semibold text-[14px] text-[#0A0A0A]">Which fields are visible on each control card</h3>
                          <p className="text-xs text-[#64748B] mt-1 max-w-2xl">Control title, framework mapping and category always show when a control is public — visitors need at least that much context. Everything else is opt-in.</p>
                          <div className="mt-3 grid sm:grid-cols-2 gap-x-6">
                            <Check k="show_descriptions" def={true} label="Show descriptions" hint="A short, non-sensitive explanation of what the control does." testid="tc-info-description" />
                            <Check k="show_status" def={false} label="Show implementation status" hint='Displays "Passed / In Progress" badges. Off by default — turn on only if you deliberately want to advertise progress.' testid="tc-info-status" />
                          </div>
                          <div className="mt-4 pt-4 border-t border-[#F1F5F9]">
                            <div className="text-[11px] uppercase tracking-wide text-rose-700 mb-2 font-semibold">Always private — never made public</div>
                            <div className="grid sm:grid-cols-2 gap-x-6">
                              <Check k="_evidence" def={false} label="Evidence files & attachments" locked hint="Protects auditor artifacts. Cannot be exposed publicly." testid="tc-locked-evidence" />
                              <Check k="_notes" def={false} label="Reviewer notes" locked hint="Internal review commentary. Cannot be exposed publicly." testid="tc-locked-notes" />
                              <Check k="_remediation" def={false} label="Remediation notes" locked hint="Includes gap language. Cannot be exposed publicly." testid="tc-locked-remediation" />
                              <Check k="_owners" def={false} label="Owners & assignees" locked hint="Protects staff PII. Cannot be exposed publicly." testid="tc-locked-owners" />
                            </div>
                          </div>
                        </Card>

                        {/* Individual controls table — precedence unchanged */}
                        <Card className="p-5" data-testid="tc-section-overrides">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <h3 className="font-semibold text-[14px] text-[#0A0A0A]">Individual controls</h3>
                              <p className="text-xs text-[#64748B] mt-1 max-w-2xl">Override the global rules for specific rows. Precedence stays the same: <span className="font-medium text-[#0A0A0A]">Manual override</span> beats <span className="font-medium text-[#0A0A0A]">Bulk override</span> beats <span className="font-medium text-[#0A0A0A]">Global default</span>.</p>
                            </div>
                          </div>
                          <div className="mt-3">
                            <ControlsManager onOverridesChanged={refreshPreviewControls} />
                          </div>
                        </Card>
                      </>
                    )}
                  </div>
                );
              })()}

              {tab === 'subprocessors' && (
                <EditorCard title="Subprocessors" count={(cfg.subprocessors || []).length}
                  onAdd={() => addItem('subprocessors', { id: uid(), vendor_name: '', purpose: '', data_processed: '', location: '', privacy_url: '', status: 'Active' })}
                  addTestid="tc-add-subprocessor" testid="tc-subprocessors-card"
                  header={(cfg.subprocessors || []).length > 0 ? (
                    <div className="flex items-center gap-2 flex-wrap" data-testid="tc-subprocessors-bulk-row">
                      <span className="text-xs text-[#64748B]">Bulk visibility:</span>
                      <button type="button" onClick={() => setCfg((prev) => ({ ...prev, subprocessors: (prev.subprocessors || []).map((x) => ({ ...x, published: true })) }))} className="text-xs font-semibold px-2 py-1 rounded-md border border-[#A7F3D0] text-[#047857] hover:bg-[#ECFDF5]" data-testid="tc-subprocessors-bulk-publish">Make all Public</button>
                      <button type="button" onClick={() => setCfg((prev) => ({ ...prev, subprocessors: (prev.subprocessors || []).map((x) => ({ ...x, published: false })) }))} className="text-xs font-semibold px-2 py-1 rounded-md border border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9]" data-testid="tc-subprocessors-bulk-private">Make all Private</button>
                    </div>
                  ) : null}>
                  {(cfg.subprocessors || []).length === 0 && <Empty>No subprocessors added. This section stays hidden publicly until you add one.</Empty>}
                  <SortableList items={cfg.subprocessors || []} onReorder={(n) => reorder('subprocessors', n)} prefix="tc-subprocessor">
                    {(s, i, dnd) => (
                      <SortableRow dnd={dnd} testid={`tc-subprocessor-${i}`}>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <Input value={s.vendor_name} onChange={(e) => updateItem('subprocessors', i, { vendor_name: e.target.value })} placeholder="Vendor name" className="rounded-[10px]" data-testid={`tc-subprocessor-name-${i}`} />
                            <PublishPill published={s.published} onToggle={(v) => updateItem('subprocessors', i, { published: v })} testid={`tc-subprocessor-publish-${i}`} />
                            <button onClick={() => removeItem('subprocessors', i)} className="text-[#94A3B8] hover:text-[#DC2626] p-1" data-testid={`tc-subprocessor-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input value={s.purpose} onChange={(e) => updateItem('subprocessors', i, { purpose: e.target.value })} placeholder="Purpose" className="rounded-[10px] text-sm" />
                            <Input value={s.data_processed} onChange={(e) => updateItem('subprocessors', i, { data_processed: e.target.value })} placeholder="Data processed" className="rounded-[10px] text-sm" />
                            <Input value={s.location} onChange={(e) => updateItem('subprocessors', i, { location: e.target.value })} placeholder="Location / region" className="rounded-[10px] text-sm" />
                            <Input value={s.status} onChange={(e) => updateItem('subprocessors', i, { status: e.target.value })} placeholder="Status" className="rounded-[10px] text-sm" />
                          </div>
                          <Input value={s.privacy_url} onChange={(e) => updateItem('subprocessors', i, { privacy_url: e.target.value })} placeholder="Privacy policy URL (https://…)" className="rounded-[10px] text-sm" />
                        </div>
                      </SortableRow>
                    )}
                  </SortableList>
                </EditorCard>
              )}

              {tab === 'faq' && (
                <EditorCard title="FAQ" count={(cfg.faqs || []).length}
                  onAdd={() => addItem('faqs', { id: uid(), question: '', answer: '' })}
                  addTestid="tc-add-faq" testid="tc-faq-card"
                  header={(cfg.faqs || []).length > 0 ? (
                    <div className="flex items-center gap-2 flex-wrap" data-testid="tc-faq-bulk-row">
                      <span className="text-xs text-[#64748B]">Bulk visibility:</span>
                      <button type="button" onClick={() => setCfg((prev) => ({ ...prev, faqs: (prev.faqs || []).map((x) => ({ ...x, published: true })) }))} className="text-xs font-semibold px-2 py-1 rounded-md border border-[#A7F3D0] text-[#047857] hover:bg-[#ECFDF5]" data-testid="tc-faq-bulk-publish">Make all Public</button>
                      <button type="button" onClick={() => setCfg((prev) => ({ ...prev, faqs: (prev.faqs || []).map((x) => ({ ...x, published: false })) }))} className="text-xs font-semibold px-2 py-1 rounded-md border border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9]" data-testid="tc-faq-bulk-private">Make all Private</button>
                    </div>
                  ) : null}>
                  <SortableList items={cfg.faqs || []} onReorder={(n) => reorder('faqs', n)} prefix="tc-faq">
                    {(f, i, dnd) => (
                      <SortableRow dnd={dnd} testid={`tc-faq-${i}`}>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <Input value={f.question} onChange={(e) => updateItem('faqs', i, { question: e.target.value })} placeholder="Question" className="rounded-[10px]" data-testid={`tc-faq-q-${i}`} />
                            <PublishPill published={f.published} onToggle={(v) => updateItem('faqs', i, { published: v })} testid={`tc-faq-publish-${i}`} />
                            <button onClick={() => removeItem('faqs', i)} className="text-[#94A3B8] hover:text-[#DC2626] p-1" data-testid={`tc-faq-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                          <Textarea value={f.answer} onChange={(e) => updateItem('faqs', i, { answer: e.target.value })} placeholder="Answer" rows={2} className="rounded-[10px] text-sm" />
                        </div>
                      </SortableRow>
                    )}
                  </SortableList>
                </EditorCard>
              )}

              {tab === 'updates' && (
                <UpdatesEditor
                  updates={cfg.recent_updates || []}
                  onAdd={addUpdate}
                  onEdit={editUpdate}
                  onToggleHidden={toggleUpdateHidden}
                  onDelete={deleteUpdate}
                />
              )}

              {tab === 'requests' && (
                <RequestsTab requests={requests} docs={cfg.documents || []} onAction={requestAction} />
              )}

              {tab === 'analytics' && (
                <AnalyticsTab documents={cfg.documents || []} />
              )}
            </div>
          </div>

          {/* Live preview moved to a full-screen modal — see previewOpen dialog below. */}
        </div>
      </div>
      {/* Full-screen Preview modal — Radix Dialog primitive with the shared
          shadcn wrapper (hideDefaultClose so we render exactly one Close).
          Toolbar hosts the identity, device toggles, and safe actions. The
          Preview canvas below is the single vertical scroll owner. */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          hideDefaultClose
          className="p-0 gap-0 max-w-none w-screen h-screen sm:rounded-none border-0 flex flex-col overflow-hidden bg-[#F1F5F9]"
          data-testid="tc-preview-shell"
          aria-label="Trust Center preview"
        >
          <DialogTitle className="sr-only">Trust Center Preview</DialogTitle>
          <DialogDescription className="sr-only">
            Full-screen preview of the public Trust Center. Switch between
            Desktop, Tablet, and Mobile using the device toggle. Press Escape
            to close and return to the editor.
          </DialogDescription>
          {previewOpen && (
            <PreviewShellBody
              cfg={cfg}
              posture={posture}
              controls={controls}
              isPublished={!!cfg?.is_published}
              publicPath={publicPath}
              publicUrl={publicUrl}
              onCopyLink={copyLink}
              onClose={() => setPreviewOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 3.1 — Admin "Preview search as a buyer".
          Reuses the SAME SearchOverlay component + backend service that
          the public Trust Center uses, via the admin-only /preview endpoint.
          `analyticsEndpoint` is intentionally omitted so admin previews
          never inflate the buyer analytics summary. */}
      <SearchOverlay
        open={searchPreviewOpen}
        onClose={() => setSearchPreviewOpen(false)}
        endpoint="/api/trust-center/search/preview"
        analyticsEndpoint={null}
        // Send the current in-memory cfg so unsaved edits are searchable.
        // The backend re-runs `merge_defaults` + `overlay_policy_docs` +
        // `public_view()` (the SAME sanitizer used by public search) and
        // feeds the sanitized projection into the SAME `SearchService`.
        // Nothing is persisted or logged.
        bodyProvider={() => ({ preview_cfg: cfg })}
        onNavigate={(anchor) => {
          // Keep the admin inside the Preview experience. Only opens the
          // live public route if the workspace has one and the user
          // explicitly wants to leave (new tab).
          if (publicPath) window.open(`${publicPath}${anchor.replace(/^\//, '/')}`, '_blank');
        }}
        openRequest={() => toast.info('Buyers see the Request drawer here.')}
        openContact={() => toast.info('Buyers reach Contact from here.')}
        mode="admin"
        title="Buyer Search Preview"
        placeholder="Search as a buyer would — Internal content is intentionally excluded…"
      />
      {regenOpen && <RegenerateDialog onClose={() => setRegenOpen(false)} onRegenerate={doRegenerate} busy={regenerating} />}
      <Dialog open={versionDlg.open} onOpenChange={(o) => !o && setVersionDlg({ open: false, resourceId: null, title: '', loading: false, data: null })}>
        <DialogContent className="max-w-lg" data-testid="tc-version-history-dialog">
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-[#0F62FE]" /> Version history</DialogTitle>
          <DialogDescription>{versionDlg.title || 'Resource'} — immutable snapshots. Restoring creates a new current version and never deletes history.</DialogDescription>
          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1 mt-2 space-y-2">
            {versionDlg.loading && <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#0F62FE]" /></div>}
            {!versionDlg.loading && (!versionDlg.data || (versionDlg.data.versions || []).length === 0) && (
              <div className="py-8 text-center text-sm text-[#64748B]" data-testid="tc-version-empty">No version history yet. Save this resource to start tracking versions.</div>
            )}
            {!versionDlg.loading && (versionDlg.data?.versions || []).map((v, vi) => {
              const isCurrent = v.version === versionDlg.data.current_version;
              return (
                <div key={`${v.version}-${vi}`} className="border border-[#E2E8F0] rounded-[10px] p-3 bg-white" data-testid={`tc-version-row-${v.version}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="border text-[10px] px-1.5 py-0 bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]">v{v.version}</Badge>
                    <Badge className="border text-[10px] px-1.5 py-0 bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]">{VERSION_ACTION_LABEL[v.source_action] || v.source_action}</Badge>
                    {isCurrent && <Badge className="border text-[10px] px-1.5 py-0 bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]">Current</Badge>}
                    <span className="text-[11px] text-[#94A3B8] ml-auto">{formatDate(v.created_at)}</span>
                  </div>
                  <div className="text-sm font-medium text-[#0A0A0A] mt-1.5">{v.snapshot?.title || '—'}</div>
                  {v.change_summary && <div className="text-xs text-[#475569] mt-0.5">{v.change_summary}</div>}
                  <div className="text-[11px] text-[#64748B] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>Status: {v.snapshot?.status || '—'}</span>
                    <span>Access: {v.snapshot?.access || '—'}</span>
                    {v.snapshot?.effective_date && <span>Effective: {v.snapshot.effective_date}</span>}
                    {v.snapshot?.owner && <span>Owner: {v.snapshot.owner}</span>}
                    {v.created_by && <span>By: {v.created_by}</span>}
                  </div>
                  {!isCurrent && (
                    <Button size="sm" variant="outline" onClick={() => doRestore(versionDlg.resourceId, v.version)} className="mt-2 rounded-[10px] h-7 text-xs gap-1.5 border-[#D7E0F2]" data-testid={`tc-version-restore-${v.version}`}><RotateCcw className="w-3 h-3" /> Restore this version</Button>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RegenerateDialog = ({ onClose, onRegenerate, busy }) => {
  const [overwrite, setOverwrite] = useState(false);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="tc-regenerate-dialog">
        <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#0F62FE]" /> Regenerate draft</DialogTitle>
        <DialogDescription>
          Nova will rebuild your Trust Center draft from your latest company profile, audits, frameworks, and published policies. Controls stay sanitized and no evidence, notes, or file links are ever exposed.
        </DialogDescription>
        <label className="flex items-start gap-2.5 text-sm cursor-pointer p-3 rounded-[10px] border border-[#E2E8F0] mt-2 hover:bg-[#F8FAFC]" data-testid="tc-regenerate-overwrite-label">
          <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)} data-testid="tc-regenerate-overwrite" className="mt-0.5" />
          <span>
            <span className="font-medium text-[#0A0A0A]">Overwrite my edits</span>
            <span className="block text-xs text-[#64748B] mt-0.5">{overwrite ? 'Auto-generated fields will be refreshed to Nova defaults. Items you added manually are always kept.' : 'Off — your edits are preserved. Only empty sections and new items are filled in.'}</span>
          </span>
        </label>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" className="rounded-[10px] border-[#D7E0F2]" onClick={onClose} data-testid="tc-regenerate-cancel">Cancel</Button>
          <Button className="bg-[#0F62FE] hover:bg-[#0A4FCC] rounded-[10px]" disabled={busy} onClick={() => onRegenerate(overwrite)} data-testid="tc-regenerate-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Regenerate'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, required, children }) => (
  <div><Label className="text-xs uppercase tracking-wider text-[#64748B]">{label}{required && <span className="text-[#0F62FE]"> *</span>}</Label><div className="mt-1.5">{children}</div></div>
);

const Empty = ({ children }) => (<div className="text-[12px] text-[var(--tc-mute)] bg-[var(--tc-surface-2)] border border-[var(--tc-line)] rounded-[var(--tc-radius)] px-3 py-3">{children}</div>);

const ControlToggle = ({ label, desc, checked, onChange, testid }) => (
  <div className="flex items-center justify-between gap-4 border-t border-[#F1F5F9] pt-3 first:border-t-0 first:pt-0">
    <div><div className="text-sm font-medium text-[#0A0A0A]">{label}</div><div className="text-xs text-[#64748B]">{desc}</div></div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

// ---- Scalable controls management: search, filter, bulk select, source overrides ----
// Enterprise redesign (Feb 2026):
//   Columns are ordered so the effective public state (Visibility) is the
//   first thing an admin scans. Source (Global / Bulk / Manual) is
//   secondary information rendered inside the Public Override cell — it
//   describes *how* the row got its state, not its business meaning.
//   Manual > Bulk > Global precedence is enforced by the backend and is
//   unchanged.
const SOURCE_BADGE = {
  global: { label: 'Default', short: '', Icon: Globe, cls: 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]' },
  bulk: { label: 'Bulk override', short: 'Bulk', Icon: Layers, cls: 'bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]' },
  manual: { label: 'Manual override', short: 'Manual', Icon: Pencil, cls: 'bg-[#FDF4FF] text-[#A21CAF] border-[#F5D0FE]' },
};

const SourceBadge = ({ source }) => {
  const s = SOURCE_BADGE[source] || SOURCE_BADGE.global;
  const Icon = s.Icon;
  const tip = source === 'manual' ? 'Manual override (highest precedence)' : source === 'bulk' ? 'Bulk override (beats default)' : 'Follows the global default rules above';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${s.cls}`} data-testid={`tc-ctl-source-${source}`} title={tip} aria-label={s.label}>
      <Icon className="w-3 h-3" />{s.short}
    </span>
  );
};

// Small effective-visibility pill — dominant column.
const VisibilityPill = ({ included }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${included ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}
    data-testid={`tc-ctl-vis-${included ? 'public' : 'private'}`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${included ? 'bg-emerald-500' : 'bg-slate-400'}`} />
    {included ? 'Public' : 'Private'}
  </span>
);

const ControlsManager = ({ onOverridesChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [framework, setFramework] = useState('all');
  const [category, setCategory] = useState('all');
  const [visibility, setVisibility] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const searchTimer = useRef(null);

  const fetchControls = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, search,
        framework: framework === 'all' ? '' : framework,
        category: category === 'all' ? '' : category,
        visibility };
      const { data } = await axios.get('/api/trust-center/controls', { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load controls');
    } finally { setLoading(false); }
  }, [page, pageSize, search, framework, category, visibility]);

  useEffect(() => { fetchControls(); }, [fetchControls]);

  const onSearchChange = (v) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); setSearch(v); }, 400);
  };

  const items = data?.items || [];
  const allSelected = items.length > 0 && items.every((it) => selected.has(it.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) { items.forEach((it) => next.delete(it.id)); }
      else { items.forEach((it) => next.add(it.id)); }
      return next;
    });
  };
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const applyAction = async (ids, action, source) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await axios.post('/api/trust-center/controls/bulk', { control_ids: ids, action, source });
      const verb = action === 'reset' ? 'reset to global' : (action === 'show' ? 'shown' : 'hidden');
      toast.success(`${data.affected} control${data.affected === 1 ? '' : 's'} ${verb}`);
      await fetchControls();
      onOverridesChanged && onOverridesChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  const bulk = (action) => {
    const ids = Array.from(selected);
    applyAction(ids, action, 'bulk').then(() => setSelected(new Set()));
  };

  const counts = data?.counts || { total: 0, included: 0, excluded: 0 };
  const facets = data?.facets || { frameworks: [], categories: [] };
  const totalPages = data?.pages || 1;
  const selectedCount = selected.size;

  return (
    <div className="space-y-4" data-testid="tc-controls-manager">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 text-xs">
          <span className="text-[#64748B]" data-testid="tc-ctl-count-total">{counts.total} total</span>
          <span className="inline-flex items-center gap-1 text-[#047857]" data-testid="tc-ctl-count-public"><span className="w-2 h-2 rounded-full bg-[#10B981]" />{counts.included} public</span>
          <span className="inline-flex items-center gap-1 text-[#475569]" data-testid="tc-ctl-count-private"><span className="w-2 h-2 rounded-full bg-slate-400" />{counts.excluded} private</span>
        </div>
        <div className="text-[11px] text-[#64748B]">Effective visibility shown per row — reflects the global rules above <span className="font-medium">plus</span> any override.</div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <div className="relative">
          <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input defaultValue={search} onChange={(e) => onSearchChange(e.target.value)} placeholder="Search controls…" className="rounded-[10px] pl-9 text-sm" data-testid="tc-ctl-search" />
        </div>
        <Select value={framework} onValueChange={(v) => { setPage(1); setFramework(v); }}>
          <SelectTrigger className="rounded-[10px] text-sm" data-testid="tc-ctl-fw-filter"><SelectValue placeholder="Framework" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All frameworks</SelectItem>
            {facets.frameworks.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={(v) => { setPage(1); setCategory(v); }}>
          <SelectTrigger className="rounded-[10px] text-sm" data-testid="tc-ctl-cat-filter"><SelectValue placeholder="Domain" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All domains</SelectItem>
            {facets.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={visibility} onValueChange={(v) => { setPage(1); setVisibility(v); }}>
          <SelectTrigger className="rounded-[10px] text-sm" data-testid="tc-ctl-vis-filter"><SelectValue placeholder="Visibility" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All visibility</SelectItem>
            <SelectItem value="included">Shown publicly</SelectItem>
            <SelectItem value="excluded">Hidden</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="flex items-center justify-between gap-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-[10px] px-3 py-2" data-testid="tc-ctl-bulkbar">
          <span className="text-sm font-medium text-[#1D4ED8]">{selectedCount} selected</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="rounded-[10px] h-8 border-[#A7F3D0] text-[#047857] hover:bg-[#ECFDF5]" disabled={busy} onClick={() => bulk('show')} data-testid="tc-ctl-bulk-show"><Eye className="w-3.5 h-3.5 mr-1" />Show</Button>
            <Button size="sm" variant="outline" className="rounded-[10px] h-8 border-[#FECDCA] text-[#B42318] hover:bg-[#FEF3F2]" disabled={busy} onClick={() => bulk('hide')} data-testid="tc-ctl-bulk-hide"><EyeOff className="w-3.5 h-3.5 mr-1" />Hide</Button>
            <Button size="sm" variant="outline" className="rounded-[10px] h-8" disabled={busy} onClick={() => bulk('reset')} data-testid="tc-ctl-bulk-reset"><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset</Button>
          </div>
        </div>
      )}

      {/* Table — redesigned Feb 2026: Visibility | Control | Framework | Category | Public Override */}
      <div className="border border-[#E2E8F0] rounded-[10px] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-[#64748B] text-[11px] uppercase tracking-wide">
              <tr className="text-left">
                <th className="p-3 w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="tc-ctl-select-all" /></th>
                <th className="p-3 font-semibold whitespace-nowrap">Visibility</th>
                <th className="p-3 font-semibold">Control</th>
                <th className="p-3 font-semibold hidden lg:table-cell whitespace-nowrap">Framework</th>
                <th className="p-3 font-semibold hidden md:table-cell whitespace-nowrap">Category</th>
                <th className="p-3 font-semibold text-right whitespace-nowrap">Public override</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-[#94A3B8]"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-[#64748B]">No controls match these filters.</td></tr>
              ) : items.map((it) => {
                const source = it.source || 'global';
                const isManual = source === 'manual';
                return (
                  <tr key={it.id} className="border-t border-[#F1F5F9] hover:bg-[#FAFBFC] align-top" data-testid={`tc-ctl-row-${it.id}`}>
                    <td className="p-3 pt-4"><Checkbox checked={selected.has(it.id)} onCheckedChange={() => toggleOne(it.id)} data-testid={`tc-ctl-check-${it.id}`} /></td>
                    <td className="p-3 pt-4"><VisibilityPill included={!!it.included} /></td>
                    <td className="p-3">
                      <div className="font-medium text-[#0A0A0A] leading-snug">{it.title}</div>
                      {it.description && <div className="text-xs text-[#64748B] mt-0.5 line-clamp-2 max-w-xl">{it.description}</div>}
                      {/* Compact mobile view of framework + category */}
                      <div className="flex flex-wrap items-center gap-1 mt-1.5 md:hidden">
                        {(it.frameworks || []).slice(0, 3).map((f) => <span key={f} className="text-[10px] bg-[#F1F5F9] text-[#475569] rounded px-1.5 py-0.5">{f}</span>)}
                        {it.category && <span className="text-[10px] text-[#64748B]">· {it.category}</span>}
                      </div>
                    </td>
                    <td className="p-3 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(it.frameworks || []).length === 0 && <span className="text-xs text-[#94A3B8]">—</span>}
                        {(it.frameworks || []).map((f) => <span key={f} className="text-[11px] bg-[#F1F5F9] text-[#475569] rounded px-1.5 py-0.5">{f}</span>)}
                      </div>
                    </td>
                    <td className="p-3 text-[#475569] hidden md:table-cell">{it.category || <span className="text-[#94A3B8]">—</span>}</td>
                    <td className="p-3 text-right">
                      <div className="flex flex-col items-end gap-1.5">
                        {/* Segmented override control: Show / Hide / Reset */}
                        <div className="inline-flex rounded-[8px] border border-[#E2E8F0] overflow-hidden text-[11px] font-medium" role="group" aria-label="Public override">
                          <button
                            type="button"
                            className={`px-2 py-1 flex items-center gap-1 transition-colors ${isManual && it.included ? 'bg-emerald-50 text-emerald-700' : 'bg-white text-[#64748B] hover:bg-[#F8FAFC]'}`}
                            disabled={busy}
                            onClick={() => applyAction([it.id], 'show', 'manual')}
                            data-testid={`tc-ctl-show-${it.id}`}
                            title="Force this control to appear publicly (Manual override)"
                          >
                            <Eye className="w-3 h-3" /> Show
                          </button>
                          <button
                            type="button"
                            className={`px-2 py-1 flex items-center gap-1 transition-colors border-l border-[#E2E8F0] ${isManual && !it.included ? 'bg-rose-50 text-rose-700' : 'bg-white text-[#64748B] hover:bg-[#F8FAFC]'}`}
                            disabled={busy}
                            onClick={() => applyAction([it.id], 'hide', 'manual')}
                            data-testid={`tc-ctl-hide-${it.id}`}
                            title="Force this control to stay hidden (Manual override)"
                          >
                            <EyeOff className="w-3 h-3" /> Hide
                          </button>
                          <button
                            type="button"
                            className={`px-2 py-1 flex items-center gap-1 transition-colors border-l border-[#E2E8F0] ${!isManual ? 'bg-[#F1F5F9] text-[#334155]' : 'bg-white text-[#64748B] hover:bg-[#F8FAFC]'}`}
                            disabled={busy || source === 'global'}
                            onClick={() => applyAction([it.id], 'reset', 'manual')}
                            data-testid={`tc-ctl-reset-${it.id}`}
                            title="Remove any override — follow the global default"
                          >
                            <RotateCcw className="w-3 h-3" /> Reset
                          </button>
                        </div>
                        <SourceBadge source={source} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs text-[#64748B]">
          <span>Rows</span>
          <Select value={String(pageSize)} onValueChange={(v) => { setPage(1); setPageSize(Number(v)); }}>
            <SelectTrigger className="rounded-[10px] h-8 w-[72px] text-xs" data-testid="tc-ctl-pagesize"><SelectValue /></SelectTrigger>
            <SelectContent>{[25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
          <span>{data?.total || 0} results</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-[10px] h-8" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="tc-ctl-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-xs text-[#64748B]" data-testid="tc-ctl-pageinfo">Page {page} of {totalPages}</span>
          <Button size="sm" variant="outline" className="rounded-[10px] h-8" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="tc-ctl-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
};

// ---- reusable editor shell (header + Add + drag hint) ----
const EditorCard = ({ title, count, onAdd, addTestid, testid, header, children }) => (
  <div className="tc-surface p-4 space-y-3" data-testid={testid}>
    <div className="flex items-center justify-between">
      <h3 className="tc-h2 flex items-center gap-2">{title}{typeof count === 'number' && <span className="text-[11px] font-semibold text-[var(--tc-mute)] bg-[var(--tc-surface-3)] rounded-full px-2 py-0.5 tabular-nums">{count}</span>}</h3>
      {onAdd && <Button size="sm" variant="outline" className="rounded-[var(--tc-radius)] h-8 border-[var(--tc-line)] text-[13px] tc-focus" data-testid={addTestid} onClick={onAdd}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>}
    </div>
    {header}
    {typeof count === 'number' && count > 1 && <p className="text-[11px] text-[var(--tc-mute)] flex items-center gap-1"><GripVertical className="w-3 h-3" /> Drag the handle or use the arrows to reorder how items appear publicly.</p>}
    {children}
  </div>
);

// ---- dependency-free, keyboard-accessible sortable list ----
const SortableList = ({ items, onReorder, prefix, children }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [armed, setArmed] = useState(null); // index whose handle is held → row draggable

  const move = (from, to) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onReorder(next);
  };

  const finish = () => {
    if (dragIndex !== null && overIndex !== null) move(dragIndex, overIndex);
    setDragIndex(null); setOverIndex(null); setArmed(null);
  };

  return (
    <div role="list" data-testid={`${prefix}-list`} className="divide-y divide-[var(--tc-line-soft)] border border-[var(--tc-line)] rounded-[var(--tc-radius-lg)] overflow-hidden bg-[var(--tc-surface)]">
      {items.map((item, i) => {
        const dnd = {
          rowProps: {
            role: 'listitem',
            draggable: armed === i,
            'aria-roledescription': 'Sortable item',
            'aria-grabbed': dragIndex === i,
            onDragStart: (e) => { setDragIndex(i); try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } catch (_) { /* */ } },
            onDragEnter: () => setOverIndex(i),
            onDragOver: (e) => e.preventDefault(),
            onDrop: (e) => { e.preventDefault(); finish(); },
            onDragEnd: finish,
            className: `${dragIndex === i ? 'opacity-50' : ''} ${overIndex === i && dragIndex !== null && dragIndex !== i ? 'bg-[var(--tc-blue-soft)]' : ''}`,
          },
          handleProps: {
            'data-testid': `${prefix}-drag-${i}`,
            'aria-label': 'Drag to reorder',
            title: 'Drag to reorder',
            onMouseDown: () => setArmed(i),
            onMouseUp: () => setArmed(null),
            onMouseLeave: () => { if (dragIndex === null) setArmed(null); },
            onTouchStart: () => setArmed(i),
            onTouchEnd: () => setArmed(null),
          },
          moveUp: () => move(i, i - 1),
          moveDown: () => move(i, i + 1),
          isFirst: i === 0,
          isLast: i === items.length - 1,
          index: i,
          prefix,
        };
        return <React.Fragment key={item.id || i}>{children(item, i, dnd)}</React.Fragment>;
      })}
    </div>
  );
};

// ---- a single draggable row: grip + up/down arrows + content ----
const SortableRow = ({ dnd, testid, children }) => (
  <div {...dnd.rowProps} className={`bg-[var(--tc-surface)] px-3 py-2.5 transition-colors tc-hover-row ${dnd.rowProps.className || ''}`} data-testid={testid}>
    <div className="flex items-start gap-2">
      <div className="flex flex-col items-center pt-0.5 shrink-0">
        <button type="button" {...dnd.handleProps} className="text-[var(--tc-mute-soft)] hover:text-[var(--tc-blue)] cursor-grab active:cursor-grabbing touch-none tc-focus p-0.5"><GripVertical className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={dnd.moveUp} disabled={dnd.isFirst} aria-label="Move up" data-testid={`${dnd.prefix}-up-${dnd.index}`} className="text-[var(--tc-mute-soft)] hover:text-[var(--tc-blue)] disabled:opacity-30 disabled:hover:text-[var(--tc-mute-soft)] p-0.5 tc-focus"><ChevronUp className="w-3 h-3" /></button>
        <button type="button" onClick={dnd.moveDown} disabled={dnd.isLast} aria-label="Move down" data-testid={`${dnd.prefix}-down-${dnd.index}`} className="text-[var(--tc-mute-soft)] hover:text-[var(--tc-blue)] disabled:opacity-30 disabled:hover:text-[var(--tc-mute-soft)] p-0.5 tc-focus"><ChevronDown className="w-3 h-3" /></button>
      </div>
      {children}
    </div>
  </div>
);

const UPDATE_TYPE_LABELS = { published: 'Status', document: 'Document', framework: 'Framework', subprocessor: 'Subprocessor', manual: 'Manual' };

/* ============================ Policies tab ============================ */
// Workspace policies auto-appear here (synced server-side). Each row is the
// join of the source policy (safe metadata) + its Trust Center publication
// settings. All mutations go through the dedicated policy endpoints and the
// returned documents are merged back into local cfg so Save stays consistent.
const POLICY_ACCESS_OPTIONS = [
  { v: 'public', label: 'Public' },
  { v: 'request', label: 'Request Access' },
  { v: 'nda', label: 'Available under NDA' },
  { v: 'hidden', label: 'Internal Only' },
];
const POLICY_ACCESS_LABEL = Object.fromEntries(POLICY_ACCESS_OPTIONS.map((o) => [o.v, o.label]));
const POLICY_SRC_BADGE = {
  published: { label: 'Published', cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' },
  draft: { label: 'Draft', cls: 'bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]' },
  archived: { label: 'Archived', cls: 'bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]' },
  missing: { label: 'Removed', cls: 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]' },
};

const copyToClipboard = async (text, okMsg = 'Link copied') => {
  try { await navigator.clipboard.writeText(text); toast.success(okMsg); }
  catch (e) {
    try {
      const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      toast.success(okMsg);
    } catch (_) { toast.error('Copy failed'); }
  }
};

const PoliciesTab = ({ setCfg, publicPath, tcSlug }) => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [fType, setFType] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fAccess, setFAccess] = useState('all');
  const [fIncluded, setFIncluded] = useState('all');
  const [fFramework, setFFramework] = useState('all');
  const [selected, setSelected] = useState([]);
  const [bulkAccess, setBulkAccess] = useState('request');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  // Merge server-side policy documents into local cfg without clobbering
  // unsaved edits to non-policy resources.
  const applyServerDocs = useCallback((serverDocs) => {
    if (!serverDocs) return;
    setCfg((p) => {
      const localNonPolicy = (p.documents || []).filter((d) => !d.policy_id);
      const serverPolicy = (serverDocs || []).filter((d) => d.policy_id);
      return { ...p, documents: [...localNonPolicy, ...serverPolicy] };
    });
  }, [setCfg]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/trust-center/policies');
      setItems(data.items || []);
      setCounts(data.counts || null);
      setTypes(data.types || []);
      applyServerDocs(data.documents);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load policies');
    } finally { setLoading(false); }
  }, [applyServerDocs]);

  useEffect(() => { load(); }, [load]);

  const recount = (list) => {
    const included = list.filter((i) => i.included);
    setCounts({
      total: list.length,
      included: included.length,
      excluded: list.length - included.length,
      active: list.filter((i) => i.policy_status === 'published').length,
      attention: included.filter((i) => i.policy_status !== 'published').length,
    });
  };

  const patchPolicy = async (policyId, payload, okMsg) => {
    try {
      const { data } = await axios.patch(`/api/trust-center/policies/${policyId}`, payload);
      setItems((p) => { const next = p.map((it) => (it.policy_id === policyId ? data.item : it)); recount(next); return next; });
      applyServerDocs(data.documents);
      if (okMsg) toast.success(okMsg);
      return data.item;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
      return null;
    }
  };

  const bulk = async (action, access) => {
    if (selected.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post('/api/trust-center/policies/bulk', { policy_ids: selected, action, access });
      applyServerDocs(data.documents);
      await load();
      setSelected([]);
      toast.success(`${data.affected} ${data.affected === 1 ? 'policy' : 'policies'} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    } finally { setBulkBusy(false); }
  };

  const frameworks = [...new Set(items.flatMap((i) => i.frameworks || []))].sort();

  // Refinement #4: internal-only identifiers (NAME_ONLY_*, NO_DRIFT_*,
  // hashes, empty strings) must NEVER surface as the primary policy name.
  // Show a friendly placeholder instead; the raw id is kept in a title
  // attribute for debugging.
  const looksLikeInternalId = (s) => {
    if (!s) return true;
    const t = String(s).trim();
    if (!t) return true;
    if (/^(NAME_ONLY_|NO_DRIFT_|POLICY_|UUID_)/i.test(t)) return true;
    if (/^[a-f0-9]{20,}$/i.test(t)) return true; // long hex hash
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(t)) return true;
    return false;
  };
  const displayName = (it) => (looksLikeInternalId(it.title) ? 'Untitled Policy' : it.title);
  const isSyntheticName = (it) => looksLikeInternalId(it.title);

  const rows = items.filter((it) => {
    if (q && !`${it.title} ${it.policy_type} ${(it.frameworks || []).join(' ')}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (fType !== 'all' && it.policy_type !== fType) return false;
    if (fStatus !== 'all' && it.policy_status !== fStatus) return false;
    if (fAccess !== 'all' && it.access !== fAccess) return false;
    if (fIncluded === 'included' && !it.included) return false;
    if (fIncluded === 'excluded' && it.included) return false;
    if (fFramework !== 'all' && !(it.frameworks || []).includes(fFramework)) return false;
    return true;
  });

  // Refinement #2 & #7: pre-compute per-status counts. These drive the
  // summary chips AND become the default filter behavior — Published +
  // Draft show inline, Archived/Removed collapse into their own section.
  const byStatus = {
    published: rows.filter((r) => r.policy_status === 'published'),
    draft: rows.filter((r) => r.policy_status === 'draft'),
    archived: rows.filter((r) => r.policy_status === 'archived'),
    missing: rows.filter((r) => r.policy_status === 'missing'),
  };
  const includedByStatus = {
    published: byStatus.published.filter((r) => r.included),
    draft: byStatus.draft.filter((r) => r.included),
    archived: byStatus.archived.filter((r) => r.included),
  };
  // Refinement #6: draft-or-archived included policies are the ones the
  // banner should explain — they are not publicly visible right now.
  const unavailableIncluded = items.filter((i) => i.included && i.policy_status !== 'published');
  const staleIncluded = unavailableIncluded; // kept for existing refs, semantics unchanged

  const [showArchivedSection, setShowArchivedSection] = useState(false);
  const toggleSel = (pid) => setSelected((p) => (p.includes(pid) ? p.filter((x) => x !== pid) : [...p, pid]));
  const resourceUrl = (it) => (tcSlug && it.resource_id ? `${window.location.origin}/t/${tcSlug}/resources?resource=${it.resource_id}` : null);
  const selCls = 'rounded-[10px] border border-[#CBD5E1] text-xs px-2 py-1.5 bg-white';

  return (
    <Card className="p-5 space-y-4" data-testid="tc-policies-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-semibold text-[15px] text-[#0A0A0A] flex items-center gap-2">
          Policies
        </h3>
        {/* Kept the subtle live-sync indicator so admins are reassured
            no manual sync is required. Manual "Sync now" button was
            removed in a prior refinement. */}
        <span
          className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#0F62FE] bg-[#EAF2FF] border border-[#CFE0FF] rounded-full px-2.5 py-1"
          data-testid="tc-policies-autosync-badge"
          title="Changes made in Workspace Policies appear here automatically"
        >
          <CheckCircle2 className="w-3 h-3" /> Auto-synced with Workspace Policies
        </span>
      </div>
      <p className="text-xs text-[#64748B] -mt-2" data-testid="tc-policies-description">
        Configure which workspace policies appear on your public Trust Center.
        Policies automatically stay synchronized with the Workspace Policies module — no manual action is required.
      </p>

      {/* Refinement #7: quick summary chips. Clicking one filters the
          table by that status; clicking again clears the filter. Counts
          reflect currently-included policies since that's what admins
          care about on this configuration page. */}
      {counts && (
        <div className="flex flex-wrap items-center gap-2" data-testid="tc-policies-summary-chips">
          <button
            type="button"
            onClick={() => setFStatus(fStatus === 'published' ? 'all' : 'published')}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${fStatus === 'published' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'}`}
            data-testid="tc-policies-chip-published"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Published <span className="tabular-nums">{includedByStatus.published.length}</span>
          </button>
          <button
            type="button"
            onClick={() => setFStatus(fStatus === 'draft' ? 'all' : 'draft')}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${fStatus === 'draft' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300'}`}
            data-testid="tc-policies-chip-draft"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            Draft <span className="tabular-nums">{includedByStatus.draft.length}</span>
          </button>
          <button
            type="button"
            onClick={() => setFStatus(fStatus === 'archived' ? 'all' : 'archived')}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${fStatus === 'archived' ? 'bg-slate-100 border-slate-400 text-slate-800' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
            data-testid="tc-policies-chip-archived"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
            Archived <span className="tabular-nums">{includedByStatus.archived.length}</span>
          </button>
          <span className="ml-auto text-[11px] text-[#94A3B8]" data-testid="tc-policies-summary-total">
            {counts.included} of {counts.total} included in Trust Center
          </span>
        </div>
      )}

      {/* Refinement #6: single reassuring callout (not a warning banner). */}
      {unavailableIncluded.length > 0 && (
        <div className="flex items-start gap-2 text-xs text-[#334155] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3" data-testid="tc-policies-warning">
          <InfoIcon className="w-4 h-4 shrink-0 mt-0.5 text-[#0F62FE]" />
          <span>
            <b>{unavailableIncluded.length} included polic{unavailableIncluded.length === 1 ? 'y is' : 'ies are'} currently unavailable</b>{' '}
            because they are drafts or no longer active. They will automatically appear in the Trust Center once they become published again.
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search policies…" data-testid="tc-policies-search"
            className="w-full pl-8 pr-3 py-1.5 rounded-[10px] border border-[#CBD5E1] text-xs focus:outline-none focus:border-[#0F62FE]" />
        </div>
        <select value={fType} onChange={(e) => setFType(e.target.value)} className={selCls} data-testid="tc-policies-filter-type">
          <option value="all">All types</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={selCls} data-testid="tc-policies-filter-status">
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
          <option value="missing">Removed</option>
        </select>
        {frameworks.length > 0 && (
          <select value={fFramework} onChange={(e) => setFFramework(e.target.value)} className={selCls} data-testid="tc-policies-filter-framework">
            <option value="all">All frameworks</option>
            {frameworks.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        )}
        <select value={fAccess} onChange={(e) => setFAccess(e.target.value)} className={selCls} data-testid="tc-policies-filter-access">
          <option value="all">All access</option>
          {POLICY_ACCESS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <select value={fIncluded} onChange={(e) => setFIncluded(e.target.value)} className={selCls} data-testid="tc-policies-filter-included">
          <option value="all">Included & excluded</option>
          <option value="included">Included only</option>
          <option value="excluded">Excluded only</option>
        </select>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-[#D7E0F2] bg-[#F0F6FF]" data-testid="tc-policies-bulk-bar">
          <span className="text-xs font-semibold text-[#0A0A0A]">{selected.length} selected</span>
          <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => bulk('include')} className="rounded-[10px] h-7 text-xs border-[#A7F3D0] text-[#047857]" data-testid="tc-policies-bulk-include">Include</Button>
          <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => bulk('exclude')} className="rounded-[10px] h-7 text-xs border-[#E2E8F0] text-[#64748B]" data-testid="tc-policies-bulk-exclude">Exclude</Button>
          <span className="inline-flex items-center gap-1">
            <select value={bulkAccess} onChange={(e) => setBulkAccess(e.target.value)} className={selCls} data-testid="tc-policies-bulk-access-select">
              {POLICY_ACCESS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => bulk('set_access', bulkAccess)} className="rounded-[10px] h-7 text-xs border-[#D7E0F2]" data-testid="tc-policies-bulk-access-apply">Set access</Button>
          </span>
          <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => bulk('reset')} className="rounded-[10px] h-7 text-xs border-[#D7E0F2] gap-1" data-testid="tc-policies-bulk-reset"><RotateCcw className="w-3 h-3" /> Reset to default</Button>
          <button onClick={() => setSelected([])} className="text-xs text-[#64748B] hover:text-[#0A0A0A] ml-auto" data-testid="tc-policies-bulk-clear">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#0F62FE]" /></div>
      ) : items.length === 0 ? (
        <Empty>No workspace policies found yet. Create and publish policies in Nova&apos;s Policies module — they will appear here automatically.</Empty>
      ) : (
        // Refinement #3: visually group rows by status. Published & Draft
        // render inline; Archived + Removed collapse into a hide-by-default
        // section that admins can expand.
        (() => {
          const renderRow = (it, i) => {
            const badge = POLICY_SRC_BADGE[it.policy_status] || POLICY_SRC_BADGE.published;
            const draftHint = it.included && it.policy_status !== 'published'
              ? `This policy is currently ${it.policy_status === 'missing' ? 'removed' : it.policy_status} in the workspace, so it is not shown publicly. It will appear automatically when re-published.`
              : null;
            const nameSynthetic = isSyntheticName(it);
            return (
              <tr key={it.policy_id} className={`border-t border-[#F1F5F9] align-top ${!it.included ? 'bg-[#FAFBFD]' : ''}`} data-testid={`tc-policy-row-${i}`}>
                <td className="px-3 py-3"><input type="checkbox" aria-label={`Select ${displayName(it)}`} checked={selected.includes(it.policy_id)} onChange={() => toggleSel(it.policy_id)} data-testid={`tc-policy-check-${i}`} /></td>
                <td className="px-2 py-3">
                  <Switch checked={it.included} onCheckedChange={(v) => patchPolicy(it.policy_id, { include: v }, v ? 'Policy included in Trust Center' : 'Policy excluded from Trust Center')} data-testid={`tc-policy-include-${i}`} />
                </td>
                <td className="px-2 py-3">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`font-semibold text-[13px] ${it.included ? 'text-[#0A0A0A]' : 'text-[#64748B]'} ${nameSynthetic ? 'italic text-[#94A3B8]' : ''}`}
                      data-testid={`tc-policy-title-${i}`}
                      title={nameSynthetic ? `Missing display name (internal id: ${it.title || it.policy_id})` : displayName(it)}
                    >
                      {displayName(it)}
                    </span>
                    {nameSynthetic && (
                      <AlertTriangle className="w-3 h-3 text-amber-500" data-testid={`tc-policy-name-warning-${i}`} title="This policy is missing a display name in the workspace." />
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    {it.generated && <Badge className="border text-[9px] px-1.5 py-0 bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE] gap-1" data-testid={`tc-policy-auto-${i}`}><Sparkles className="w-2.5 h-2.5" /> Auto</Badge>}
                    {(it.frameworks || []).slice(0, 3).map((f) => <span key={f} className="text-[9px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#64748B]">{f}</span>)}
                    {(it.frameworks || []).length > 3 && <span className="text-[9px] text-[#94A3B8]">+{it.frameworks.length - 3}</span>}
                  </div>
                </td>
                <td className="px-2 py-3 text-[#475569] whitespace-nowrap" data-testid={`tc-policy-type-${i}`}>{it.policy_type || '—'}</td>
                <td className="px-2 py-3 text-[#94A3B8] whitespace-nowrap text-[11px]" data-testid={`tc-policy-version-${i}`}>v{it.policy_version || 1}</td>
                <td className="px-2 py-3">
                  {/* Refinement #5: concise status badge only. Extended explanation
                      lives in the badge's tooltip, not on every row. */}
                  <Badge
                    className={`border text-[10px] px-1.5 py-0 ${badge.cls}`}
                    data-testid={`tc-policy-status-${i}`}
                    title={draftHint || `Workspace status: ${badge.label}`}
                  >
                    {badge.label}
                  </Badge>
                </td>
                <td className="px-2 py-3">
                  <select value={it.access} onChange={(e) => patchPolicy(it.policy_id, { access: e.target.value }, `Access set to ${POLICY_ACCESS_LABEL[e.target.value]}`)}
                    className="rounded-lg border border-[#CBD5E1] text-[11px] px-1.5 py-1 bg-white max-w-[130px]" data-testid={`tc-policy-access-${i}`}>
                    {POLICY_ACCESS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                </td>
                <td className="px-2 py-3 text-[#94A3B8] whitespace-nowrap text-[11px]">{it.last_updated ? formatDate(it.last_updated) : '—'}</td>
                <td className="px-2 py-3 text-[#94A3B8] whitespace-nowrap text-[11px]" data-testid={`tc-policy-review-${i}`}>{it.next_review_date ? formatDate(it.next_review_date) : '—'}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setEditItem(it)} className="p-1.5 rounded-lg text-[#64748B] hover:text-[#0F62FE] hover:bg-[#F0F6FF]" title="Edit Trust Center listing" data-testid={`tc-policy-edit-${i}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setPreviewItem(it)} className="p-1.5 rounded-lg text-[#64748B] hover:text-[#0F62FE] hover:bg-[#F0F6FF]" title="Preview public listing" data-testid={`tc-policy-preview-${i}`}><Eye className="w-3.5 h-3.5" /></button>
                    <button onClick={() => { const u = resourceUrl(it); if (u) copyToClipboard(u, 'Public resource link copied'); else toast.error('Set a public URL slug first (Basics tab)'); }}
                      className="p-1.5 rounded-lg text-[#64748B] hover:text-[#0F62FE] hover:bg-[#F0F6FF]" title="Copy public resource link" data-testid={`tc-policy-copylink-${i}`}><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            );
          };

          const groupHeader = (title, subtitle, count, tone) => (
            <tr className="bg-[#FAFBFD]" data-testid={`tc-policies-group-${title.toLowerCase()}`}>
              <td colSpan={10} className="px-3 py-2 border-t border-[#F1F5F9]">
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${tone}`} />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[#0A0A0A]">{title}</span>
                  <span className="text-[11px] font-medium text-[#94A3B8]">{count}</span>
                  <span className="text-[11px] text-[#94A3B8]">·</span>
                  <span className="text-[11px] text-[#94A3B8]">{subtitle}</span>
                </div>
              </td>
            </tr>
          );

          const showGrouping = fStatus === 'all';

          return (
            <div className="tc-table-wrap overflow-x-auto border border-[var(--tc-line)]">
              <table className="w-full text-[12px] min-w-[860px]" data-testid="tc-policies-table">
                <thead>
                  <tr>
                    <th className="w-8" data-role="th"><input type="checkbox" aria-label="Select all" data-testid="tc-policies-select-all" checked={rows.length > 0 && rows.every((r) => selected.includes(r.policy_id))} onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.policy_id) : [])} /></th>
                    <th className="text-left" data-role="th">Include</th>
                    <th className="text-left" data-role="th">Policy</th>
                    <th className="text-left" data-role="th">Type</th>
                    <th className="text-left" data-role="th">Version</th>
                    <th className="text-left" data-role="th">Status</th>
                    <th className="text-left" data-role="th">Access</th>
                    <th className="text-left" data-role="th">Updated</th>
                    <th className="text-left" data-role="th">Review</th>
                    <th className="text-right" data-role="th">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!showGrouping && rows.map((it, i) => renderRow(it, i))}
                  {showGrouping && (
                    <>
                      {byStatus.published.length > 0 && groupHeader('Published', 'Visible in the Trust Center', byStatus.published.length, 'bg-emerald-500')}
                      {byStatus.published.map((it, i) => renderRow(it, `pub-${i}`))}
                      {byStatus.draft.length > 0 && groupHeader('Draft', 'Not publicly visible until published', byStatus.draft.length, 'bg-amber-500')}
                      {byStatus.draft.map((it, i) => renderRow(it, `drf-${i}`))}
                      {(byStatus.archived.length + byStatus.missing.length) > 0 && (
                        <tr className="bg-[#FAFBFD]">
                          <td colSpan={10} className="px-3 py-2 border-t border-[#F1F5F9]">
                            <button
                              type="button"
                              onClick={() => setShowArchivedSection((v) => !v)}
                              className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[#64748B] hover:text-[#0A0A0A]"
                              data-testid="tc-policies-group-archived-toggle"
                              aria-expanded={showArchivedSection}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                              Archived &amp; removed
                              <span className="text-[11px] font-medium text-[#94A3B8]">{byStatus.archived.length + byStatus.missing.length}</span>
                              <span className="text-[10px] text-[#94A3B8] normal-case tracking-normal">{showArchivedSection ? '(hide)' : '(collapsed by default)'}</span>
                            </button>
                          </td>
                        </tr>
                      )}
                      {showArchivedSection && byStatus.archived.map((it, i) => renderRow(it, `arc-${i}`))}
                      {showArchivedSection && byStatus.missing.map((it, i) => renderRow(it, `mis-${i}`))}
                    </>
                  )}
                  {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-xs text-[#64748B]" data-testid="tc-policies-nomatch">No policies match your filters.</td></tr>}
                </tbody>
              </table>
            </div>
          );
        })()
      )}

      {editItem && <PolicyEditDialog item={editItem} types={types} onClose={() => setEditItem(null)}
        onSave={async (payload) => { const ok = await patchPolicy(editItem.policy_id, payload, 'Trust Center listing updated'); if (ok) setEditItem(null); }} />}
      {previewItem && <PolicyPreviewDialog item={previewItem} onClose={() => setPreviewItem(null)} />}
    </Card>
  );
};

const PolicyEditDialog = ({ item, types, onClose, onSave }) => {
  const [f, setF] = useState({
    access: item.access, policy_type: item.policy_type || 'Other', description: item.description || '',
    owner: item.owner || '', effective_date: item.effective_date || '', next_review_date: item.next_review_date || '',
    show_next_review: !!item.show_next_review, link_mode: item.link_mode || 'request_only', external_link: item.external_link || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => { setSaving(true); try { await onSave(f); } finally { setSaving(false); } };
  const resetDesc = async () => { setSaving(true); try { await onSave({ reset_description: true }); } finally { setSaving(false); } };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="tc-policy-edit-dialog">
        <DialogTitle className="text-lg font-semibold">Trust Center listing — {item.title}</DialogTitle>
        <DialogDescription className="text-xs text-[#64748B]">
          These settings affect only how this policy appears on your public Trust Center. The workspace policy itself is never modified.
        </DialogDescription>
        <div className="space-y-3 mt-1">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Access type</Label>
              <select value={f.access} onChange={(e) => set('access', e.target.value)} className="mt-1 w-full rounded-[10px] border border-[#CBD5E1] text-sm px-3 py-2 bg-white" data-testid="tc-policy-dialog-access">
                {POLICY_ACCESS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Policy type (Trust Center label)</Label>
              <select value={f.policy_type} onChange={(e) => set('policy_type', e.target.value)} className="mt-1 w-full rounded-[10px] border border-[#CBD5E1] text-sm px-3 py-2 bg-white" data-testid="tc-policy-dialog-type">
                {(types.length ? types : [f.policy_type]).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Trust Center description {item.description_overridden && <span className="text-[#B45309]">(overridden)</span>}</Label>
              <button type="button" onClick={resetDesc} className="text-[10px] font-semibold text-[#0F62FE] hover:underline" data-testid="tc-policy-dialog-resetdesc">Reset to policy description</button>
            </div>
            <Textarea value={f.description} onChange={(e) => set('description', e.target.value)} rows={3} className="mt-1 rounded-[10px] text-sm" placeholder="Short public description" data-testid="tc-policy-dialog-description" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Owner (Trust Center)</Label>
              <Input value={f.owner} onChange={(e) => set('owner', e.target.value)} placeholder="security@company.com" className="mt-1 rounded-[10px] text-sm" data-testid="tc-policy-dialog-owner" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Effective date</Label>
              <Input type="date" value={f.effective_date} onChange={(e) => set('effective_date', e.target.value)} className="mt-1 rounded-[10px] text-sm" data-testid="tc-policy-dialog-effective" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Next review date</Label>
              <Input type="date" value={f.next_review_date} onChange={(e) => set('next_review_date', e.target.value)} className="mt-1 rounded-[10px] text-sm" data-testid="tc-policy-dialog-review" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#475569] cursor-pointer" data-testid="tc-policy-dialog-showreview">
            <input type="checkbox" checked={f.show_next_review} onChange={(e) => set('show_next_review', e.target.checked)} className="rounded" />
            Show next review date publicly
          </label>
          <div className="border-t border-[#F1F5F9] pt-3 space-y-2">
            <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Resource link behavior</Label>
            <div className="flex items-center gap-4 text-xs text-[#334155]">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="tc-policy-linkmode" checked={f.link_mode === 'request_only'} onChange={() => set('link_mode', 'request_only')} data-testid="tc-policy-dialog-linkmode-request" />
                Request only (recommended)
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="tc-policy-linkmode" checked={f.link_mode === 'external_link'} onChange={() => set('link_mode', 'external_link')} data-testid="tc-policy-dialog-linkmode-link" />
                Safe public URL
              </label>
            </div>
            {f.link_mode === 'external_link' && (
              <>
                <Input value={f.external_link} onChange={(e) => set('external_link', e.target.value)} placeholder="https://… (approved public copy only)" className="rounded-[10px] text-sm" data-testid="tc-policy-dialog-link" />
                <p className="text-[10px] text-[#94A3B8]">Only opens publicly when access is <b>Public</b>. Never paste internal file URLs or storage links.</p>
              </>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="rounded-[10px] border-[#D7E0F2]" onClick={onClose} data-testid="tc-policy-dialog-cancel">Cancel</Button>
          <Button className="bg-[#0F62FE] hover:bg-[#0A4FCC] rounded-[10px]" disabled={saving} onClick={submit} data-testid="tc-policy-dialog-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save listing'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PolicyPreviewDialog = ({ item, onClose }) => {
  const locked = item.access !== 'public';
  const visible = item.included && item.policy_status === 'published' && item.access !== 'hidden';
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="tc-policy-preview-dialog">
        <DialogTitle className="text-lg font-semibold">Public listing preview</DialogTitle>
        <DialogDescription className="text-xs text-[#64748B]">How this policy appears in your public Resources library.</DialogDescription>
        {visible ? (
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 mt-1" data-testid="tc-policy-preview-card">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm text-[#0A0A0A]">{item.title}</span>
              <Badge className={`border text-[10px] px-1.5 py-0 ${item.access === 'public' ? 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' : 'bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]'}`}>{POLICY_ACCESS_LABEL[item.access]}</Badge>
              <span className="text-[10px] text-[#64748B]">v{item.policy_version || 1}</span>
            </div>
            <div className="text-[11px] text-[#64748B] mt-1">{item.policy_type || 'Policy'}{(item.frameworks || []).length ? ` · ${item.frameworks.slice(0, 3).join(', ')}` : ''}</div>
            {item.description && <p className="text-xs text-[#475569] mt-2">{item.description}</p>}
            <div className="flex items-center gap-2 mt-3">
              <span className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-[#0F62FE] text-white">
                {item.access === 'public' && item.link_mode === 'external_link' && item.external_link ? 'Open' : 'Request Access'}
              </span>
              {item.show_next_review && item.next_review_date && <span className="text-[10px] text-[#94A3B8]">Review: {formatDate(item.next_review_date)}</span>}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-4 mt-1 text-xs text-[#64748B]" data-testid="tc-policy-preview-hidden">
            This policy is <b>not visible publicly</b> right now
            {!item.included ? ' because it is excluded from the Trust Center.' : item.access === 'hidden' ? ' because its access type is Hidden.' : ` because the source workspace policy is ${item.policy_status === 'missing' ? 'removed' : item.policy_status}.`}
          </div>
        )}
        <div className="flex justify-end mt-3">
          <Button variant="outline" className="rounded-[10px] border-[#D7E0F2]" onClick={onClose} data-testid="tc-policy-preview-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UpdatesEditor = ({ updates, onAdd, onEdit, onToggleHidden, onDelete }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [adding, setAdding] = useState(false);

  const submitAdd = async () => {
    if (!title.trim()) { toast.error('Add a title'); return; }
    setAdding(true);
    const ok = await onAdd({ title: title.trim(), description: description.trim() });
    setAdding(false);
    if (ok) { setTitle(''); setDescription(''); }
  };

  const sorted = [...(updates || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  return (
    <div className="space-y-5">
      <Card className="p-5 space-y-3" data-testid="tc-updates-add-card">
        <h3 className="font-semibold text-[15px] text-[#0A0A0A] flex items-center gap-2"><Clock className="w-4 h-4 text-[#0F62FE]" /> Add a manual update</h3>
        <p className="text-xs text-[#64748B]">Updates are auto-generated when you publish, change frameworks, add documents or subprocessors. Add your own milestones here. The public timeline is sorted newest-first by date. Keep text generic — never include internal control or evidence detail.</p>
        <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Completed annual penetration test" className="rounded-[10px]" data-testid="tc-update-new-title" /></Field>
        <Field label="Description (optional)"><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Short, public-facing summary" className="rounded-[10px]" data-testid="tc-update-new-desc" /></Field>
        <div className="flex justify-end">
          <Button onClick={submitAdd} disabled={adding} className="bg-[#0F62FE] hover:bg-[#0A4FCC] rounded-[10px]" data-testid="tc-update-add-btn">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add update</>}</Button>
        </div>
      </Card>

      <Card className="p-5 space-y-3" data-testid="tc-updates-list-card">
        <h3 className="font-semibold text-[15px] text-[#0A0A0A]">Recent updates timeline</h3>
        {sorted.length === 0 && <Empty>No updates yet. Auto-updates appear here as you make changes.</Empty>}
        {sorted.map((u) => (
          <UpdateRow key={u.id} u={u} onEdit={onEdit} onToggleHidden={onToggleHidden} onDelete={onDelete} />
        ))}
      </Card>
    </div>
  );
};

const UpdateRow = ({ u, onEdit, onToggleHidden, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(u.title || '');
  const [description, setDescription] = useState(u.description || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    const ok = await onEdit(u.id, { title: title.trim(), description: description.trim() });
    setBusy(false);
    if (ok) setEditing(false);
  };

  return (
    <div className={`rounded-lg border p-3 ${u.hidden ? 'border-[#E2E8F0] bg-[#F8FAFC] opacity-80' : 'border-[#E2E8F0]'}`} data-testid={`tc-update-${u.id}`}>
      {editing ? (
        <div className="space-y-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-[10px]" data-testid={`tc-update-edit-title-${u.id}`} />
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="rounded-[10px] text-sm" data-testid={`tc-update-edit-desc-${u.id}`} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setTitle(u.title || ''); setDescription(u.description || ''); }} className="rounded-[10px] border-[#D7E0F2]"><X className="w-4 h-4" /></Button>
            <Button size="sm" onClick={save} disabled={busy} className="bg-[#0F62FE] hover:bg-[#0A4FCC] rounded-[10px]" data-testid={`tc-update-save-${u.id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-sm font-semibold ${u.hidden ? 'text-[#94A3B8]' : 'text-[#0A0A0A]'}`}>{u.title}</span>
              {u.auto && <Badge className="bg-[#EFF6FF] text-[#0F62FE] border border-[#BFDBFE] gap-1 text-[10px] px-1.5 py-0" data-testid={`tc-update-auto-${u.id}`}><Sparkles className="w-3 h-3" /> Auto</Badge>}
              <Badge className="bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0] text-[10px] px-1.5 py-0">{UPDATE_TYPE_LABELS[u.type] || 'Manual'}</Badge>
              {u.hidden && <Badge className="bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA] text-[10px] px-1.5 py-0">Hidden</Badge>}
            </div>
            {u.description && <p className="text-sm text-[#64748B] mt-0.5">{u.description}</p>}
            <p className="text-[11px] text-[#94A3B8] mt-1">{formatDate(u.date)}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => onToggleHidden(u.id, !u.hidden)} className="text-[#64748B] hover:text-[#0F62FE] p-1" title={u.hidden ? 'Show publicly' : 'Hide from public'} data-testid={`tc-update-toggle-${u.id}`}>{u.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            <button onClick={() => setEditing(true)} className="text-[#64748B] hover:text-[#0F62FE] p-1" title="Edit" data-testid={`tc-update-edit-${u.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => onDelete(u.id)} className="text-[#94A3B8] hover:text-[#DC2626] p-1" title="Delete" data-testid={`tc-update-del-${u.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
};

const CardListEditor = ({ title, listKey, cfg, updateItem, removeItem, addItem, reorder, testid, withType }) => (
  <EditorCard title={title} count={(cfg[listKey] || []).length}
    onAdd={() => addItem(listKey, { id: uid(), title: '', description: '', enabled: true, ...(withType ? { type: 'administrative' } : {}) })}
    addTestid={`${testid}-add`} testid={`${testid}-card`}>
    <SortableList items={cfg[listKey] || []} onReorder={(n) => reorder(listKey, n)} prefix={testid}>
      {(c, i, dnd) => (
        <SortableRow dnd={dnd} testid={`${testid}-item-${i}`}>
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Input value={c.title} onChange={(e) => updateItem(listKey, i, { title: e.target.value })} placeholder="Title" className="rounded-[10px]" data-testid={`${testid}-title-${i}`} />
              {withType && (
                <Select value={(c.type === 'technical') ? 'technical' : 'administrative'} onValueChange={(v) => updateItem(listKey, i, { type: v })}>
                  <SelectTrigger className="rounded-[10px] w-[150px] text-xs shrink-0" data-testid={`${testid}-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="administrative">Administrative</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Switch checked={c.enabled !== false} onCheckedChange={(v) => updateItem(listKey, i, { enabled: v })} />
              <button onClick={() => removeItem(listKey, i)} className="text-[#94A3B8] hover:text-[#DC2626] p-1" data-testid={`${testid}-del-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <Textarea value={c.description} onChange={(e) => updateItem(listKey, i, { description: e.target.value })} placeholder="Description" rows={2} className="rounded-[10px] text-sm" />
          </div>
        </SortableRow>
      )}
    </SortableList>
  </EditorCard>
);

// ---- documentation request review workflow ----
const REQ_STATUS = {
  new: { label: 'New', cls: 'bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]' },
  under_review: { label: 'Under Review', cls: 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]' },
  approved: { label: 'Approved', cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' },
  rejected: { label: 'Rejected', cls: 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]' },
  completed: { label: 'Completed', cls: 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]' },
};
const REQ_FILTERS = [['all', 'All'], ['new', 'New'], ['under_review', 'Under Review'], ['approved', 'Approved'], ['rejected', 'Rejected'], ['completed', 'Completed']];
const reqResources = (r) => (r.requested_documents && r.requested_documents.length ? r.requested_documents : (r.document_title ? [r.document_title] : []));

const RequestsTab = ({ requests, docs, onAction }) => {
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [approveFor, setApproveFor] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);

  const counts = requests.reduce((a, r) => { const s = r.status || 'new'; a[s] = (a[s] || 0) + 1; return a; }, {});
  const rows = requests.filter((r) => filter === 'all' || (r.status || 'new') === filter);

  if (requests.length === 0) {
    return (
      <Card className="p-0" data-testid="tc-requests-table">
        <div className="text-center py-16" data-testid="tc-requests-empty"><Inbox className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" strokeWidth={1.5} /><div className="font-semibold text-[#0A0A0A]">No access requests yet</div><div className="text-sm text-[#64748B] mt-1">Documentation requests from your public page appear here.</div></div>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="tc-requests-table">
      <div className="flex flex-wrap gap-2">
        {REQ_FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`tc-req-filter-${k}`} aria-pressed={filter === k}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === k ? 'bg-[#0F62FE] text-white border-[#0F62FE]' : 'bg-white text-[#475569] border-[#D7E0F2] hover:border-[#0F62FE]'}`}>
            {label}{k !== 'all' && counts[k] ? ` (${counts[k]})` : ''}
          </button>
        ))}
      </div>

      <div className="space-y-2.5">
        {rows.map((r) => {
          const st = REQ_STATUS[r.status || 'new'] || REQ_STATUS.new;
          const isOpen = openId === r.id;
          const resources = reqResources(r);
          const status = r.status || 'new';
          return (
            <Card key={r.id} className="p-0 overflow-hidden" data-testid={`tc-request-${r.id}`}>
              <button onClick={() => setOpenId(isOpen ? null : r.id)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#F8FAFC] transition-colors" data-testid={`tc-request-toggle-${r.id}`}>
                <ChevronRight className={`w-4 h-4 text-[#94A3B8] transition-transform shrink-0 ${isOpen ? 'rotate-90' : ''}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#0A0A0A] text-sm">{r.name}</span>
                    {r.company && <span className="text-xs text-[#64748B]">· {r.company}</span>}
                  </div>
                  <div className="text-xs text-[#94A3B8] mt-0.5 truncate">{resources.length ? resources.join(', ') : 'General inquiry'}</div>
                </div>
                <Badge className={`border text-[11px] shrink-0 ${st.cls}`}>{st.label}</Badge>
                {r.nda_required && <Badge className="border text-[10px] shrink-0 bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]" data-testid={`tc-request-nda-${r.id}`}>NDA</Badge>}
                <span className="text-xs text-[#94A3B8] shrink-0 hidden sm:inline">{formatDate(r.requested_at)}</span>
              </button>

              {isOpen && (
                <div className="border-t border-[#E2E8F0] p-4 space-y-4 bg-[#FCFDFE]" data-testid={`tc-request-detail-${r.id}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <Info icon={Mail} label="Email" value={r.email} />
                    <Info icon={Building2} label="Company" value={r.company || '—'} />
                    <Info icon={Pencil} label="Job title" value={r.job_title || '—'} />
                    <Info icon={Clock} label="Submitted" value={formatDate(r.requested_at)} />
                  </div>

                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Requested resources</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {resources.length ? resources.map((d, i) => <span key={i} className="text-xs px-2 py-1 rounded-md bg-white border border-[#E2E8F0] text-[#334155]">{d}</span>) : <span className="text-sm text-[#64748B]">General inquiry</span>}
                    </div>
                  </div>

                  {r.message && (
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8] flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Message</Label>
                      <p className="text-sm text-[#334155] mt-1 bg-white border border-[#E2E8F0] rounded-lg p-3 whitespace-pre-line">{r.message}</p>
                    </div>
                  )}

                  {r.status === 'approved' && (r.approved_documents || []).length > 0 && (
                    <div className="text-sm bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg p-3" data-testid={`tc-request-approved-${r.id}`}>
                      <span className="font-semibold text-[#047857]">Approved resources:</span> <span className="text-[#065F46]">{r.approved_documents.join(', ')}</span>
                    </div>
                  )}

                  <InternalNote request={r} onAction={onAction} />

                  {(r.history || []).length > 0 && (
                    <details className="text-sm" data-testid={`tc-request-history-${r.id}`}>
                      <summary className="cursor-pointer text-[#64748B] font-medium inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Status history ({r.history.length})</summary>
                      <ol className="mt-2 border-l border-[#E2E8F0] ml-1.5 space-y-2">
                        {r.history.map((h) => (
                          <li key={h.id} className="pl-3 text-xs text-[#64748B] relative">
                            <span className="absolute -left-[3px] top-1.5 w-1.5 h-1.5 rounded-full bg-[#CBD5E1]" />
                            <span className="text-[#94A3B8]">
                              {h.from_status && h.from_status !== h.status && (
                                <>{(REQ_STATUS[h.from_status] || {}).label || h.from_status} <span className="text-[#CBD5E1]">→</span> </>
                              )}
                            </span>
                            <span className="font-medium text-[#334155]">{(REQ_STATUS[h.status] || {}).label || h.action}</span>
                            <span className="text-[#94A3B8]"> · {formatDate(h.at)} · {h.actor}</span>
                            {h.message && <div className="text-[#94A3B8] italic mt-0.5">“{h.message}”</div>}
                            {h.note && <div className="text-[#94A3B8] mt-0.5">Note: {h.note}</div>}
                          </li>
                        ))}
                      </ol>
                    </details>
                  )}

                  <div className="flex flex-wrap gap-2 pt-3 border-t border-[#E2E8F0]" data-testid={`tc-request-actions-${r.id}`}>
                    {(status === 'new') && (
                      <Button size="sm" variant="outline" className="rounded-[10px] border-[#D7E0F2]" data-testid={`tc-request-review-${r.id}`} onClick={() => onAction(r.id, { action: 'review' })}><Eye className="w-4 h-4 mr-1" /> Mark Under Review</Button>
                    )}
                    {(status === 'new' || status === 'under_review' || status === 'rejected') && (
                      <Button size="sm" className="bg-[#059669] hover:bg-[#047857] rounded-[10px]" data-testid={`tc-request-approve-${r.id}`} onClick={() => setApproveFor(r)}><CheckCircle2 className="w-4 h-4 mr-1" /> {status === 'rejected' ? 'Approve instead' : 'Approve'}</Button>
                    )}
                    {(status === 'new' || status === 'under_review' || status === 'approved') && (
                      <Button size="sm" variant="outline" className="rounded-[10px] border-[#FECACA] text-[#B91C1C] hover:bg-[#FEF2F2]" data-testid={`tc-request-reject-${r.id}`} onClick={() => setRejectFor(r)}><XCircle className="w-4 h-4 mr-1" /> {status === 'approved' ? 'Reject instead' : 'Reject'}</Button>
                    )}
                    {(status === 'approved') && (
                      <Button size="sm" variant="outline" className="rounded-[10px] border-[#D7E0F2]" data-testid={`tc-request-complete-${r.id}`} onClick={() => onAction(r.id, { action: 'complete' })}><Check className="w-4 h-4 mr-1" /> Mark Completed</Button>
                    )}
                    {(status === 'approved' || status === 'rejected' || status === 'completed') && (
                      <Button size="sm" variant="outline" className="rounded-[10px] border-[#D7E0F2]" data-testid={`tc-request-reopen-${r.id}`} onClick={() => onAction(r.id, { action: 'reopen' })}><History className="w-4 h-4 mr-1" /> Reopen</Button>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
        {rows.length === 0 && <p className="text-sm text-[#64748B] text-center py-8">No requests with this status.</p>}
      </div>

      {approveFor && <ApproveDialog request={approveFor} docs={docs} onClose={() => setApproveFor(null)} onAction={onAction} />}
      {rejectFor && <RejectDialog request={rejectFor} onClose={() => setRejectFor(null)} onAction={onAction} />}
    </div>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2"><Icon className="w-3.5 h-3.5 text-[#94A3B8] mt-0.5 shrink-0" /><span><span className="text-[#94A3B8] text-xs">{label}: </span><span className="text-[#334155]">{value}</span></span></div>
);

const InternalNote = ({ request, onAction }) => {
  const [note, setNote] = useState(request.internal_note || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    await onAction(request.id, { action: 'note', note });
    setBusy(false);
  };
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Internal note (private — never emailed)</Label>
      <div className="flex gap-2 mt-1.5">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="rounded-[10px] text-sm" placeholder="Add an internal note for your team…" data-testid={`tc-request-note-${request.id}`} />
        <Button size="sm" variant="outline" className="rounded-[10px] border-[#D7E0F2] shrink-0 self-start" disabled={busy} onClick={save} data-testid={`tc-request-note-save-${request.id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
      </div>
    </div>
  );
};

const ApproveDialog = ({ request, docs, onClose, onAction }) => {
  const requested = reqResources(request);
  const docTitles = (docs || []).map((d) => (d.title || '').trim()).filter(Boolean);
  const options = Array.from(new Set([...requested, ...docTitles]));
  const [selected, setSelected] = useState(requested.length ? requested : options);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const toggle = (t) => setSelected((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  const submit = async () => {
    setBusy(true);
    const ok = await onAction(request.id, { action: 'approve', approved_documents: selected, custom_message: message.trim() });
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="tc-approve-dialog">
        <DialogTitle>Approve request — {request.name}</DialogTitle>
        <DialogDescription>Select the resources to approve. The requester is emailed the resource names only (no file links). Your security team follows up with secure delivery.</DialogDescription>
        {request.status === 'rejected' && (
          <div className="flex items-start gap-2 bg-[#FFFBEB] border border-[#FDE68A] rounded-[10px] p-3 text-xs text-[#92400E]" data-testid="tc-approve-reversal-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>This request was previously <b>Rejected</b>. Approving it now will change its status and send an approval email to the requester.</span>
          </div>
        )}
        <div className="space-y-2 max-h-56 overflow-y-auto mt-2">
          {options.length === 0 && <p className="text-sm text-[#64748B]">No specific resources — a general approval email will be sent.</p>}
          {options.map((t) => (
            <label key={t} className="flex items-center gap-2.5 text-sm cursor-pointer p-2 rounded-lg hover:bg-[#F8FAFC]" data-testid={`tc-approve-doc-${t.replace(/\s+/g, '-').toLowerCase()}`}>
              <Checkbox checked={selected.includes(t)} onCheckedChange={() => toggle(t)} />
              <span className="text-[#334155]">{t}</span>
            </label>
          ))}
        </div>
        <div className="mt-2">
          <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Custom message (optional, included in email)</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className="rounded-[10px] text-sm mt-1.5" placeholder="e.g. Our team will share these under NDA within 2 business days." data-testid="tc-approve-message" />
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" className="rounded-[10px] border-[#D7E0F2]" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#059669] hover:bg-[#047857] rounded-[10px]" disabled={busy} onClick={submit} data-testid="tc-approve-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve & notify'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RejectDialog = ({ request, onClose, onAction }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    const ok = await onAction(request.id, { action: 'reject', custom_message: reason.trim() });
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="tc-reject-dialog">
        <DialogTitle>Reject request — {request.name}</DialogTitle>
        <DialogDescription>A polite rejection email will be sent to the requester. Adding a reason is optional and will be included in the email.</DialogDescription>
        {request.status === 'approved' && (
          <div className="flex items-start gap-2 bg-[#FEF2F2] border border-[#FECACA] rounded-[10px] p-3 text-xs text-[#991B1B]" data-testid="tc-reject-reversal-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>This request was previously <b>Approved</b>. Rejecting it now will change its status and send a status-update email to the requester.</span>
          </div>
        )}
        <div className="mt-2">
          <Label className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Reason (optional)</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="rounded-[10px] text-sm mt-1.5" placeholder="e.g. We share these documents only under a signed NDA." data-testid="tc-reject-reason" />
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" className="rounded-[10px] border-[#D7E0F2]" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#DC2626] hover:bg-[#B91C1C] rounded-[10px]" disabled={busy} onClick={submit} data-testid="tc-reject-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject & notify'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---- Trust Center Health: admin-only publishing readiness (mirrors backend compute_health) ----
const TC_ACCESS_MAP = { public: 'public', gated: 'request', hidden: 'hidden' };
const resolveDocAccessAdmin = (d) => {
  const a = (d.access || '').toLowerCase();
  if (a === 'restricted') return 'request';
  if (['public', 'request', 'hidden'].includes(a)) return a;
  return TC_ACCESS_MAP[(d.visibility || 'gated').toLowerCase()] || 'request';
};

export const computeHealth = (cfg, controlsCount = 0, activePolicies = null) => {
  const c = cfg || {};
  const theme = c.theme || {};
  const disclosure = c.disclosure || {};
  const allDocs = (c.documents || []).filter((d) => resolveDocAccessAdmin(d) !== 'hidden');
  const docStatus = (d) => { const s = (d.status || '').toLowerCase(); return ['draft', 'published', 'archived'].includes(s) ? s : 'published'; };
  const docs = allDocs.filter((d) => docStatus(d) === 'published');
  const frameworks = (c.compliance_frameworks || []).filter((f) => f.enabled !== false && (f.name || '').trim());
  const faqs = (c.faqs || []).filter((f) => (f.question || '').trim() && (f.answer || '').trim());
  const updates = (c.recent_updates || []).filter((u) => !u.hidden && (u.title || '').trim());
  const email = (c.security_contact_email || '').trim();
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const missingVersion = docs.filter((d) => !d.version).length;
  const missingEffective = docs.filter((d) => !(d.effective_date || '').trim()).length;
  const missingReview = docs.filter((d) => !(d.next_review_date || '').trim()).length;
  const missingOwner = docs.filter((d) => !(d.owner || '').trim()).length;
  // ---- Policies (Trust Center publication of workspace policies) ----
  const policyDocs = (c.documents || []).filter((d) => d.policy_id);
  const srcStatus = (d) => (d.policy_missing ? 'missing' : (d.policy_status || 'published').toLowerCase());
  const inclPolicies = policyDocs.filter((d) => d.included_in_trust_center !== false);
  const inclLive = inclPolicies.filter((d) => srcStatus(d) === 'published');
  const inclStale = inclPolicies.filter((d) => srcStatus(d) !== 'published');
  const activeCount = activePolicies == null ? policyDocs.filter((d) => srcStatus(d) === 'published').length : activePolicies;
  const polMissingReview = inclLive.filter((d) => !(d.next_review_date || '').trim()).length;
  const polMissingVersion = inclLive.filter((d) => !d.policy_version).length;
  const p = (ok) => (ok ? 'pass' : 'warn');
  // `required=true` marks items that we treat as publishing prerequisites
  // in the UI. This is purely a UX hint — the backend publish rules are
  // unchanged, and admins can still publish before completing required
  // items (the copy on the panel says "recommendation"). We surface
  // these separately so admins can immediately see what most reviewers
  // will expect before the Trust Center goes live.
  const checklist = [
    { key: 'logo', required: true, section: 'Company', label: 'Company logo uploaded', status: p(!!(theme.logo_url || '').trim()), hint: 'Add your company logo in Basics.', target_tab: 'basics' },
    { key: 'description', required: true, section: 'Company', label: 'Company description completed', status: p(!!(c.overview_text || '').trim()), hint: 'Write an overview describing your security posture.', target_tab: 'basics' },
    { key: 'security_contact', required: true, section: 'Company', label: 'Security contact configured', status: p(emailOk), hint: 'Add a valid security contact email.', target_tab: 'basics' },
    { key: 'trust_statement', required: false, section: 'Company', label: 'Trust statement reviewed', status: p(!!(c.trust_statement || '').trim()), hint: 'Review your public trust statement.', target_tab: 'basics' },
    { key: 'frameworks', required: false, section: 'Compliance', label: 'Frameworks configured', status: p(frameworks.length > 0), hint: 'Add the compliance frameworks you support.', target_tab: 'compliance' },
    { key: 'controls', required: false, section: 'Controls', label: 'Controls published', status: p(controlsCount > 0), hint: 'Publish controls on your Controls page.', target_tab: 'controls' },
    { key: 'resources', required: true, section: 'Resources', label: 'Published resources available', status: p(docs.length > 0), hint: 'Publish at least one documentation resource for reviewers.', target_tab: 'documents' },
    { key: 'resource_versions', required: false, section: 'Resources', label: 'Resource versions assigned', status: p(docs.length > 0 && missingVersion === 0), hint: missingVersion ? `${missingVersion} resource(s) missing a version.` : 'Assign versions to your resources.', target_tab: 'documents' },
    { key: 'resource_effective', required: false, section: 'Resources', label: 'Effective dates assigned', status: p(docs.length > 0 && missingEffective === 0), hint: missingEffective ? `${missingEffective} resource(s) missing an effective date.` : 'Assign effective dates to your resources.', target_tab: 'documents' },
    { key: 'resource_reviews', required: false, section: 'Resources', label: 'Resource review dates assigned', status: p(docs.length > 0 && missingReview === 0), hint: missingReview ? `${missingReview} resource(s) missing a review date.` : 'Assign next-review dates to your resources.', target_tab: 'documents' },
    { key: 'resource_owners', required: false, section: 'Resources', label: 'Resource owners assigned', status: p(docs.length > 0 && missingOwner === 0), hint: missingOwner ? `${missingOwner} resource(s) missing an owner.` : 'Assign an owner to each resource.', target_tab: 'documents' },
    { key: 'resource_exposure', required: false, section: 'Resources', label: 'Draft/archived resources kept private', status: 'pass', hint: 'Only published resources are shown publicly.', target_tab: 'documents' },
    { key: 'policies_detected', required: false, section: 'Policies', label: 'Active policies detected', status: p(activeCount > 0), hint: 'Publish policies in the Policies module to feature them here.', target_tab: 'policies' },
    { key: 'policy_included', required: true, section: 'Policies', label: 'At least one policy included', status: p(inclLive.length > 0), hint: 'Include at least one policy in your Trust Center.', target_tab: 'policies' },
    { key: 'policy_access', required: false, section: 'Policies', label: 'Policy access configured', status: p(inclLive.length > 0 && inclLive.every((d) => resolveDocAccessAdmin(d) !== 'hidden')), hint: 'Set a public access type for each included policy.', target_tab: 'policies' },
    { key: 'policy_versions', required: false, section: 'Policies', label: 'Policy versions available', status: p(inclLive.length > 0 && polMissingVersion === 0), hint: polMissingVersion ? `${polMissingVersion} included policy(ies) missing a version.` : 'Include policies to surface their versions.', target_tab: 'policies' },
    { key: 'policy_reviews', required: false, section: 'Policies', label: 'Policy review dates set', status: p(inclLive.length > 0 && polMissingReview === 0), hint: polMissingReview ? `${polMissingReview} included policy(ies) missing a next review date.` : 'Add next review dates to included policies.', target_tab: 'policies' },
    { key: 'policy_exposure', required: false, section: 'Policies', label: 'Included policies still active', status: p(inclStale.length === 0), hint: inclStale.length ? `${inclStale.length} included policy(ies) are no longer active in the workspace.` : 'Archived or deleted policies are never shown publicly.', target_tab: 'policies' },
    { key: 'contact', required: true, section: 'Contact', label: 'Contact page completed', status: p(emailOk), hint: 'Complete the contact details.', target_tab: 'basics' },
    { key: 'disclosure', required: false, section: 'Contact', label: 'Responsible disclosure configured', status: p(!!(disclosure.statement || '').trim()), hint: 'Add a responsible disclosure statement.', target_tab: 'basics' },
    { key: 'faq', required: false, section: 'FAQ', label: 'FAQ completed', status: p(faqs.length > 0), hint: 'Add frequently asked questions.', target_tab: 'faq' },
    { key: 'recent_updates', required: false, section: 'Updates', label: 'Recent updates available', status: p(updates.length > 0), hint: 'Publish at least one recent update.', target_tab: 'updates' },
  ];
  const total = checklist.length;
  const passed = checklist.filter((x) => x.status === 'pass').length;
  const score = total ? Math.round((passed / total) * 100) : 0;
  const rating = score >= 90 ? 'Excellent' : score >= 70 ? 'Good' : 'Needs Attention';
  const warnings = checklist.filter((x) => x.status === 'warn');
  const requiredMissing = warnings.filter((x) => x.required);
  const recommendedMissing = warnings.filter((x) => !x.required);
  const readyToPublish = requiredMissing.length === 0;
  return { score, rating, passed, total, checklist, warnings, requiredMissing, recommendedMissing, readyToPublish };
};

const OVERVIEW_SUGGESTION_CATEGORIES = ['Compliance', 'Security', 'Privacy', 'AI Governance'];

/** Featured + Suggested manager for the Overview admin tab.
 *  Explicit `featured_on_overview=true` resources are authoritative; the
 *  server computes deterministic suggestions to fill the remaining slots
 *  (up to 4 per category) and never persists them silently. */
// Trust indicators — admin-curated "Trust at a glance" band on the public
// Overview. Max 8 shown publicly; suggestions offered, never auto-published.
const INDICATOR_TARGETS = [
  ['', 'No link'],
  ['compliance', 'Compliance'],
  ['resources', 'Resources'],
  ['subprocessors', 'Subprocessors'],
  ['faq', 'FAQ'],
  ['contact', 'Contact'],
];
const SUGGESTED_INDICATORS = [
  { label: 'Encryption at rest & in transit', target: '' },
  { label: 'MFA enforced', target: '' },
  { label: 'Annual penetration testing', target: '' },
  { label: 'Continuous monitoring', target: '' },
  { label: 'Security contact available', target: 'contact' },
];

const TrustIndicatorsManager = ({ cfg, setCfg }) => {
  const list = cfg.trust_indicators || [];
  const set = (next) => setCfg((p) => ({ ...p, trust_indicators: next }));
  const update = (idx, patch) => set(list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => set(list.filter((_, i) => i !== idx));
  const add = (label = '', target = '', target_query = '') => set([...list, { id: uid(), label, target, target_query, enabled: true }]);
  const enabledCount = list.filter((t) => t.enabled !== false).length;
  // Framework suggestions deep-link to the framework panel on Compliance.
  const fwSuggestions = (cfg.compliance_frameworks || [])
    .filter((f) => f.enabled !== false && (f.name || '').trim())
    .map((f) => ({ label: f.name.trim(), target: 'compliance', target_query: f.name.trim() }));
  // Report/certificate suggestions deep-link to the Resources category so
  // clicking "SOC 2 Type II" lands on the actual downloadable card.
  const reportDocs = (cfg.documents || []).filter((d) => (d.category || '').toLowerCase() === 'reports' || (d.category || '').toLowerCase() === 'certificates');
  const reportSuggestions = reportDocs.slice(0, 4).map((d) => ({
    label: (d.title || '').trim(),
    target: 'resources',
    target_query: (d.category || '').trim(),
  }));
  const suggestions = [...fwSuggestions, ...reportSuggestions, ...SUGGESTED_INDICATORS]
    .filter((sug) => sug.label && !list.some((t) => (t.label || '').trim().toLowerCase() === sug.label.toLowerCase()))
    .slice(0, 10);

  return (
    <div className="tc-surface p-5" data-testid="tc-indicators-card">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="tc-h2">Trust indicators</div>
          <div className="tc-muted mt-0.5">Shown as “Trust at a glance” at the top of your public Overview. Pick up to 8 meaningful signals — never vanity counters.</div>
        </div>
        <Badge className={`border text-[11px] shrink-0 ${enabledCount > 8 ? 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]' : 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'}`} data-testid="tc-indicators-count">{Math.min(enabledCount, 8)}/8 public</Badge>
      </div>

      {list.length === 0 && (
        <p className="text-sm text-[#64748B] mb-3" data-testid="tc-indicators-empty">
          No indicators yet — your framework list is shown as a fallback. Add indicators below to control exactly what appears.
        </p>
      )}

      {list.length > 0 && (
        <div className="space-y-2 mb-3" data-testid="tc-indicators-list">
          {list.map((t, i) => (
            <div key={t.id || i} className="flex items-center gap-2 flex-wrap" data-testid={`tc-indicator-${i}`}>
              <Switch checked={t.enabled !== false} onCheckedChange={(v) => update(i, { enabled: v })} data-testid={`tc-indicator-enabled-${i}`} />
              <Input value={t.label || ''} onChange={(e) => update(i, { label: e.target.value })} placeholder="e.g. MFA enforced" maxLength={60}
                className="rounded-[10px] h-9 flex-1 min-w-[200px]" data-testid={`tc-indicator-label-${i}`} />
              <select value={t.target || ''} onChange={(e) => update(i, { target: e.target.value })}
                className="rounded-[10px] border border-[#CBD5E1] text-sm px-2.5 py-2 bg-white w-[150px]" data-testid={`tc-indicator-target-${i}`}>
                {INDICATOR_TARGETS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
              {/* target_query: only meaningful when target = compliance (framework name) or resources (category).
                  Left as free text so admins can pin a specific framework panel or category filter. */}
              {(t.target === 'compliance' || t.target === 'resources') && (
                <Input value={t.target_query || ''} onChange={(e) => update(i, { target_query: e.target.value })}
                  placeholder={t.target === 'compliance' ? 'Framework name (optional)' : 'Category (optional)'}
                  maxLength={120}
                  className="rounded-[10px] h-9 w-[220px]" data-testid={`tc-indicator-query-${i}`} />
              )}
              <button onClick={() => remove(i)} className="text-[#94A3B8] hover:text-[#DC2626] p-1" data-testid={`tc-indicator-del-${i}`} aria-label="Remove indicator"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      {enabledCount > 8 && (
        <p className="text-xs text-[#B45309] mb-3" data-testid="tc-indicators-overflow">Only the first 8 enabled indicators are shown publicly.</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {suggestions.map((sug) => (
          <button key={sug.label} type="button" onClick={() => add(sug.label, sug.target, sug.target_query || '')}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-dashed border-[#CBD5E1] text-xs text-[#475569] hover:border-[#0F62FE] hover:text-[#0F62FE] transition-colors"
            data-testid={`tc-indicator-suggest-${sug.label.replace(/\s+/g, '-').toLowerCase()}`}>
            <Plus className="w-3 h-3" /> {sug.label}
          </button>
        ))}
        <button type="button" onClick={() => add()} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#CBD5E1] text-xs font-semibold text-[#0F62FE] hover:bg-[#EFF6FF] transition-colors" data-testid="tc-indicator-add">
          <Plus className="w-3 h-3" /> Add custom
        </button>
      </div>
    </div>
  );
};

/* ============================ Phase 2: Security Profile Manager ============================ */
// Admin surface for the Security single-page hub. 10 collapsible sections,
// each with: enable toggle, overview text, bullets, related documents,
// related policies, related controls, related FAQs. Empty sections auto-hide
// publicly. Suggestions come from a server-side heuristic and NEVER
// auto-publish — the admin must accept each one.
const SECURITY_SECTIONS_META = [
  { key: 'identity_access',          title: 'Identity & Access',           hint: 'MFA, SSO, RBAC, least-privilege.' },
  { key: 'infrastructure',           title: 'Infrastructure',              hint: 'Cloud, isolation, network, hardening.' },
  { key: 'encryption',               title: 'Encryption',                  hint: 'At rest and in transit, key management.' },
  { key: 'application_api_security', title: 'Application & API Security',  hint: 'APIs, WAF, dependency scanning, input validation.' },
  { key: 'logging_monitoring',       title: 'Logging & Monitoring',        hint: 'Audit logs, alerting, detection, retention.' },
  { key: 'vulnerability_management', title: 'Vulnerability Management',    hint: 'Pen tests, SLAs, patching, disclosure program.' },
  { key: 'secure_development',       title: 'Secure Development',          hint: 'SDLC, code review, secret scanning, training.' },
  { key: 'business_continuity',      title: 'Business Continuity',         hint: 'Backups, DR, RPO/RTO, incident response.' },
  { key: 'data_privacy',             title: 'Data Privacy',                hint: 'Lifecycle, residency, retention, deletion.' },
  { key: 'ai_governance',            title: 'AI Governance',               hint: 'Usage, guardrails, model risk, oversight.' },
];

const emptySecuritySection = () => ({
  enabled: true, published: false, featured_on_overview: false, overview_order: 0,
  overview: '', bullets: [],
  policy_ids: [], control_ids: [], document_ids: [], faq_ids: [],
});

const SecurityCrossLinkPicker = ({ label, hint, candidates, selectedIds, onToggle, testidPrefix }) => {
  const [expanded, setExpanded] = useState(false);
  const selected = candidates.filter((c) => selectedIds.includes(c.id));
  const unselected = candidates.filter((c) => !selectedIds.includes(c.id));
  return (
    <div className="rounded-xl border border-[#E2E8F0] p-3" data-testid={`${testidPrefix}-picker`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div>
          <div className="text-[13px] font-medium text-[var(--tc-ink)]">{label}</div>
          <div className="tc-muted text-xs">{hint}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge className="border text-[10px] bg-[#F8FAFC] text-[#475569] border-[#E2E8F0]">{selected.length} linked</Badge>
          {candidates.length > 0 && (
            <button type="button" onClick={() => setExpanded((v) => !v)}
              className="text-xs font-semibold text-[#0F62FE] hover:underline"
              data-testid={`${testidPrefix}-toggle`}>
              {expanded ? 'Hide' : `Manage (${candidates.length})`}
            </button>
          )}
        </div>
      </div>
      {selected.length > 0 && (
        <ul className="space-y-1 mb-2" data-testid={`${testidPrefix}-selected`}>
          {selected.map((c) => (
            <li key={c.id} className="flex items-center gap-2 text-xs">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#047857] shrink-0" />
              <span className="text-[#334155] flex-1 truncate">{c.title}</span>
              <button type="button" onClick={() => onToggle(c.id)} className="text-[#94A3B8] hover:text-[#DC2626]"
                data-testid={`${testidPrefix}-remove-${c.id}`} aria-label="Remove">
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {expanded && (
        <div className="border-t border-[#E2E8F0] pt-2 max-h-56 overflow-y-auto" data-testid={`${testidPrefix}-list`}>
          {unselected.length === 0 && <p className="text-xs text-[#94A3B8]">No candidates.</p>}
          <ul className="space-y-1">
            {unselected.map((c) => (
              <li key={c.id} className="flex items-start gap-2">
                <button type="button" onClick={() => onToggle(c.id)}
                  className="mt-0.5 w-4 h-4 rounded border border-[#CBD5E1] hover:border-[#0F62FE] flex items-center justify-center shrink-0"
                  data-testid={`${testidPrefix}-add-${c.id}`} aria-label="Add">
                  <Plus className="w-3 h-3 text-[#94A3B8]" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-[#334155] truncate">{c.title}</div>
                  {c.description && <div className="text-[11px] text-[#94A3B8] truncate">{c.description}</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const SecuritySectionEditor = ({ sectionKey, meta, section, suggestion, setSection }) => {
  const [expanded, setExpanded] = useState(false);
  const setField = (k, v) => setSection({ ...section, [k]: v });
  const bullets = section.bullets || [];
  const addBullet = () => setField('bullets', [...bullets, '']);
  const updateBullet = (i, v) => setField('bullets', bullets.map((b, idx) => (idx === i ? v : b)));
  const removeBullet = (i) => setField('bullets', bullets.filter((_, idx) => idx !== i));

  const applySuggestedOverview = () => {
    if (suggestion?.overview) setField('overview', suggestion.overview);
  };
  const addSuggestedBullet = (b) => {
    if (!bullets.includes(b)) setField('bullets', [...bullets, b]);
  };
  const toggleId = (field, id) => {
    const cur = section[field] || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setField(field, next);
  };

  const suggestedPolicies = suggestion?.policies || [];
  const suggestedDocs = suggestion?.documents || [];
  const suggestedControls = suggestion?.controls || [];
  const suggestedFaqs = suggestion?.faqs || [];

  const enabledCount = [section.overview, ...bullets].filter((x) => (x || '').trim()).length
    + (section.policy_ids?.length || 0) + (section.document_ids?.length || 0)
    + (section.control_ids?.length || 0) + (section.faq_ids?.length || 0);
  const isConfigured = enabledCount > 0;

  return (
    <div className="tc-surface" data-testid={`tc-security-section-${sectionKey}`}>
      <div className="p-4 border-b border-[var(--tc-line-soft)]">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}
            className="flex items-center gap-2 flex-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 rounded"
            data-testid={`tc-security-toggle-${sectionKey}`}>
            <ChevronDown className={`w-4 h-4 text-[#94A3B8] transition-transform ${expanded ? 'rotate-180' : '-rotate-90'}`} />
            <div>
              <div className="tc-h3 flex items-center gap-2">
                {meta.title}
                {isConfigured
                  ? <Badge className="border text-[10px] bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]" data-testid={`tc-security-badge-${sectionKey}`}>Configured</Badge>
                  : <Badge className="border text-[10px] bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]" data-testid={`tc-security-badge-${sectionKey}`}>Empty · hidden</Badge>
                }
              </div>
              <div className="tc-muted text-xs mt-0.5">{meta.hint}</div>
            </div>
          </button>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <PublishPill published={section.published} onToggle={(v) => setField('published', v)} testid={`tc-security-publish-${sectionKey}`} />
            {(() => {
              const canFeature = isConfigured && section.published !== false;
              return (
                <button type="button" onClick={() => { if (canFeature) setField('featured_on_overview', !section.featured_on_overview); }}
                  data-testid={`tc-security-feature-${sectionKey}`}
                  aria-label="Feature on Overview"
                  disabled={!canFeature}
                  title={!canFeature
                    ? 'Add content and make this section Public before featuring it on the Overview'
                    : (section.featured_on_overview ? 'Featured on Overview — click to remove' : 'Not featured — click to feature on Overview')}
                  className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition-colors ${!canFeature
                    ? 'bg-[#F8FAFC] text-[#CBD5E1] border-[#EEF2F6] cursor-not-allowed'
                    : section.featured_on_overview
                      ? 'bg-[#EEF2FF] text-[#4338CA] border-[#C7D2FE] hover:bg-[#E0E7FF]'
                      : 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0] hover:bg-[#EEF2FF] hover:text-[#4338CA]'}`}>
                  {section.featured_on_overview && canFeature ? '★ Overview' : '☆ Overview'}
                </button>
              );
            })()}
            <label className="text-xs text-[#64748B]">Enabled</label>
            <Switch checked={section.enabled !== false} onCheckedChange={(v) => setField('enabled', v)} data-testid={`tc-security-enabled-${sectionKey}`} />
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[13px] font-medium text-[var(--tc-ink)]">Overview</label>
              {suggestion?.overview && !section.overview && (
                <button type="button" onClick={applySuggestedOverview}
                  className="text-xs font-semibold text-[#0F62FE] hover:underline"
                  data-testid={`tc-security-apply-overview-${sectionKey}`}>
                  Apply suggested overview
                </button>
              )}
            </div>
            <Textarea value={section.overview || ''} onChange={(e) => setField('overview', e.target.value)}
              rows={3} maxLength={1200}
              placeholder={suggestion?.overview || 'Describe how you handle this area…'}
              className="rounded-[var(--tc-radius)] text-sm"
              data-testid={`tc-security-overview-${sectionKey}`} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[13px] font-medium text-[var(--tc-ink)]">Key highlights (bullets)</label>
              <button type="button" onClick={addBullet} className="text-xs font-semibold text-[#0F62FE] inline-flex items-center gap-1 hover:underline" data-testid={`tc-security-add-bullet-${sectionKey}`}>
                <Plus className="w-3 h-3" /> Add bullet
              </button>
            </div>
            <div className="space-y-2">
              {bullets.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={b} onChange={(e) => updateBullet(i, e.target.value)} maxLength={180}
                    className="rounded-[10px] h-9 flex-1" placeholder="e.g. MFA enforced for all employees"
                    data-testid={`tc-security-bullet-${sectionKey}-${i}`} />
                  <button type="button" onClick={() => removeBullet(i)} className="text-[#94A3B8] hover:text-[#DC2626] p-1"
                    data-testid={`tc-security-bullet-del-${sectionKey}-${i}`} aria-label="Remove bullet">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {bullets.length === 0 && <p className="text-xs text-[#94A3B8]">No highlights yet.</p>}
            </div>
            {suggestion?.bullets?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`tc-security-suggested-bullets-${sectionKey}`}>
                {suggestion.bullets.map((sb) => (
                  <button key={sb} type="button" onClick={() => addSuggestedBullet(sb)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-dashed border-[#CBD5E1] text-xs text-[#475569] hover:border-[#0F62FE] hover:text-[#0F62FE] transition-colors"
                    data-testid={`tc-security-suggest-bullet-${sectionKey}-${sb.replace(/\s+/g, '-').toLowerCase().slice(0, 40)}`}>
                    <Plus className="w-3 h-3" /> {sb}
                  </button>
                ))}
              </div>
            )}
          </div>

          <SecurityCrossLinkPicker label="Related Policies" hint="Workspace policies attached to this section (published only appear publicly)."
            candidates={suggestedPolicies} selectedIds={section.policy_ids || []}
            onToggle={(id) => toggleId('policy_ids', id)}
            testidPrefix={`tc-security-policies-${sectionKey}`} />
          <SecurityCrossLinkPicker label="Related Documents" hint="Trust Center resources (non-policy) attached to this section."
            candidates={suggestedDocs.map((d) => ({ id: d.id, title: d.title, description: `${d.category || ''}${d.access ? ` · ${d.access}` : ''}` }))}
            selectedIds={section.document_ids || []}
            onToggle={(id) => toggleId('document_ids', id)}
            testidPrefix={`tc-security-docs-${sectionKey}`} />
          <SecurityCrossLinkPicker label="Related Controls" hint="Public controls attached to this section (visible only if public Controls is enabled)."
            candidates={suggestedControls.map((c) => ({ id: c.id, title: c.title, description: [c.category, c.framework].filter(Boolean).join(' · ') }))}
            selectedIds={section.control_ids || []}
            onToggle={(id) => toggleId('control_ids', id)}
            testidPrefix={`tc-security-controls-${sectionKey}`} />
          <SecurityCrossLinkPicker label="Related FAQs" hint="Existing FAQ items to highlight in this section."
            candidates={suggestedFaqs.map((f) => ({ id: f.id, title: f.question }))}
            selectedIds={section.faq_ids || []}
            onToggle={(id) => toggleId('faq_ids', id)}
            testidPrefix={`tc-security-faqs-${sectionKey}`} />
        </div>
      )}
    </div>
  );
};

const SecurityProfileManager = ({ cfg, setCfg }) => {
  const [suggestions, setSuggestions] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/trust-center/security-suggestions');
      setSuggestions(data.sections || {});
    } catch (e) {
      console.warn('security suggestions load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const profile = cfg.security_profile || {};
  const setSection = (key, next) => {
    setCfg((p) => ({
      ...p,
      security_profile: { ...(p.security_profile || {}), [key]: next },
    }));
  };
  // Bulk publish / make-private across every security section that has content.
  const bulkPublish = (makePublic) => {
    setCfg((p) => {
      const prof = { ...(p.security_profile || {}) };
      SECURITY_SECTIONS_META.forEach(({ key }) => {
        const s = prof[key] || emptySecuritySection();
        prof[key] = { ...s, published: makePublic };
      });
      return { ...p, security_profile: prof };
    });
  };

  const configuredCount = SECURITY_SECTIONS_META.filter(({ key }) => {
    const s = profile[key] || {};
    if (s.enabled === false) return false;
    return (s.overview || '').trim() || (s.bullets || []).length || (s.policy_ids || []).length
      || (s.document_ids || []).length || (s.control_ids || []).length || (s.faq_ids || []).length;
  }).length;

  return (
    <div className="space-y-4" data-testid="tc-security-tab">
      <div className="tc-surface p-5 flex items-start justify-between gap-4 flex-wrap" data-testid="tc-security-header">
        <div className="min-w-0">
          <div className="tc-h2">Security posture</div>
          <div className="tc-muted mt-0.5">
            Fill in each of the 10 security sections shown publicly on your Trust Center. Empty sections auto-hide.
            Suggestions come from your policies, controls, integrations, and FAQs — nothing is auto-published, you review each one.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Badge className={`border text-[11px] shrink-0 ${configuredCount > 0 ? 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' : 'bg-[#F8FAFC] text-[#475569] border-[#E2E8F0]'}`}
            data-testid="tc-security-configured-count">{configuredCount}/10 configured</Badge>
          <button type="button" onClick={() => bulkPublish(true)}
            className="text-xs font-semibold px-2 py-1 rounded-md border border-[#A7F3D0] text-[#047857] hover:bg-[#ECFDF5]"
            data-testid="tc-security-bulk-publish">Make all Public</button>
          <button type="button" onClick={() => bulkPublish(false)}
            className="text-xs font-semibold px-2 py-1 rounded-md border border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9]"
            data-testid="tc-security-bulk-private">Make all Private</button>
          <button type="button" onClick={load} disabled={loading}
            className="text-xs font-semibold text-[#0F62FE] hover:underline disabled:opacity-50"
            data-testid="tc-security-refresh-suggestions">
            {loading ? 'Loading…' : 'Refresh suggestions'}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {SECURITY_SECTIONS_META.map((meta) => {
          const section = profile[meta.key] || emptySecuritySection();
          return (
            <SecuritySectionEditor
              key={meta.key}
              sectionKey={meta.key}
              meta={meta}
              section={section}
              suggestion={suggestions[meta.key]}
              setSection={(next) => setSection(meta.key, next)}
            />
          );
        })}
      </div>
    </div>
  );
};



// Overview manual control for Compliance frameworks. Frameworks are seeded
// from posture (no standalone editor), so their Private/Public + "feature on
// Overview" controls live here. Nothing appears on the public Overview unless
// an admin explicitly features it; the full list still renders on Compliance.
const OverviewFrameworksManager = ({ cfg, setCfg }) => {
  const frameworks = (cfg.compliance_frameworks || []).filter((f) => (f.name || '').trim());
  const setFw = (id, patch) => setCfg((p) => ({
    ...p,
    compliance_frameworks: (p.compliance_frameworks || []).map((f) => (f.id === id ? { ...f, ...patch } : f)),
  }));
  const bulkPublish = (makePublic) => setCfg((p) => ({
    ...p,
    compliance_frameworks: (p.compliance_frameworks || []).map((f) => ({ ...f, published: makePublic })),
  }));
  return (
    <Card className="p-5 space-y-3" data-testid="tc-overview-frameworks-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-[15px] text-[#0A0A0A]">Compliance frameworks</h3>
          <p className="text-xs text-[#64748B] mt-0.5">
            Control which frameworks are Public and which are featured on the Overview. Featured frameworks appear on the public Overview; all Public frameworks still show on the Compliance page.
          </p>
        </div>
        {frameworks.length > 0 && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => bulkPublish(true)} className="text-xs font-semibold px-2 py-1 rounded-md border border-[#A7F3D0] text-[#047857] hover:bg-[#ECFDF5]" data-testid="tc-fw-bulk-publish">Make all Public</button>
            <button type="button" onClick={() => bulkPublish(false)} className="text-xs font-semibold px-2 py-1 rounded-md border border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9]" data-testid="tc-fw-bulk-private">Make all Private</button>
          </div>
        )}
      </div>
      {frameworks.length === 0 && (
        <div className="text-xs text-[#94A3B8] py-1" data-testid="tc-fw-empty">
          No frameworks yet. Frameworks are seeded from your audits — add an audit framework to see it here.
        </div>
      )}
      <div className="space-y-1">
        {frameworks.map((f) => (
          <div key={f.id} className="flex items-center gap-2 border border-[#E2E8F0] rounded-[10px] px-2.5 py-1.5" data-testid={`tc-fw-row-${f.id}`}>
            <BadgeCheck className="w-4 h-4 text-[#0F62FE] shrink-0" />
            <span className="flex-1 truncate text-sm text-[#0A0A0A]">{f.name}</span>
            <PublishPill published={f.published} onToggle={(v) => setFw(f.id, { published: v })} testid={`tc-fw-publish-${f.id}`} />
            <button type="button" onClick={() => setFw(f.id, { featured_on_overview: !f.featured_on_overview })}
              data-testid={`tc-fw-feature-${f.id}`}
              aria-label="Feature on Overview"
              title={f.featured_on_overview ? 'Featured on Overview — click to remove' : 'Not featured — click to feature on Overview'}
              className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition-colors ${f.featured_on_overview
                ? 'bg-[#EEF2FF] text-[#4338CA] border-[#C7D2FE] hover:bg-[#E0E7FF]'
                : 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0] hover:bg-[#EEF2FF] hover:text-[#4338CA]'}`}>
              {f.featured_on_overview ? '★ Overview' : '☆ Overview'}
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
};


const OverviewFeaturedManager = ({ cfg, updateItem, setTab, onServerRefresh }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmAll, setConfirmAll] = useState(null); // { category, count }

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const { data: body } = await axios.get('/api/trust-center/overview-suggestions');
      setData(body.categories || {});
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('overview suggestions load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const findIdx = (id) => (cfg?.documents || []).findIndex((x) => x.id === id);

  const acceptSuggestion = async (rid) => {
    const idx = findIdx(rid);
    if (idx < 0) return;
    // Local optimistic toggle so Live Preview updates immediately.
    const current = (cfg.documents || []);
    const catFeatured = current.filter((d) => (d.category || '') === (current[idx].category || '') && d.featured_on_overview);
    const nextOrder = Math.max(0, ...catFeatured.map((d) => parseInt(d.featured_order || 0, 10))) + 1;
    updateItem('documents', idx, { featured_on_overview: true, featured_order: nextOrder });
    // Also refresh the server-driven suggestion list so the accepted item
    // moves out of Suggested and the next best candidate slides in.
    setTimeout(fetchSuggestions, 400);
  };

  const unfeature = (rid) => {
    const idx = findIdx(rid);
    if (idx < 0) return;
    updateItem('documents', idx, { featured_on_overview: false });
    setTimeout(fetchSuggestions, 400);
  };

  const dismissSuggestion = async (rid, cat) => {
    try {
      const { data: body } = await axios.post('/api/trust-center/overview-suggestions/dismiss',
                                              { resource_id: rid, category: cat });
      setData(body.categories || {});
    } catch (e) {
      toast?.error?.('Could not dismiss suggestion');
    }
  };

  const resetDismissed = async (cat) => {
    try {
      const { data: body } = await axios.post('/api/trust-center/overview-suggestions/reset',
                                              { category: cat });
      setData(body.categories || {});
    } catch (e) {
      toast?.error?.('Could not reset dismissed suggestions');
    }
  };

  const featureAll = async (cat) => {
    try {
      await axios.post('/api/trust-center/overview-suggestions/feature-all', { category: cat });
      setConfirmAll(null);
      await fetchSuggestions();
      if (onServerRefresh) await onServerRefresh();
    } catch (e) {
      toast?.error?.('Could not feature suggestions');
    }
  };

  return (
    <Card className="p-5 space-y-3" data-testid="tc-overview-featured-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[15px] text-[#0A0A0A]">Featured on Overview</h3>
          <p className="text-xs text-[#64748B] mt-0.5">
            Explicit featured resources take precedence. Suggestions are advisory — accept them individually or use "Feature all" to fill the remaining slots up to four.
          </p>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" />}
      </div>

      {OVERVIEW_SUGGESTION_CATEGORIES.map((cat) => {
        const bundle = data[cat] || { featured: [], suggested: [], dismissed_count: 0, available_slots: 4, eligible_total: 0 };
        const cid = cat.toLowerCase().replace(/\s+/g, '-');
        const totalPool = (cfg.documents || []).filter((d) => (d.category || '') === cat && !d.policy_id && (d.status || 'published') === 'published' && !d.archived_at).length;
        return (
          <div key={cat} className="border border-[#E2E8F0] rounded-[10px] p-3" data-testid={`tc-featured-cat-${cid}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-[#0A0A0A]">{cat}</div>
              <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]" data-testid={`tc-featured-count-${cid}`}>
                {bundle.featured.length} featured
                {bundle.available_slots > 0 && bundle.suggested.length > 0 && ` · ${bundle.available_slots} slot${bundle.available_slots === 1 ? '' : 's'} open`}
                {' · '}{totalPool} eligible
              </span>
            </div>

            {/* Featured group (authoritative) */}
            {bundle.featured.length > 0 && (
              <div className="space-y-1 mb-2" data-testid={`tc-featured-group-${cid}`}>
                <div className="text-[10px] uppercase tracking-wider text-[#64748B]">Featured</div>
                {bundle.featured.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-sm text-[#0A0A0A] hover:bg-[#F8FAFC] rounded px-1.5 py-1" data-testid={`tc-featured-item-${d.id}`}>
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#059669] text-white text-[9px] font-bold shrink-0">★</span>
                    <span className="flex-1 truncate">{d.title || 'Untitled resource'}</span>
                    <button type="button" onClick={() => unfeature(d.id)} className="text-[11px] text-[#64748B] hover:text-[#0F62FE] px-1.5 py-0.5 rounded" data-testid={`tc-unfeature-${d.id}`}>Unfeature</button>
                  </div>
                ))}
              </div>
            )}

            {/* Suggested group (advisory) */}
            {bundle.suggested.length > 0 && (
              <div className="space-y-1 mb-2" data-testid={`tc-suggested-group-${cid}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-[#64748B]">Suggested</div>
                  {bundle.available_slots > 0 && bundle.suggested.length >= 2 && (
                    <button type="button" onClick={() => setConfirmAll({ category: cat, count: Math.min(bundle.available_slots, bundle.suggested.length) })} className="text-[11px] font-semibold text-[#0F62FE] hover:underline" data-testid={`tc-feature-all-${cid}`}>
                      Feature all ({Math.min(bundle.available_slots, bundle.suggested.length)})
                    </button>
                  )}
                </div>
                {bundle.suggested.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-sm text-[#0A0A0A] hover:bg-[#F8FAFC] rounded px-1.5 py-1" data-testid={`tc-suggested-item-${d.id}`}>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#EEF2FF] text-[#4338CA] text-[9px] font-semibold uppercase tracking-wider shrink-0" data-testid={`tc-suggested-badge-${d.id}`}>Suggested</span>
                    <span className="flex-1 truncate" title={d.description || ''}>{d.title || 'Untitled resource'}</span>
                    <button type="button" onClick={() => acceptSuggestion(d.id)} className="text-[11px] font-semibold text-[#0F62FE] hover:underline px-1.5 py-0.5" data-testid={`tc-accept-${d.id}`}>Feature</button>
                    <button type="button" onClick={() => dismissSuggestion(d.id, cat)} className="text-[11px] text-[#94A3B8] hover:text-[#DC2626] px-1.5 py-0.5" data-testid={`tc-dismiss-${d.id}`}>Dismiss</button>
                    <button type="button" onClick={() => setTab('documents')} className="text-[11px] text-[#94A3B8] hover:text-[#0F62FE] px-1.5 py-0.5" data-testid={`tc-view-in-resources-${d.id}`}>View</button>
                  </div>
                ))}
              </div>
            )}

            {/* Empty & advisory states */}
            {bundle.featured.length === 0 && bundle.suggested.length === 0 && totalPool === 0 && (
              <div className="text-xs text-[#94A3B8] py-1" data-testid={`tc-empty-${cid}`}>
                No {cat} resources yet. Add some in the <button type="button" onClick={() => setTab('documents')} className="font-semibold text-[#0F62FE] hover:underline">Resources</button> tab.
              </div>
            )}
            {bundle.featured.length === 0 && bundle.suggested.length === 0 && totalPool > 0 && bundle.dismissed_count === 0 && (
              <div className="text-xs text-[#94A3B8] py-1">
                No eligible suggestions right now — resources may be draft, hidden, or archived.
              </div>
            )}
            {bundle.dismissed_count > 0 && (
              <div className="flex items-center justify-end mt-1">
                <button type="button" onClick={() => resetDismissed(cat)} className="text-[11px] text-[#94A3B8] hover:text-[#0F62FE]" data-testid={`tc-reset-dismissed-${cid}`}>
                  Reset dismissed ({bundle.dismissed_count})
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Feature-all confirmation */}
      {confirmAll && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" data-testid="tc-feature-all-confirm">
          <div className="bg-white rounded-[12px] p-5 max-w-md w-full shadow-xl">
            <h4 className="font-semibold text-[15px] text-[#0A0A0A] mb-1.5">Feature suggested resources?</h4>
            <p className="text-sm text-[#475569] mb-4">
              This will feature {confirmAll.count} suggested {confirmAll.category} resource{confirmAll.count === 1 ? '' : 's'} on your public Overview. Existing featured resources will not be reordered.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setConfirmAll(null)} className="text-sm px-3 py-1.5 rounded-lg text-[#475569] hover:bg-[#F1F5F9]" data-testid="tc-feature-all-cancel">Cancel</button>
              <button type="button" onClick={() => featureAll(confirmAll.category)} className="text-sm px-3 py-1.5 rounded-lg bg-[#0F62FE] hover:bg-[#0A4FCC] text-white font-semibold" data-testid="tc-feature-all-confirm-btn">Feature</button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

const HealthPanel = ({ cfg, controlsCount, activePolicies, onJump }) => {
  const [showAll, setShowAll] = useState(false);
  const health = useMemo(() => computeHealth(cfg, controlsCount, activePolicies), [cfg, controlsCount, activePolicies]);
  const {
    score, rating, passed, total,
    requiredMissing, recommendedMissing, readyToPublish,
  } = health;
  // Colour ramp: green when publish-ready, blue for good progress,
  // amber when required items still block a confident publish.
  const barColor = readyToPublish
    ? (score >= 90 ? '#059669' : '#0F62FE')
    : '#B45309';
  const remaining = total - passed;
  // Priority list = required items first, then recommended, capped at 4.
  const topActions = [...requiredMissing, ...recommendedMissing].slice(0, 4);
  return (
    <Card className="p-5 mb-6" data-testid="tc-health-panel">
      {/* -------- Summary row -------- */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-[15px] text-[#0A0A0A] inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#0F62FE]" /> Trust Center Readiness
            </h3>
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ color: barColor, backgroundColor: `${barColor}14` }}
              data-testid="tc-health-rating"
            >
              {rating}
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-2 flex-wrap" data-testid="tc-health-summary">
            <span className="text-2xl font-bold" style={{ color: barColor }} data-testid="tc-health-score">
              {score}%
            </span>
            <span className="text-sm font-medium text-[#334155]">
              {readyToPublish ? 'Ready to publish' : 'Ready to publish once required items are complete'}
            </span>
          </div>
          <p className="text-xs text-[#64748B] mt-1">
            <span data-testid="tc-health-passed">{passed}</span> of {total} complete
            {remaining > 0 && (
              <> · <span data-testid="tc-health-remaining">{remaining}</span> recommendation{remaining === 1 ? '' : 's'} remaining</>
            )}
            {' '}· You can publish anytime.
          </p>
        </div>
        {remaining > 0 && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#D7E0F2] hover:bg-[#F1F5F9] text-[#0A0A0A] shrink-0"
            data-testid="tc-health-toggle"
          >
            {showAll ? 'Hide checklist' : 'Review remaining items'}
          </button>
        )}
      </div>

      {/* -------- Progress bar -------- */}
      <div className="mt-4">
        <div className="h-2 w-full rounded-full bg-[#E2E8F0] overflow-hidden" data-testid="tc-health-progress">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${score}%`, backgroundColor: barColor }}
          />
        </div>
      </div>

      {/* -------- Top priority actions (shown when checklist is collapsed) -------- */}
      {!showAll && topActions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#F1F5F9]" data-testid="tc-health-top-actions">
          <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-2">
            What to do next
          </p>
          <ul className="space-y-1.5">
            {topActions.map((w) => (
              <li key={w.key}>
                <button
                  type="button"
                  onClick={() => onJump(w.target_tab)}
                  title={w.hint}
                  data-testid={`tc-health-top-${w.key}`}
                  className="w-full flex items-center gap-2.5 text-sm text-left px-2 py-1.5 rounded-lg hover:bg-[#F1F5F9] transition-colors"
                >
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0 ${w.required ? 'bg-[#FEE2E2] text-[#B45309]' : 'bg-[#E0EDFF] text-[#0F62FE]'}`}>
                    {w.required ? <AlertTriangle className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                  </span>
                  <span className="font-medium text-[#0A0A0A]">{w.label}</span>
                  {w.required && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[#B45309] bg-[#FEF3C7] px-1.5 py-0.5 rounded">
                      Required
                    </span>
                  )}
                  <span className="ml-auto text-xs font-medium text-[#0F62FE] shrink-0">
                    Go to {w.section} →
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* -------- Full breakdown (required + recommended) -------- */}
      {showAll && (
        <div className="mt-4 pt-4 border-t border-[#F1F5F9] space-y-5" data-testid="tc-health-checklist">
          {/* Required prerequisites */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">
                Required before publishing
              </p>
              <span className="text-[11px] text-[#64748B]">
                {health.checklist.filter((i) => i.required && i.status === 'pass').length} / {health.checklist.filter((i) => i.required).length} complete
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
              {health.checklist.filter((i) => i.required).map((item) => (
                <ChecklistRow key={item.key} item={item} onJump={onJump} testid="tc-health-item-required" />
              ))}
            </div>
          </div>

          {/* Recommended improvements */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">
                Recommended improvements
              </p>
              <span className="text-[11px] text-[#64748B]">
                {health.checklist.filter((i) => !i.required && i.status === 'pass').length} / {health.checklist.filter((i) => !i.required).length} complete
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
              {health.checklist.filter((i) => !i.required).map((item) => (
                <ChecklistRow key={item.key} item={item} onJump={onJump} testid="tc-health-item" />
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

/** Single row inside the expanded readiness checklist. Passing rows are
 * static (no CTA); failing rows are clickable buttons that jump the
 * admin to the relevant Trust Center section so they can complete the
 * item without hunting for it. */
const ChecklistRow = ({ item, onJump, testid }) => {
  const isWarn = item.status === 'warn';
  const Content = (
    <>
      {item.status === 'pass'
        ? <CheckCircle2 className="w-4 h-4 text-[#059669] shrink-0" />
        : <AlertTriangle className="w-4 h-4 text-[#B45309] shrink-0" />}
      <span className={item.status === 'pass' ? 'text-[#334155]' : 'text-[#92400E] font-medium'}>{item.label}</span>
      {isWarn && (
        <span className="ml-auto text-[11px] font-semibold text-[#0F62FE] opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          Fix →
        </span>
      )}
    </>
  );
  if (isWarn) {
    return (
      <button
        type="button"
        onClick={() => onJump(item.target_tab)}
        title={item.hint}
        data-testid={`${testid}-${item.key}`}
        className="group flex items-center gap-2 text-sm text-left px-1.5 py-1 rounded-md hover:bg-[#F1F5F9] transition-colors cursor-pointer"
      >
        {Content}
      </button>
    );
  }
  return (
    <div
      data-testid={`${testid}-${item.key}`}
      className="flex items-center gap-2 text-sm px-1.5 py-1"
    >
      {Content}
    </div>
  );
};


// ---- live preview: renders the SAME component tree as the public page ----
// Builds the sanitized public shape from the draft (cfg+posture) and renders
// TrustCenterView inside a device <iframe> (its own CSS viewport) so Desktop /
// Tablet / Mobile show the TRUE responsive layout buyers will see. The iframe
// gets its own React root so in-preview navigation + buttons stay interactive.
const DEVICES = {
  desktop: { label: 'Desktop', w: 1280, h: 820, icon: Monitor },
  tablet: { label: 'Tablet', w: 834, h: 1024, icon: Tablet },
  mobile: { label: 'Mobile', w: 390, h: 760, icon: Smartphone },
};
const PREVIEW_DEVICE_KEY = 'tc_preview_device';

const DeviceFrame = ({ width, height, scale, render }) => {
  const iframeRef = useRef(null);
  const rootRef = useRef(null);
  const [body, setBody] = useState(null);

  const mount = useCallback(() => {
    const idoc = iframeRef.current?.contentDocument;
    if (!idoc || !idoc.body) return;
    idoc.head.innerHTML = document.head.innerHTML; // copy compiled CSS (scripts in innerHTML don't execute)
    idoc.body.style.margin = '0';
    idoc.body.style.background = '#FAFAFA';
    setBody(idoc.body);
  }, []);

  // Fallback in case about:blank fired its load before React attached onLoad.
  useEffect(() => {
    const f = iframeRef.current;
    if (f?.contentDocument?.readyState === 'complete' && !body) mount();
  }, [body, mount]);

  useEffect(() => {
    if (!body) return;
    if (!rootRef.current) rootRef.current = createRoot(body);
    rootRef.current.render(render());
  }, [body, render]);

  useEffect(() => () => {
    const r = rootRef.current; rootRef.current = null;
    if (r) setTimeout(() => r.unmount(), 0);
  }, []);

  return (
    <iframe ref={iframeRef} onLoad={mount} title="Trust Center device preview" data-testid="tc-preview-iframe"
      style={{ width, height, border: 0, transform: `scale(${scale})`, transformOrigin: 'top left', backgroundColor: '#FAFAFA' }} />
  );
};

// PreviewShellBody — the full Preview shell body (toolbar + canvas). Rendered
// inside a full-screen Radix DialogContent so it inherits the primitive's
// focus trap, Escape handling, body scroll lock, and focus restoration.
// The device toolbar lives here so the toolbar row has ONE Close (X) icon
// and no duplication with the rendered TrustCenterView below.
const PreviewShellBody = ({ cfg, posture, controls, isPublished, publicPath, publicUrl, onCopyLink, onClose }) => {
  const [device, setDevice] = useState(() => {
    try { const d = localStorage.getItem(PREVIEW_DEVICE_KEY); return DEVICES[d] ? d : 'desktop'; } catch (e) { return 'desktop'; }
  });
  const [activeKey, setActiveKey] = useState('overview');
  const canvasRef = useRef(null);
  const [pane, setPane] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = canvasRef.current; if (!el) return;
    const update = () => setPane({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update); ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const data = useMemo(() => buildPublicData(cfg, posture, controls), [cfg, posture, controls]);
  const renderView = useCallback(
    () => (data ? <TrustCenterView data={data} mode="preview" activeKey={activeKey} onNavigate={setActiveKey} /> : null),
    [data, activeKey],
  );

  const setDev = (d) => { setDevice(d); try { localStorage.setItem(PREVIEW_DEVICE_KEY, d); } catch (e) { /* */ } };

  const openLive = () => {
    if (!isPublished || !publicPath) return;
    window.open(publicPath, '_blank', 'noopener,noreferrer');
  };

  if (!data) return null;

  const dev = DEVICES[device];
  // Fit both dimensions so tablet portrait (1024h) never clips off the
  // bottom of a laptop viewport. Never upscale — Desktop displays at 100%
  // whenever the canvas has at least 1280×820 available (~1344px viewport).
  const availW = Math.max(0, pane.w - 24); // 12px padding on each side
  const availH = Math.max(0, pane.h - 24);
  const scale = pane.w && pane.h ? Math.min(1, availW / dev.w, availH / dev.h) : 1;
  const frameW = Math.round(dev.w * scale);
  const frameH = Math.round(dev.h * scale);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Toolbar — sticky top, 56px, single Close on the right */}
      <div
        className="flex items-center gap-3 px-4 sm:px-6 h-14 border-b border-[#E2E8F0] bg-white shrink-0"
        data-testid="tc-preview-toolbar"
        role="toolbar"
        aria-label="Trust Center preview controls"
      >
        {/* Left — identity + draft status */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Eye className="w-4 h-4 text-[#0F62FE] shrink-0" strokeWidth={2} />
          <span className="text-[13px] sm:text-[14px] font-semibold text-[#0A0A0A] truncate">
            Trust Center Preview
          </span>
          {isPublished ? (
            <span
              className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#047857] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full px-2 py-0.5 ml-1"
              data-testid="tc-preview-status-chip"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Published
            </span>
          ) : (
            <span
              className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#64748B] bg-[#F1F5F9] border border-[#E2E8F0] rounded-full px-2 py-0.5 ml-1"
              data-testid="tc-preview-status-chip"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#94A3B8]" /> Draft
            </span>
          )}
        </div>

        {/* Center — device toggles */}
        <div
          className="inline-flex items-center rounded-lg border border-[#D7E0F2] bg-white p-0.5 shrink-0"
          role="group"
          aria-label="Preview device size"
        >
          {Object.entries(DEVICES).map(([k, d]) => {
            const Ic = d.icon;
            const active = device === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setDev(k)}
                data-testid={`tc-preview-device-${k}`}
                aria-pressed={active}
                aria-label={`Preview at ${d.label} width`}
                title={`${d.label} · ${d.w}×${d.h}`}
                className={`px-2.5 sm:px-3 h-9 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40 ${
                  active
                    ? 'bg-[#0F62FE] text-white shadow-sm'
                    : 'text-[#475569] hover:text-[#0A0A0A] hover:bg-[#F1F5F9]'
                }`}
              >
                <Ic className="w-3.5 h-3.5" />
                <span className="hidden md:inline">{d.label}</span>
              </button>
            );
          })}
        </div>

        {/* Right — safe actions + single Close */}
        <div className="flex items-center gap-1.5 flex-1 justify-end">
          {publicUrl && (
            <button
              type="button"
              onClick={onCopyLink}
              data-testid="tc-preview-copy-url"
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold text-[#475569] hover:text-[#0F62FE] hover:bg-[#F0F6FF] border border-transparent hover:border-[#D7E0F2] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40"
              title={`Copy public URL: ${publicUrl}`}
            >
              <Copy className="w-3.5 h-3.5" /> Copy URL
            </button>
          )}
          {isPublished && publicPath && (
            <button
              type="button"
              onClick={openLive}
              data-testid="tc-preview-open-live"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold text-white bg-[#0F62FE] hover:bg-[#0A4FCC] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40"
              title="Open the live public Trust Center in a new tab"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open live page
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="tc-preview-close"
            aria-label="Close preview and return to editor"
            title="Close preview and return to editor"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-[#475569] hover:text-[#0A0A0A] hover:bg-[#F1F5F9] border border-transparent hover:border-[#E2E8F0] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40"
          >
            <X className="w-4 h-4" strokeWidth={2.25} />
          </button>
        </div>
      </div>

      {/* Canvas — neutral backdrop, centered device frame. This is the only
          vertical scroll region (the iframe body scrolls the rendered page).
          On Desktop the iframe renders at 100% whenever ≥ ~1344px viewport
          width is available; below that we downscale to fit without ever
          upscaling. Tablet and Mobile always render at their intrinsic
          responsive widths on a wide canvas. */}
      <div
        ref={canvasRef}
        className="flex-1 min-h-0 overflow-auto flex items-center justify-center p-3 sm:p-6"
        data-testid="tc-preview-canvas"
      >
        {frameW > 0 && frameH > 0 && (
          <div
            style={{ width: frameW, height: frameH }}
            className="shrink-0 rounded-xl overflow-hidden border border-[#E2E8F0] bg-white shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18),0_4px_8px_-4px_rgba(15,23,42,0.10)]"
            data-testid="tc-preview-device-frame"
          >
            <DeviceFrame width={dev.w} height={dev.h} scale={scale} render={renderView} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TrustCenterAdmin;

/* ============================ Analytics Tab (Phase C) ============================ */
const RANGE_PRESETS = [
  { k: '7', label: 'Last 7 days' },
  { k: '30', label: 'Last 30 days' },
  { k: '90', label: 'Last 90 days' },
];
const GRANULARITY = [
  { k: 'daily', label: 'Daily' },
  { k: 'weekly', label: 'Weekly' },
  { k: 'monthly', label: 'Monthly' },
];

const isoDaysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString();

const fmtPct = (n) => `${Math.round((Number(n) || 0) * 1000) / 10}%`;
const fmtNum = (n) => (Number(n) || 0).toLocaleString();
const fmtEventType = (t) => ({
  page_view: 'Page view', resource_view: 'Resource view', framework_view: 'Framework view',
  control_view: 'Control view', search: 'Search', documentation_request: 'Documentation request',
})[t] || t;

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};

const KpiCard = ({ label, value, hint, testid }) => (
  <div className="rounded-xl border border-[#D7E0F2] bg-white p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold">{label}</div>
    <div className="text-2xl md:text-3xl font-semibold tracking-tight text-[#0A0A0A] mt-1">{value}</div>
    {hint && <div className="text-xs text-[#94A3B8] mt-1">{hint}</div>}
  </div>
);

// Lightweight SVG bar chart — no chart library dependency.
const SVGBars = ({ series, testid }) => {
  const rows = series || [];
  if (!rows.length) return <div className="text-sm text-[#94A3B8] py-6 text-center" data-testid={`${testid}-empty`}>No visitor activity in this range.</div>;
  const max = Math.max(1, ...rows.map((r) => r.visitors || 0));
  const barW = 100 / rows.length;
  return (
    <div className="w-full" data-testid={testid}>
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-40">
        {rows.map((r, i) => {
          const h = ((r.visitors || 0) / max) * 34;
          return (
            <g key={r.bucket || i}>
              <rect x={i * barW + barW * 0.15} y={38 - h} width={barW * 0.7} height={h} fill="#0F62FE" opacity={0.85} rx={0.4}>
                <title>{r.bucket}: {r.visitors} visitors · {r.sessions} sessions · {r.views} views</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-[#94A3B8] mt-1">
        <span>{rows[0]?.bucket}</span>
        <span>{rows[rows.length - 1]?.bucket}</span>
      </div>
    </div>
  );
};

const TopList = ({ title, rows, testid, formatKey }) => (
  <div className="rounded-xl border border-[#D7E0F2] bg-white p-4" data-testid={testid}>
    <div className="text-sm font-semibold text-[#0A0A0A] mb-2">{title}</div>
    {(!rows || rows.length === 0) ? (
      <div className="text-xs text-[#94A3B8] py-4">No data yet.</div>
    ) : (
      <ul className="divide-y divide-[#F1F5F9]">
        {rows.slice(0, 8).map((r, i) => (
          <li key={r.key || i} className="flex items-center justify-between py-2 text-sm" data-testid={`${testid}-row-${i}`}>
            <span className="text-[#334155] truncate pr-2">{formatKey ? formatKey(r) : (r.label || r.key || '—')}</span>
            <span className="text-xs text-[#64748B] whitespace-nowrap">{fmtNum(r.views)} views · {fmtNum(r.visitors)} visitors</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Funnel = ({ stages }) => (
  <div className="rounded-xl border border-[#D7E0F2] bg-white p-4" data-testid="tc-analytics-funnel">
    <div className="text-sm font-semibold text-[#0A0A0A] mb-3 inline-flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#0F62FE]" /> Trust Center Funnel</div>
    <div className="space-y-2">
      {(stages || []).map((s) => {
        const pct = Math.max(3, Math.round((Number(s.rate) || 0) * 100));
        return (
          <div key={s.key} data-testid={`tc-funnel-${s.key}`}>
            <div className="flex items-center justify-between text-xs text-[#475569] mb-1">
              <span className="font-medium text-[#0A0A0A]">{s.label}</span>
              <span>{fmtNum(s.count)} · {fmtPct(s.rate)}</span>
            </div>
            <div className="h-2.5 rounded-full bg-[#F1F5F9] overflow-hidden">
              <div className="h-full rounded-full bg-[#0F62FE]" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const RecentActivity = ({ rows }) => (
  <div className="rounded-xl border border-[#D7E0F2] bg-white p-4" data-testid="tc-analytics-recent-activity">
    <div className="text-sm font-semibold text-[#0A0A0A] mb-2 inline-flex items-center gap-2"><Activity className="w-4 h-4 text-[#0F62FE]" /> Recent visitor activity</div>
    <div className="text-xs text-[#94A3B8] mb-3">Anonymous — no IPs, no personal data. Country from edge headers when available.</div>
    {(!rows || rows.length === 0) ? (
      <div className="text-sm text-[#94A3B8] py-6 text-center">No recent visitor activity.</div>
    ) : (
      <ul className="divide-y divide-[#F1F5F9] max-h-[420px] overflow-auto">
        {rows.map((r, i) => (
          <li key={i} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`tc-recent-row-${i}`}>
            <div className="min-w-0">
              <div className="text-[#0A0A0A] truncate">
                <span className="font-semibold">{r.visitor_short || 'Visitor'}</span>
                <span className="text-[#64748B]"> · {fmtEventType(r.event_type)}</span>
                {r.target_name && <span className="text-[#64748B]"> · {r.target_name}</span>}
                {!r.target_name && r.search_query && <span className="text-[#64748B]"> · &ldquo;{r.search_query}&rdquo;</span>}
              </div>
              <div className="text-xs text-[#94A3B8]">{fmtDateTime(r.occurred_at)} · {r.country || 'Unknown'}</div>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const AnalyticsTab = ({ documents }) => {
  const [rangeDays, setRangeDays] = useState('30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [gran, setGran] = useState('daily');
  const [resourceId, setResourceId] = useState('');
  const [framework, setFramework] = useState('');
  const [country, setCountry] = useState('');
  const [visitorType, setVisitorType] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [tops, setTops] = useState({});

  const range = useMemo(() => {
    if (rangeDays === 'custom' && customFrom && customTo) return { from: customFrom, to: customTo };
    const days = parseInt(rangeDays, 10) || 30;
    return { from: isoDaysAgo(days), to: new Date().toISOString() };
  }, [rangeDays, customFrom, customTo]);

  const params = useMemo(() => {
    const p = { from: range.from, to: range.to };
    if (resourceId) p.resource_id = resourceId;
    if (framework) p.framework = framework;
    if (country) p.country = country;
    if (visitorType) p.visitor_type = visitorType;
    return p;
  }, [range, resourceId, framework, country, visitorType]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, co, tPages, tRes, tFw, tCtl, tSearch] = await Promise.all([
        axios.get('/api/trust-center/analytics/summary', { params }),
        axios.get('/api/trust-center/analytics/companies', { params: { from: range.from, to: range.to } }),
        axios.get('/api/trust-center/analytics/top', { params: { ...params, kind: 'pages' } }),
        axios.get('/api/trust-center/analytics/top', { params: { ...params, kind: 'resources' } }),
        axios.get('/api/trust-center/analytics/top', { params: { ...params, kind: 'frameworks' } }),
        axios.get('/api/trust-center/analytics/top', { params: { ...params, kind: 'controls' } }),
        axios.get('/api/trust-center/analytics/top', { params: { ...params, kind: 'searches' } }),
      ]);
      setSummary(s.data || null);
      setCompanies((co.data || {}).rows || []);
      setTops({
        pages: (tPages.data || {}).rows || [],
        resources: (tRes.data || {}).rows || [],
        frameworks: (tFw.data || {}).rows || [],
        controls: (tCtl.data || {}).rows || [],
        searches: (tSearch.data || {}).rows || [],
      });
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [params, range.from, range.to]);

  useEffect(() => { reload(); }, [reload]);

  const availableCountries = useMemo(() => {
    const set = new Set();
    ((summary && summary.countries) || []).forEach((c) => c.code && set.add(c.code));
    return Array.from(set);
  }, [summary]);

  const exportCsv = async () => {
    try {
      const res = await axios.get('/api/trust-center/analytics/export', { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'trust-center-analytics.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const kpis = (summary && summary.kpis) || {};
  const funnelStages = (summary && summary.funnel && summary.funnel.stages) || [];
  const series = (summary && summary.series) || {};
  const recent = (summary && summary.recent_activity) || [];

  return (
    <div className="space-y-5" data-testid="tc-analytics-tab">
      <Card className="p-4 space-y-3" data-testid="tc-analytics-filters">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm font-semibold text-[#0A0A0A] inline-flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#0F62FE]" /> Visitor Analytics</div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={reload} data-testid="tc-analytics-refresh" className="border-[#D7E0F2] rounded-[10px]">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
            </Button>
            <Button size="sm" onClick={exportCsv} data-testid="tc-analytics-export" className="bg-[#0F62FE] hover:bg-[#0A4FCC] rounded-[10px] inline-flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <Select value={rangeDays} onValueChange={setRangeDays}>
            <SelectTrigger data-testid="tc-analytics-range" className="rounded-[10px] text-xs"><SelectValue placeholder="Range" /></SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map((p) => <SelectItem key={p.k} value={p.k}>{p.label}</SelectItem>)}
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          {rangeDays === 'custom' && (
            <>
              <Input type="date" value={customFrom.slice(0, 10)} onChange={(e) => setCustomFrom(e.target.value)} data-testid="tc-analytics-from" className="rounded-[10px] text-xs" />
              <Input type="date" value={customTo.slice(0, 10)} onChange={(e) => setCustomTo(e.target.value)} data-testid="tc-analytics-to" className="rounded-[10px] text-xs" />
            </>
          )}
          <Select value={gran} onValueChange={setGran}>
            <SelectTrigger data-testid="tc-analytics-granularity" className="rounded-[10px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{GRANULARITY.map((g) => <SelectItem key={g.k} value={g.k}>{g.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={resourceId || 'all'} onValueChange={(v) => setResourceId(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="tc-analytics-resource" className="rounded-[10px] text-xs"><SelectValue placeholder="Resource" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All resources</SelectItem>
              {(documents || []).slice(0, 100).map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={framework || 'all'} onValueChange={(v) => setFramework(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="tc-analytics-framework" className="rounded-[10px] text-xs"><SelectValue placeholder="Framework" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All frameworks</SelectItem>
              <SelectItem value="soc2">SOC 2</SelectItem>
              <SelectItem value="iso27001">ISO 27001</SelectItem>
              <SelectItem value="hipaa">HIPAA</SelectItem>
              <SelectItem value="gdpr">GDPR</SelectItem>
              <SelectItem value="pcidss">PCI DSS</SelectItem>
              <SelectItem value="ai">AI & Privacy</SelectItem>
            </SelectContent>
          </Select>
          <Select value={country || 'all'} onValueChange={(v) => setCountry(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="tc-analytics-country" className="rounded-[10px] text-xs"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All countries</SelectItem>
              {availableCountries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={visitorType || 'all'} onValueChange={(v) => setVisitorType(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="tc-analytics-visitor-type" className="rounded-[10px] text-xs"><SelectValue placeholder="Visitor type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All visitors</SelectItem>
              <SelectItem value="new">New visitors</SelectItem>
              <SelectItem value="returning">Returning visitors</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="tc-analytics-kpis">
        <KpiCard testid="tc-kpi-total-visitors" label="Total Visitors" value={fmtNum(kpis.total_visitors)} hint="Sessions in range" />
        <KpiCard testid="tc-kpi-unique-visitors" label="Unique Visitors" value={fmtNum(kpis.unique_visitors)} hint="Anonymous visitor IDs" />
        <KpiCard testid="tc-kpi-returning-visitors" label="Returning Visitors" value={fmtNum(kpis.returning_visitors)} />
        <KpiCard testid="tc-kpi-doc-requests" label="Documentation Requests" value={fmtNum(kpis.documentation_requests)} />
        <KpiCard testid="tc-kpi-conversion" label="Conversion Rate" value={fmtPct(kpis.conversion_rate)} hint="Requests ÷ unique visitors" />
      </div>

      <Card className="p-4" data-testid="tc-analytics-trend">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-[#0A0A0A]">Visitor trend</div>
          <div className="inline-flex rounded-lg border border-[#E2E8F0] bg-white p-0.5">
            {GRANULARITY.map((g) => (
              <button key={g.k} onClick={() => setGran(g.k)} data-testid={`tc-analytics-gran-${g.k}`}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold ${gran === g.k ? 'bg-[#0F62FE] text-white' : 'text-[#475569] hover:text-[#0A0A0A]'}`}>
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <SVGBars series={series[gran] || []} testid="tc-analytics-bars" />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Funnel stages={funnelStages} />
        <RecentActivity rows={recent} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopList testid="tc-top-pages" title="Most viewed pages" rows={tops.pages} />
        <TopList testid="tc-top-resources" title="Most viewed resources" rows={tops.resources} />
        <TopList testid="tc-top-frameworks" title="Most viewed frameworks" rows={tops.frameworks} formatKey={(r) => ({ soc2: 'SOC 2', iso27001: 'ISO 27001', hipaa: 'HIPAA', gdpr: 'GDPR', pcidss: 'PCI DSS', ai: 'AI & Privacy' }[r.key] || r.key)} />
        <TopList testid="tc-top-controls" title="Most viewed controls" rows={tops.controls} />
        <TopList testid="tc-top-searches" title="Most common searches" rows={tops.searches} formatKey={(r) => r.key} />
        <div className="rounded-xl border border-[#D7E0F2] bg-white p-4" data-testid="tc-analytics-companies">
          <div className="text-sm font-semibold text-[#0A0A0A] mb-2">Visitor companies</div>
          <div className="text-xs text-[#94A3B8] mb-2">Derived only from documentation-request email domains. Free-mail providers are excluded.</div>
          {(!companies || companies.length === 0) ? (
            <div className="text-xs text-[#94A3B8] py-4">No documentation requests yet.</div>
          ) : (
            <ul className="divide-y divide-[#F1F5F9]">
              {companies.slice(0, 8).map((c, i) => (
                <li key={c.domain} className="flex items-center justify-between py-2 text-sm" data-testid={`tc-company-row-${i}`}>
                  <span className="text-[#334155] truncate pr-2">{c.companies && c.companies.length ? c.companies[0] : c.domain}<span className="text-xs text-[#94A3B8] ml-2">@{c.domain}</span></span>
                  <span className="text-xs text-[#64748B] whitespace-nowrap">{fmtNum(c.requests)} req</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="text-xs text-[#94A3B8]" data-testid="tc-analytics-privacy-note">
        Privacy-safe: no IP addresses, no browser fingerprints, no cookies. Visitor IDs are anonymous. Raw events auto-expire after 180 days. Visitors with Do-Not-Track enabled are never tracked.
      </div>
    </div>
  );
};
